import type { ApprovalRef } from '../generated/executionApprovals'
import { videoFamilyFor } from '../components/canvas-v2/videoFamilyPreference'
import { CanvasReferencePicking } from '../components/canvas-v2/CanvasReferencePicking'
import { useArrangeCanvas } from './useArrangeCanvas'
import { isApiRecord, type ApiValue } from '../api/responseDecoders'
/**
 * WorkspacePageV2 — Full V2 canvas workspace
 *
 * Sprint 8: Wires together ReactFlow canvas with canvas-v2 node/edge types,
 * Yjs real-time sync, CanvasContext (all methods delegating to Y doc + backend API),
 * undo/redo (50-entry in-memory stack), copy/paste/duplicate with edge remapping,
 * keyboard shortcuts (Cmd+C/V/D/Z, Shift+Z), viewport management (local persistence),
 * CanvasToolbox + CanvasControls + InteractiveMiniMap, and config sync (500ms debounce).
 *
 * Route: /project/:projectId/workspace/:canvasId?
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
  useRef,
  useMemo,
  useSyncExternalStore,
  type Dispatch,
  type KeyboardEvent as ReactKeyboardEvent,
  type SetStateAction,
} from 'react'
import { useParams, useSearchParams, useLocation, useNavigate, useNavigationType } from 'react-router-dom'
import { ProjectIdProvider } from '../contexts/ProjectIdContext'
import { ActiveCanvasIdProvider, DEFAULT_CANVAS_ID } from '../contexts/ActiveCanvasIdContext'
import { CanvasHydrationProvider } from '../contexts/CanvasHydrationContext'
import { trackCanvasLoadFinished } from '../analytics/events'
import { PaletteAnalyticsProvider } from '../analytics/paletteAnalytics'
import { useNuxTooltipEvents } from '../onboarding/nuxEvents'
import { useProjectVisit } from '../analytics/projectVisit'
import type { NodeMutationSource, UploadSource } from '../analytics/generated/analyticsEvents'
import { tagUploadSource } from '../analytics/uploadSource'
import { APP_NAME } from '../constants/brand'
import {
  InsufficientSparksProvider,
  useInsufficientSparks,
  useShowInsufficientSparks,
} from '../contexts/InsufficientSparksContext'
import InsufficientSparksModal from '../components/ui/InsufficientSparksModal'
import ReactFlow, {
  Background,
  BackgroundVariant,
  ReactFlowProvider,
  MiniMap,
  Panel,
  useNodesState,
  useEdgesState,
  addEdge as rfAddEdge,
  useReactFlow,
  useStore,
  useStoreApi,
  useUpdateNodeInternals,
  PanOnScrollMode,
  SelectionMode,
  type Connection,
  type NodeMouseHandler,
  type ReactFlowInstance,
} from 'reactflow'
import 'reactflow/dist/style.css'
import { Check, ChevronDown, CopyPlus, MessageSquare, Pencil, Plus, Trash2, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
  nodeTypes,
  edgeTypes,
  CanvasContext,
  type CanvasContextValue,
  type NodeDeleteTrigger,
  createNode,
  nodeSchemas,
  areTypesCompatible,
  applyWorkspaceStyleDefault,
  applyProjectAspectRatioDefault,
  applyProjectResolutionDefault,
  supportsAspectRatio,
  supportsVideoResolution,
  supportsWorkspaceStyle,
  assertNoBackendAuthoredOutputMapWrites,
  assertNoDirectAssetInputPortWrites,
  getDirectAssetInputPortWrites,
  CANVAS_PERFORMANCE_SETTINGS_STORAGE_KEY,
  DEFAULT_CANVAS_PERFORMANCE_SETTINGS,
  normalizeCanvasPerformanceSettings,
  CanvasBottomPanelVisibilityProvider,
  CanvasEdgesProvider,
  CanvasNodeChromeModeProvider,
  isEdgeActiveNodeStatus,
  computeHandleVisibility,
  handleVisibilityEqual,
  type HandleVisibility,
  useCanvasBottomPanelVisibility,
  useHoveredNodeIdSelector,
  useSetHoveredNodeId,
  useSetHandleVisibility,
  type CanvasPerformanceSettings,
  type NodeDataRecord,
  type RawNodeDataRecord,
  findNonOverlappingCanvasPosition,
} from '../components/canvas-v2'

import { useYDoc } from '../hooks/useYDoc'
import { deleteAgentChatClientSnapshot } from '../hooks/useAgentChat'
import { useImageModels } from '../hooks/useImageModels'
import { useVideoModels } from '../hooks/useVideoModels'
import { useDirectorAutoConfirmOff } from '../hooks/useDirectorAutoConfirmOff'
import { syncAutoSeededVideoEditorTimeline } from '../hooks/videoEditorTimelineSeed'
import {
  dataValue,
  isDefaultViewport,
  planViewportOnSync,
  useYCanvas,
  useYViewport,
  type ReactFlowCanvasEdge,
  type ReactFlowCanvasEdgeData,
  type ReactFlowCanvasNode,
  type ReactFlowFieldValue,
  type ReactFlowCanvasNodeData,
  type RawCanvasYNode,
} from '../hooks/useYCanvas'
import { CANVAS_INTERACTION_IGNORE_ATTR } from '../hooks/canvasAutoFocus'
import {
  edgeTypeForReferencePairKind,
  type ReferencePairKind,
} from '../components/canvas-v2/referencePairEdges'
import {
  CANVAS_LOCAL_WHEEL_TARGET_ATTR,
  getCanvasWheelZoomDecision,
} from '../components/canvas-v2/canvasWheelScope'
import {
  DuplicateNodeCollapseError,
  newDuplicateTraceId,
  prepareDuplicateNodeData,
  summarizeCollapseReport,
  type DuplicateNodeOptions,
  type PreparedDuplicateNodeData,
} from '../components/canvas-v2/duplicateNodeData'
import * as Y from 'yjs'
import { useExecutionPlan } from '../hooks/useExecutionPlan'
import { useProject, useProjectConfig } from '../api/hooks'
import { withVideoGeneratorCreationAudioDefaults } from '../utils/videoGeneratorAudioDefaults'
import {
  createCanvas,
  resolveVideoFamilyForNode,
  deleteCanvas,
  executeNode,
  getYDocNodePresence,
  InsufficientSparksError,
  isDefinitiveExecuteNodeRefusal,
  listCanvases,
  renameCanvas,
  type CanvasSummary,
} from '../api/client'
import {
  FE_TYPE_TO_BACKEND,
  CANVAS_HIDDEN_FE_TYPES,
  YDOC_NODE_FIELDS,
  isFeNodeType,
  isTypedReactFlowNode,
  type FeNodeType,
  type YDocInputFlushEdgeRef,
} from '../generated/nodeDataTypes'
import { CanvasControls, CanvasLoadOverlay, LoadingState, ErrorState } from '../components/workspace/CanvasArea'
import { ChatSidebar } from '../components/workspace/ChatSidebar'
import { AppHeader } from '../components/workspace/AppHeader'
import { SimplifiedWorkspace } from '../components/workspace/SimplifiedWorkspace'
import { useLastInteractedNode } from '../components/workspace/useLastInteractedNode'
import type { WorkspaceViewMode } from '../components/workspace/WorkspaceViewSwitch'
import { useWorkspaceViewPreference } from '../components/workspace/useWorkspaceViewPreference'
import { NodePresentationContext } from '../components/canvas-v2/NodePresentationContext'
import { AddNodeModal } from '../components/canvas-v2/AddNodeModal'
import { AssetManagerDrawer } from '../components/canvas-v2/AssetManagerDrawer'
import { CanvasBottomPalette } from '../components/canvas-v2/CanvasBottomPalette'
import { CanvasSelectionBounds } from '../components/canvas-v2/CanvasSelectionBounds'
import { CanvasObservedByBadge } from '../components/canvas-v2/CanvasObservedByBadge'
import { DEFAULT_CANVAS_TOOL, type CanvasTool } from '../components/canvas-v2/canvasTool'
import {
  collectParentIds,
  computeMarqueeSelection,
  diffSelection,
  selectionRectToFlow,
  type MarqueeCandidate,
} from '../components/canvas-v2/marqueeSelection'
import { ClipDownloadModal } from '../components/canvas-v2/ClipDownloadModal'
import { KeyboardShortcutsModal } from '../components/canvas-v2/KeyboardShortcutsModal'
import { ProjectConfigGear } from '../components/canvas-v2/ProjectConfigGear'
import {
  DEFAULT_AUTO_CONFIRM_COST_SETTINGS,
  type AutoConfirmCostSettings,
} from '../components/chat/autoConfirmCost'
import UsageCostModal from '../components/ui/UsageCostModal'
import Modal from '../components/ui/Modal'
import { useLocalStorage } from '../hooks/useLocalStorage'
// PublishButton + SparksBalance now mounted inside <AppHeader>.
import { NewVersionPrompt } from '../components/layout/NewVersionPrompt'
import { isClosingFrameHandshakeRefusal, videoExecuteStateUpdates } from '../features/closingFrame/closingFrameHandshake'
import {
  CANVAS_DELETE_KEY_CODE,
  CANVAS_MULTI_SELECTION_KEY_CODE,
} from '../components/canvas-v2/reactFlowKeyBindings'
import { installShiftMetaTracking } from '../components/canvas-v2/shiftMetaTracking'
import { beginCanvasGesture, endCanvasGesture, isCanvasMediaLoading, subscribeCanvasMediaLoading, touchCanvasGesture } from '../components/canvas-v2/canvasImageLoader'
import { GroupDropTargetProvider } from '../components/canvas-v2/nodes/GroupNode/GroupDropTargetContext'
import { useCanvasDragToGroup } from '../components/canvas-v2/nodes/GroupNode/useCanvasDragToGroup'
import { SingleNodeDetailsProvider } from '../components/canvas-v2/nodes/shared/SingleNodeDetailsContext'
import {
  ReferenceSelector,
  type SelectedNodeReference,
} from '../components/canvas-v2/nodes/shared/ReferenceSelector'
import {
  NodeDeleteConfirmationDialog,
  type NodeDeleteConfirmationItem,
} from '../components/canvas-v2/nodes/shared/NodeDeleteConfirmationDialog'
import { VoiceCatalogueModalProvider } from '../components/canvas-v2/nodes/shared/VoiceCatalogueModalProvider'
import { isBlockingOverlayOpen } from '../hooks/canvasShortcutGuard'
import { createDebug, createNadDebug } from '../utils/debugLog'
import { reconcileReactFlowReset } from '../components/canvas-v2/reconcileReactFlowReset'
import { ydocSafeCloneNodeData, ydocSafeSnapshotNodes } from '../utils/ydocNodeData'
import { noteNodeActions, useNodeActionAnalytics } from '../analytics/nodeActionAnalytics'
import { shouldPreserveBrowserCopy } from './workspaceKeyboardShortcuts'
import { appendOrReplaceNodeById } from './workspaceNodeState'
import { getWorkspaceProjectErrorState } from './workspaceProjectError'
import { buildDownloadCandidates, buildReferenceMediaConnectionMigration, referenceConnectionTargetHandle } from '../components/canvas-v2/nodes/shared/mentionUtils'
import { dispatchAppEvent } from '../components/canvas-v2/nodes/shared/useNodeActions'
import { openProjectSettings } from '../components/canvas-v2/projectSettingsEvents'
import { CanvasContextMenu } from '../components/canvas-v2/contextMenu/CanvasContextMenu'
import {
  buildMenu,
  combineCapability,
  type ContextMenuItemId,
} from '../components/canvas-v2/contextMenu/contextMenuModel'
import {
  getNodeContextAction,
  useNodeContextActionsVersion,
} from '../components/canvas-v2/contextMenu/nodeContextActions'
import { classifyMediaFiles, describeMediaFiles, MEDIA_ACCEPT_BY_TYPE, type ClassifiedMediaFile } from '../utils/uploadFileValidation'
import { rememberRemovedAssets } from '../components/canvas-v2/removedAssetMemory'
import { toast } from 'sonner'
import {
  canvasRoomKeyOf,
  createUndoHistory,
  edgeReplayKey,
  fingerprintGraph,
  isSnapshotReplayable,
  refusalMessageKey,
  type ReplayRefusal,
  type UndoCandidate,
} from './workspaceUndoHistory'
import { UploadContext } from '../contexts/UploadContext'
import { useAuth } from '../contexts/AuthContext'
import { useIsMobileViewport } from '../hooks/useMediaQuery'
import {
  createLandingUploadSeedNodeData,
  landingUploadSeedTraceId,
  workspaceLocationState,
} from './workspaceLandingUploads'
import {
  TASK_COMPLETION_CHIME_STORAGE_KEY,
  completedNodeIdsFromPreviousActive,
  createTaskCompletionSnapshot,
  playTaskCompletionChime,
  primeTaskCompletionChime,
  type TaskCompletionSnapshot,
} from '../components/canvas-v2/taskCompletionChime'
import { applyDefaultOnMigrations } from '../components/canvas-v2/defaultOnMigrations'
import { useProjectTeamAnalytics } from '../analytics/useProjectTeamAnalytics'
import { trackInteraction } from '../analytics/interactionAnalytics'
import { analyticsNodeType } from '../analytics/nodeAnalytics'

const dragLog = createDebug('dw:panel-drag:position-sync')
// Feature tag `canvas-snap-to-grid` (queryable via `nad trace --feature ...`):
// records each persisted drag-stop position with the effective snap state so
// "snap ON ⇒ x,y are multiples of CANVAS_GRID_SIZE" is assertable from logs.
const snapLog = createNadDebug('dw:canvas-snap-to-grid:drag')
const labelApplyLog = createNadDebug('dw:canvas:label-apply', { priority: true })
const reactFlowResetLog = createNadDebug('dw:canvas:reactflow-reset')
// Grid pitch (px) — single source of truth shared by the ReactFlow `snapGrid`
// prop and the `<Background>` dot `gap` so the snap increment always lands on a
// visible dot. Changing this one value moves both the dots and the snap step.
const CANVAS_GRID_SIZE = 24
const undoLog = createDebug('dw:undo')
// Feature-tagged logger for the canvas-scoping of the undo/redo history, so
// `nad trace --feature undo-canvas-scope` can prove a history was parked,
// restored, or deliberately dropped, and never replayed into a foreign canvas.
const undoScopeLog = createNadDebug('dw:undo-canvas-scope:switch')
const refSyncLog = createNadDebug('dw:reference-sync:edge-mutation')
const referenceMediaConnectionLog = createNadDebug('dw:reference-media-order')
const selectionLog = createDebug('dw:canvas:selection')
// Marquee diagnostics. Enable with `localStorage.debug = 'dw:canvas:marquee*'`.
// Answers the two things static reading could not: whether the marquee ever
// asks for a GROUP (which would explain the blue 2px border flickering
// mid-drag), and when ReactFlow raises/clears its own selection box (the
// amber flash after the drag ends).
const marqueeLog = createNadDebug('dw:canvas:marquee')
const marqueeBoxLog = createNadDebug('dw:canvas:marquee-box')
// Enablement gates. createNadDebug is NOT free when its namespace is off: it
// builds the `key=value` summary with Object.entries + JSON.stringify before
// it ever consults `debug`. These loggers fire per pointer move, so calling
// them unguarded would cost on every drag even with logging disabled —
// exactly the class of per-frame waste that made the canvas flash.
const marqueeLogOn = createDebug('dw:canvas:marquee')
const marqueeBoxLogOn = createDebug('dw:canvas:marquee-box')

/**
 * True while the user is dragging a marquee. Module scope so the selector
 * identity is stable — an inline arrow would resubscribe the store on every
 * render of a component this large.
 */
const marqueeSelectingSelector = (state: { userSelectionActive: boolean }): boolean =>
  state.userSelectionActive
const selectionNadLog = createNadDebug('dw:canvas:selection')
const simplifiedViewLog = createNadDebug('dw:simplified-view')
const contextMenuLog = createNadDebug('dw:canvas-context-menu')
const landingUploadLog = createNadDebug('dw:landing-upload:workspace')
const newNodeFocusLog = createNadDebug('dw:new-node-focus')
// How long the pan to a newly added node takes; selection waits for it.
const NEW_NODE_FOCUS_PAN_MS = 400
// Toolbox-add twin of the server-side in_resolution stamp (feature tag
// `project-video-resolution`, queryable via `nad trace --feature ...`).
const resolutionStampLog = createNadDebug('dw:project-video-resolution:toolbox-stamp')
const defaultVideoModelCreationLog = createNadDebug('dw:default-video-model-node-creation')
// Toolbox-add twin of the server-side in_style stamp in GraphManager._apply_add_node
// (feature tag `project-default-style`, queryable via `nad trace --feature ...`).
// Records the configured project style (WorkflowState.style_id) that a newly-added
// style-aware node inherits, so the source-of-truth alignment is observable.
const styleStampLog = createNadDebug('dw:project-default-style:toolbox-stamp')
// Toolbox-add twin of the project aspect-ratio stamp. Same feature tag as the
// backend's `director-aspect-default` traces so `nad trace --feature
// director-aspect-default` shows the manual-add path beside the Director one.
const aspectStampLog = createNadDebug('dw:director-aspect-default:toolbox-stamp')
const keyboardShortcutLog = createNadDebug('dw:prompt-sidebar-copy')
// Feature-tagged trace for the overlay-aware shortcut guard: emitted when a
// canvas shortcut is suppressed because a blocking overlay is open.
// `nad trace --feature canvas-shortcut-overlay-guard`.
const overlayGuardLog = createNadDebug('dw:canvas-shortcut-overlay-guard')
const canvasRegenCueLog = createNadDebug('dw:canvas-regen-cue')
const tabNavigationLog = createNadDebug('dw:tab-navigation')
// Feature-tagged NAD trace for the cross-canvas "Import from another canvas" UI
// handoff. The copy itself is performed by the Director tool
// `copy_nodes_from_canvas`; this UI only dispatches a chat handoff. The tag
// matches the backend `_COPY_FEATURE` (`cross-canvas-node-tools`) so
// `nad trace --feature cross-canvas-node-tools` shows the FE handoff dispatch
// alongside the backend `cross_canvas_node_copy:start/complete`. Dev-gated.
const crossCanvasImportLog = createNadDebug('dw:cross-canvas-node-tools:import-handoff')
const nodeOpsLog = createNadDebug('dw:canvas:node-ops')
const nodeDuplicateLog = createNadDebug('dw:node-duplicate:canvas')
// Feature-tagged NAD trace for the YDoc-first deletion path. Marks the delete
// boundary where node removal is committed to YDoc only (no optimistic React
// writes) so rebuild() drives the React node-state update
// (`nad trace --feature canvas-node-delete`). Dev-gated behind VITE_NAD_DEBUG.
const nodeDeleteLog = createNadDebug('dw:canvas-node-delete:remove')
const nodeDeleteConfirmLog = createNadDebug('dw:canvas-node-delete:confirm')
const canvasInteractionLog = createNadDebug('dw:canvas:autofocus')
const viewportRestoreLog = createNadDebug('dw:canvas:viewport')
// Feature-tagged NAD trace for the canvas tab-bar reconciliation loop. The
// tab list is REST-only (`listCanvases`); the `canvas_created` chat WS event
// is a fast-path, but the broadcast is not queued for late subscribers. Any
// event fired while AgentChat was unmounted or the WS was still handshaking
// is only recovered by a REST refetch — this trace records every refetch site
// (mount / ws-connected / director-event) plus the resulting tab diff.
// `nad trace --feature canvas-tab-sync`.
const canvasTabSyncLog = createNadDebug('dw:canvas-tab-sync')
// Canvas tab navigation: every tab click, every navigateToCanvas() push, and
// every resulting active-canvas route change (with its PUSH/REPLACE/POP type).
// A tab click that does not end on the clicked canvas shows up as a
// `route-change` whose `to` differs from the preceding `tab-click` target.
// `nad trace --feature canvas-tab-nav`.
const canvasTabNavLog = createNadDebug('dw:canvas-tab-nav')
// Distinguishes a real browser reload (new id, shell mounts from scratch) from
// an in-app tab switch (same id) in `workspace-loading-gate` events.
const CANVAS_TAB_NAV_PAGE_LOAD_ID = Math.random().toString(36).slice(2, 8)
// One trace_id per canvas visit, so `nad trace --feature canvas-tab-nav --expand`
// shows the latest switch's tab-click → navigate → route-change → gate →
// canvas-surface-state chain. A different canvasId starts a new visit.
let canvasTabNavVisit = { canvasId: '', seq: 0 }
function canvasTabNavTraceId(canvasId: string): string {
  if (canvasTabNavVisit.canvasId !== canvasId) {
    canvasTabNavVisit = { canvasId, seq: canvasTabNavVisit.seq + 1 }
  }
  return `canvas-tab-nav:${CANVAS_TAB_NAV_PAGE_LOAD_ID}:${canvasTabNavVisit.seq}:${canvasId}`
}
// Trace the complete user-facing destructive action boundary: request,
// cancellation/confirmation, and the existing typed API mutation result.
// `nad trace --feature canvas-tab-delete`.
const canvasTabDeleteLog = createNadDebug('dw:canvas-tab-delete:confirmation')
const taskCompletionChimeLog = createNadDebug('dw:task-completion-chime')
// Feature-tagged NAD traces for the active-only edge/handle rendering fast path.
// `dw:canvas:active-edges` records each visibleEdges recompute (how many of the
// total edges survive the active-node filter); `dw:canvas:handle-gate` records
// each per-node handle-visibility set change. Both fire only at the WorkspacePage
// boundary (never per-node per-render) and are dev-gated behind VITE_NAD_DEBUG.
// Trace: `nad trace --feature canvas:active-edges` / `--feature canvas:handle-gate`.
const activeEdgesNadLog = createNadDebug('dw:canvas:active-edges')
const handleGateNadLog = createNadDebug('dw:canvas:handle-gate')
const workspaceMobileLog = createNadDebug('dw:mobile-ui-loading')
// Trace: `nad trace --feature canvas --event wheel-zoom` (namespace parses to
// feature=canvas, concern=zoom-gesture). Logs the custom ctrl/cmd+wheel zoom
// handler's decisions so a stale-metaKey suppression (the Cmd+V pan→zoom bug) is
// visible: `wheel-zoom` on real zooms, `wheel-zoom-suppressed-stale-meta` when a
// stale WheelEvent.metaKey is ignored, `meta-key-state` on tracked-Meta transitions.
const zoomNadLog = createNadDebug('dw:canvas:zoom-gesture')
// NAD trace for will-change text-crispness fix (post 1586138096476821, RFC-0754
// regression of 8f7e24db8). Permanent will-change pins raster scale and causes
// pixelated text on zoom; scoping to interaction lets Chrome re-raster crisp at
// idle. Trace: `nad trace --feature dw:canvas-will-change-text-crispness`.
const willChangeTextCrispnessLog = createNadDebug('dw:canvas-will-change-text-crispness')
// When a scroll pan stops swallowing clicks (pointer moved after a wheel) and
// when a node press becomes a real drag. Workplace post 1645063093917654.
const canvasPointerReleaseLog = createNadDebug('dw:canvas:pointer-release')

function referenceSyncTraceId(targetId: string): string {
  return `reference-sync:${targetId}`
}

// ─── Constants ────────────────────────────────────────────────────────────────

const LEGACY_AUTO_CONFIRM_COST_KEY = 'dreamwright_skip_cost_confirmation'
const AUTO_CONFIRM_IMAGE_COST_KEY = 'dreamwright_auto_confirm_image_costs'
const AUTO_CONFIRM_VIDEO_COST_KEY = 'dreamwright_auto_confirm_video_costs'

// DEFAULT_CANVAS_ID is imported from ActiveCanvasIdContext (single frontend source).
// Keep in sync with backend MAX_CANVAS_TITLE_LENGTH in conversation_service.py.
const CANVAS_TITLE_MAX_LENGTH = 120
type CanvasTab = {
  id: string
  title: string
  updated_at: string
  created_at?: string
  node_count: number
}
type PendingCanvasDelete = {
  canvasId: string
  traceId: string
}
const EMPTY_ACTIVE_VISUAL_EDGE_NODE_IDS = new Set<string>()
const PASTE_OFFSET = 50
const CANVAS_REGEN_CUE_STORE_VERSION = 1
const CANVAS_REGEN_CUE_CLASS = 'canvas-regen-cue-unseen'
const CANVAS_REGEN_CUE_NODE_SEPARATOR = '\u001e'
const CANVAS_REGEN_CUE_KEY_SEPARATOR = '\u001f'
const VIEWPORT_PAN_ACTIVATION_DISTANCE_PX = 4
// Wheel events closer together than this are one scroll stream (including the
// macOS momentum tail); a longer gap starts a new scroll.
const WHEEL_STREAM_GAP_MS = 180
const IS_LOCAL_DEV =
  import.meta.env.DEV &&
  !import.meta.env.VITE_ENABLE_AUTH &&
  (typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))

function resolveWorkspaceCanvasId(searchParams: URLSearchParams, routeCanvasId?: string): string {
  return searchParams.get('canvas') || routeCanvasId || DEFAULT_CANVAS_ID
}

function defaultCanvasTab(): CanvasTab {
  return {
    id: DEFAULT_CANVAS_ID,
    title: 'Agent Canvas',
    updated_at: new Date(0).toISOString(),
    created_at: new Date(0).toISOString(),
    node_count: 0,
  }
}

function normalizeCanvasTabs(canvases: CanvasTab[], activeCanvasId: string): CanvasTab[] {
  const byId = new Map<string, CanvasTab>()
  for (const canvas of canvases) {
    byId.set(canvas.id, canvas)
  }
  if (!byId.has(DEFAULT_CANVAS_ID)) {
    byId.set(DEFAULT_CANVAS_ID, defaultCanvasTab())
  }
  if (!byId.has(activeCanvasId)) {
    byId.set(activeCanvasId, {
      id: activeCanvasId,
      title: activeCanvasId === DEFAULT_CANVAS_ID ? 'Agent Canvas' : activeCanvasId,
      updated_at: new Date(0).toISOString(),
      created_at: new Date(0).toISOString(),
      node_count: 0,
    })
  }
  return Array.from(byId.values()).sort((a, b) => {
    if (a.id === DEFAULT_CANVAS_ID) return -1
    if (b.id === DEFAULT_CANVAS_ID) return 1
    return (a.created_at || a.updated_at).localeCompare(b.created_at || b.updated_at)
  })
}

function inertWhen(isInert: boolean): { inert?: '' } {
  return isInert ? { inert: '' } : {}
}

type WorkspaceNode = ReactFlowCanvasNode
type WorkspaceEdge = ReactFlowCanvasEdge
type MobileWorkspacePane = 'chat' | 'canvas'
type NewCanvasSourceNode = {
  id: string
  label: string
  type: string
}

function CanvasTabDeleteButton({
  canvasId,
  canvasTitle,
  deleting,
  disabled,
  onRequestDelete,
}: {
  canvasId: string
  canvasTitle: string
  deleting: boolean
  disabled: boolean
  onRequestDelete: (canvasId: string) => void
}) {
  const { t } = useTranslation('pages')
  const label = t('workspace.newCanvas.delete', { title: canvasTitle })

  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation()
        if (!disabled && !deleting) onRequestDelete(canvasId)
      }}
      disabled={disabled || deleting}
      className="mr-1 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md text-text-muted opacity-0 transition-all duration-200 hover:bg-surface-3 hover:text-destructive focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-40 group-hover:opacity-70"
      title={label}
      aria-label={label}
      data-testid={`canvas-tab-delete-${canvasId}`}
    >
      <X className="h-3 w-3" />
    </button>
  )
}

function CanvasTabDeleteConfirmationDialog({
  canvas,
  deleting,
  error,
  onCancel,
  onConfirm,
}: {
  canvas: CanvasTab | null
  deleting: boolean
  error: string | null
  onCancel: () => void
  onConfirm: () => void
}) {
  const { t } = useTranslation('pages')

  return (
    <Modal
      isOpen={canvas !== null}
      onClose={deleting ? () => {} : onCancel}
      title={t('workspace.newCanvas.deleteDialogTitle')}
      size="sm"
    >
      <div data-testid="canvas-tab-delete-dialog" className="space-y-4">
        <div className="flex gap-3">
          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-destructive/10 text-destructive">
            <Trash2 className="h-4 w-4" />
          </div>
          <div className="min-w-0 space-y-1">
            <p className="text-sm text-foreground">
              {t('workspace.newCanvas.deleteDialogMessage', { title: canvas?.title ?? '' })}
            </p>
            <p className="text-sm text-muted-foreground">
              {t('workspace.newCanvas.deleteDialogWarning')}
            </p>
            {error && (
              <p className="text-sm text-destructive" data-testid="canvas-tab-delete-error">
                {error}
              </p>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            data-testid="canvas-tab-delete-cancel"
            onClick={onCancel}
            disabled={deleting}
            className="h-9 rounded-lg border border-input-border bg-surface-1 px-3 text-sm font-medium text-foreground hover:border-input-border-hover hover:bg-surface-3 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t('workspace.newCanvas.cancel')}
          </button>
          <button
            type="button"
            data-testid="canvas-tab-delete-confirm"
            onClick={onConfirm}
            disabled={deleting}
            className="h-9 rounded-lg bg-destructive px-3 text-sm font-medium text-white hover:bg-destructive/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {deleting
              ? t('workspace.newCanvas.deleting')
              : t('workspace.newCanvas.deleteDialogConfirm')}
          </button>
        </div>
      </div>
    </Modal>
  )
}
type PortEdgeRef = YDocInputFlushEdgeRef
type TargetPortRef = { target: string; targetHandle?: string }
type CanvasRegenCueStore = {
  version?: number
  known?: string[]
  acknowledged?: string[]
}
type NormalizedCanvasRegenCueStore = {
  known: string[]
  acknowledged: string[]
}
type CanvasRegenNodeRuntime = {
  status?: string
  outputSignature: string | null
}
declare global {
  interface Window {
    /**
     * Dev-only view of the per-canvas undo history, mirroring `__ydoc`.
     *
     * Undo depth is not visible in the DOM, so without this an E2E failure can
     * only report "the node did not come back" and never which bucket was empty
     * or which room the store was pointed at.
     */
    __dwUndoHistory?: {
      activeRoomKey: () => string
      depth: (roomKey?: string) => { undo: number; redo: number }
      trackedRoomKeys: () => string[]
    }
    __dwFocusNode?: (
      nodeId: string,
      options?: { maxZoom?: number; yOffset?: number },
    ) => string
    __dwReadNodeField?: (nodeId: string, field: string) => unknown
    __dwSetNodeField?: (nodeId: string, field: string, value: ReactFlowFieldValue) => string
    __dwGetCanvasNodes?: () => Array<{
      id: string
      type: string | null
      parentId: string | null
      x: number
      y: number
      childIds: string[]
      selected: boolean
    }>
    __dwGetCanvasEdges?: () => Array<{
      id: string
      source: string
      target: string
      sourceHandle: string | null
      targetHandle: string | null
    }>
    __dwGetSelectedNodeIds?: () => string[]
    __dwGetViewport?: () => { x: number; y: number; zoom: number } | null
    __dwSetViewport?: (viewport: { x: number; y: number; zoom: number }) => string
    __ydoc?: Y.Doc
    __Y?: typeof Y
    __activeExecutingNodes?: Set<string>
  }
}

function stringDataField(
  data: unknown,
  key: string,
): string | undefined {
  const value = dataValue(data, key)
  return typeof value === 'string' ? value : undefined
}

function firstStringDataField(
  data: unknown,
  keys: string[],
): string | undefined {
  for (const key of keys) {
    const value = stringDataField(data, key)
    if (value) return value
  }
  return undefined
}

interface PendingNodeDelete {
  nodeIds: string[]
  nodes: NodeDeleteConfirmationItem[]
  trigger: NodeDeleteTrigger
  traceId: string
}

interface CanvasContextMenuState {
  kind: 'node' | 'canvas'
  /** Pointer position in client coordinates. */
  x: number
  y: number
  /** The right-clicked node (node menu only). */
  anchorNodeId?: string
  /** Nodes the node-menu items act on: the selection, or just the anchor. */
  targetNodeIds: string[]
  /** Right-click point in flow coordinates (canvas menu): where Upload places files. */
  flowPosition?: { x: number; y: number }
}

/** Accept every media family the canvas can turn into an upload node. */
const CONTEXT_MENU_UPLOAD_ACCEPT = Object.values(MEDIA_ACCEPT_BY_TYPE).join(',')

/** `canvas_node_action.source` for each delete entry point. */
const NODE_DELETE_TRIGGER_SOURCE: Record<NodeDeleteTrigger, NodeMutationSource> = {
  toolbar: 'node_toolbar',
  keyboard: 'keyboard',
  context_menu: 'context_menu',
}

const NODE_DELETE_LABEL_FIELDS = [
  'name',
  'character_name',
  'location_name',
  'in_name',
  'label',
] as const

function isGroupCanvasNode(node: WorkspaceNode): boolean {
  return node.type === 'nodeGroup' || node.type === 'group'
}

function getNodeDeleteLabel(node: WorkspaceNode): string {
  return firstStringDataField(node.data, [...NODE_DELETE_LABEL_FIELDS]) ?? node.id
}

function getNewCanvasSourceNode(node: WorkspaceNode): NewCanvasSourceNode {
  return {
    id: node.id,
    label: firstStringDataField(node.data, [...NODE_DELETE_LABEL_FIELDS]) ?? node.id,
    type: node.type ?? 'node',
  }
}

function buildNewCanvasDirectorHandoffMessage(
  title: string,
  prompt: string,
  selectedSources: NewCanvasSourceNode[],
): string {
  const parts = [
    `Create a new canvas titled "${title}".`,
    'Summarize the relevant context from this conversation and the current canvas for the new canvas Director.',
  ]

  if (selectedSources.length > 0) {
    const sourceLines = selectedSources
      .map((sourceNode) => `- ${sourceNode.label} (${sourceNode.type}, ${sourceNode.id})`)
      .join('\n')
    const sourceNodeIds = selectedSources.map((sourceNode) => sourceNode.id)
    parts.push(
      `Copy these selected source nodes into the new canvas by passing source_node_ids=${JSON.stringify(sourceNodeIds)} to create_canvas:\n${sourceLines}`,
    )
  }

  const trimmedPrompt = prompt.trim()
  if (trimmedPrompt) {
    parts.push(`User note for the new canvas:\n${trimmedPrompt}`)
  }

  parts.push(
    'Use create_canvas with your handoff summary as the prompt. After create_canvas succeeds, immediately send that same handoff to the returned director_id with send_message_to_director so the new canvas chat starts with the handoff and the new Director begins work.',
  )

  return parts.join('\n\n')
}

// Build the Director chat handoff for importing nodes from another canvas into
// THIS (current) canvas. The frontend never copies nodes itself — the copy is
// performed by the Director's read-only-source `copy_nodes_from_canvas` tool.
// The message must name the source canvas by id AND title so the Director scopes
// the read to the right canvas. Do NOT thread source-canvas node IDs into
// `agent-chat:send`'s `referencedNodeIds` (those @mentions resolve against the
// CURRENT canvas and would dangle) — encode the source identity in the body.
function buildImportCanvasDirectorHandoffMessage(
  sourceCanvasTitle: string,
  sourceCanvasId: string,
  description: string,
): string {
  const parts = [
    'Import nodes from another canvas into THIS current canvas.',
    `Source canvas: "${sourceCanvasTitle}" (id: ${sourceCanvasId}).`,
    `First call list_canvas_nodes with source_canvas_id="${sourceCanvasId}" to see what is available. For every node you select, call inspect_canvas_node without version_number and retain its source_node_digest. Then call copy_nodes_from_canvas with source_canvas_id="${sourceCanvasId}", node_ids=[...], and the exact source_node_digests mapping — the nodes will be copied into the current canvas. Do NOT modify the source canvas.`,
  ]

  const trimmedDescription = description.trim()
  if (trimmedDescription) {
    parts.push(`The user wants specifically:\n${trimmedDescription}`)
  } else {
    parts.push('Ask the user which nodes they want if it is ambiguous.')
  }

  return parts.join('\n\n')
}

interface NewCanvasInitModalProps {
  isOpen: boolean
  title: string
  prompt: string
  sourceNodes: NewCanvasSourceNode[]
  selectedSourceNodeIds: Set<string>
  creating: boolean
  error: string | null
  onTitleChange: (value: string) => void
  onPromptChange: (value: string) => void
  onAskDirector: () => void
  onOpenSourceSelector: () => void
  onClose: () => void
  onSubmit: () => void
}

function NewCanvasInitModal({
  isOpen,
  title,
  prompt,
  sourceNodes,
  selectedSourceNodeIds,
  creating,
  error,
  onTitleChange,
  onPromptChange,
  onAskDirector,
  onOpenSourceSelector,
  onClose,
  onSubmit,
}: NewCanvasInitModalProps) {
  const { t } = useTranslation('pages')
  const [activeTab, setActiveTab] = useState<'manual' | 'director'>('manual')

  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !creating) onClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [creating, isOpen, onClose])

  useEffect(() => {
    if (isOpen) setActiveTab('manual')
  }, [isOpen])

  if (!isOpen) return null

  const canSubmit = title.trim().length > 0 && !creating
  const selectedSourceNodes = sourceNodes.filter((sourceNode) =>
    selectedSourceNodeIds.has(sourceNode.id),
  )
  const canAskDirector = title.trim().length > 0 && !creating

  return (
    <div
      className="fixed inset-0 z-[1100] flex items-start justify-center overflow-y-auto bg-background/80 px-4 py-6 sm:pt-16 sm:pb-6"
      role="presentation"
      data-testid="new-canvas-modal"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !creating) onClose()
      }}
    >
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-canvas-modal-title"
        className="flex max-h-[calc(100vh-3rem)] w-full max-w-lg shrink-0 flex-col rounded-xl border border-border bg-background shadow-2xl sm:max-h-[calc(100vh-5.5rem)]"
        data-testid="new-canvas-dialog"
        onMouseDown={(event) => event.stopPropagation()}
        onSubmit={(event) => {
          event.preventDefault()
          if (activeTab === 'manual' && canSubmit) onSubmit()
          if (activeTab === 'director' && canAskDirector) onAskDirector()
        }}
      >
        <div className="flex items-center justify-between border-b border-border-subtle px-4 py-3">
          <h2 id="new-canvas-modal-title" className="text-lg font-semibold text-foreground">
            {t('workspace.newCanvas.modalTitle')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={creating}
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-input-border bg-surface-1 text-muted-foreground transition-colors hover:border-input-border-hover hover:bg-surface-3 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
            aria-label={t('workspace.newCanvas.close')}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 space-y-4 overflow-y-auto px-4 py-4">
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-muted-foreground">
              {t('workspace.newCanvas.nameLabel')}
            </span>
            <input
              type="text"
              value={title}
              onChange={(event) => onTitleChange(event.target.value)}
              className="h-9 w-full rounded-lg border border-input-border bg-input-bg px-3 text-sm text-foreground outline-none transition-colors placeholder:text-text-muted focus:border-primary focus:ring-2 focus:ring-primary/20"
              placeholder={t('workspace.newCanvas.namePlaceholder')}
              data-testid="new-canvas-title"
            />
          </label>

          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-muted-foreground">
              {t('workspace.newCanvas.promptLabel')}
            </span>
            <textarea
              value={prompt}
              onChange={(event) => onPromptChange(event.target.value)}
              className="min-h-24 w-full resize-none rounded-lg border border-input-border bg-input-bg px-3 py-2 text-sm text-foreground outline-none transition-colors placeholder:text-text-muted focus:border-primary focus:ring-2 focus:ring-primary/20"
              placeholder={t('workspace.newCanvas.promptPlaceholder')}
              data-testid="new-canvas-prompt"
            />
          </label>

          <div>
            <div className="mb-1.5 flex items-center justify-between gap-2">
              <span className="text-xs font-medium text-muted-foreground">
                {t('workspace.newCanvas.sourceLabel')}
              </span>
              <button
                type="button"
                onClick={onOpenSourceSelector}
                disabled={sourceNodes.length === 0}
                className="h-7 rounded-lg border border-input-border bg-surface-1 px-3 text-xs font-medium text-foreground transition-colors hover:border-input-border-hover hover:bg-surface-3 disabled:cursor-not-allowed disabled:opacity-50"
                data-testid="new-canvas-source-selector-open"
              >
                {t('workspace.newCanvas.selectSources')}
              </button>
            </div>
            {sourceNodes.length === 0 ? (
              <div className="rounded-lg border border-border-subtle bg-surface-0 px-3 py-2 text-xs font-medium text-muted-foreground">
                {t('workspace.newCanvas.noSources')}
              </div>
            ) : selectedSourceNodes.length > 0 ? (
              <div className="flex max-h-24 flex-wrap gap-1.5 overflow-y-auto rounded-lg border border-border-subtle bg-surface-0 p-2">
                {selectedSourceNodes.map((sourceNode) => (
                  <span
                    key={sourceNode.id}
                    className="max-w-full truncate rounded-md border border-primary/25 bg-primary/10 px-2 py-1 text-xs font-medium text-primary"
                    data-testid={`new-canvas-selected-source-node-${sourceNode.id}`}
                    title={`${sourceNode.label} (${sourceNode.type})`}
                  >
                    {sourceNode.label}
                  </span>
                ))}
              </div>
            ) : (
              <div className="rounded-lg border border-border-subtle bg-surface-0 px-3 py-2 text-xs font-medium text-muted-foreground">
                {t('workspace.newCanvas.noSourcesSelected')}
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-1 border-y border-border-subtle bg-surface-0 px-2 py-1.5">
          <button
            type="button"
            onClick={() => setActiveTab('manual')}
            className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
              activeTab === 'manual'
                ? 'border-input-border bg-surface-1 text-foreground shadow-sm'
                : 'border-transparent text-muted-foreground hover:border-input-border-hover hover:bg-surface-3 hover:text-foreground'
            }`}
            data-testid="new-canvas-tab-manual"
          >
            {t('workspace.newCanvas.tabManual')}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('director')}
            className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
              activeTab === 'director'
                ? 'border-input-border bg-surface-1 text-foreground shadow-sm'
                : 'border-transparent text-muted-foreground hover:border-input-border-hover hover:bg-surface-3 hover:text-foreground'
            }`}
            data-testid="new-canvas-tab-director"
          >
            <span className="inline-flex items-center gap-1.5">
              <MessageSquare className="h-4 w-4" />
              {t('workspace.newCanvas.tabDirector')}
            </span>
          </button>
        </div>

        <div className="min-h-0 overflow-y-auto px-4 py-3">
          {activeTab === 'manual' ? (
            <p className="text-xs font-medium leading-relaxed text-muted-foreground">
              {t('workspace.newCanvas.manualTabExplanation')}
            </p>
          ) : (
            <div className="rounded-lg border border-primary/20 bg-primary/5 p-3">
              <div className="mb-2 flex items-start gap-2">
                <MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <p className="text-sm leading-relaxed text-foreground">
                  {t('workspace.newCanvas.directorTabExplanation')}
                </p>
              </div>
              <p className="text-xs font-medium leading-relaxed text-muted-foreground">
                {t('workspace.newCanvas.directorTabDetail')}
              </p>
            </div>
          )}
          {error && (
            <div className="mt-3 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}
        </div>

        <div className="flex flex-wrap justify-end gap-2 border-t border-border-subtle px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={creating}
            className="h-9 rounded-lg border border-input-border bg-surface-1 px-4 text-sm font-medium text-foreground transition-colors hover:border-input-border-hover hover:bg-surface-3 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t('workspace.newCanvas.cancel')}
          </button>
          {activeTab === 'manual' ? (
            <button
              type="submit"
              disabled={!canSubmit}
              className="h-9 rounded-lg bg-primary px-4 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
              data-testid="new-canvas-submit"
              title={t('workspace.newCanvas.createDirectlyTooltip')}
            >
              {creating ? t('workspace.newCanvas.creating') : t('workspace.newCanvas.createDirectly')}
            </button>
          ) : (
            <button
              type="submit"
              disabled={!canAskDirector}
              className="inline-flex h-9 items-center gap-2 rounded-lg border border-primary/40 bg-primary/10 px-4 text-sm font-medium text-primary transition-colors hover:border-primary/60 hover:bg-primary/15 disabled:cursor-not-allowed disabled:border-input-border disabled:bg-surface-1 disabled:text-text-muted"
              data-testid="new-canvas-ask-director"
              title={t('workspace.newCanvas.askDirectorTooltip')}
            >
              <MessageSquare className="h-4 w-4" />
              {t('workspace.newCanvas.askDirector')}
            </button>
          )}
        </div>
      </form>
    </div>
  )
}

interface ImportCanvasModalProps {
  isOpen: boolean
  sourceCanvases: CanvasTab[]
  sourceCanvasId: string
  description: string
  error: string | null
  onSourceCanvasChange: (canvasId: string) => void
  onDescriptionChange: (value: string) => void
  onAskDirector: () => void
  onClose: () => void
}

function ImportCanvasModal({
  isOpen,
  sourceCanvases,
  sourceCanvasId,
  description,
  error,
  onSourceCanvasChange,
  onDescriptionChange,
  onAskDirector,
  onClose,
}: ImportCanvasModalProps) {
  const { t } = useTranslation('pages')

  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  if (!isOpen) return null

  const displayCanvasTitle = (canvas: CanvasTab): string =>
    canvas.id === DEFAULT_CANVAS_ID && canvas.title === 'Agent Canvas'
      ? t('workspace.defaultCanvasTitle')
      : canvas.title

  const hasSources = sourceCanvases.length > 0
  const canAskDirector = hasSources && sourceCanvasId.trim().length > 0

  return (
    <div
      className="fixed inset-0 z-[1100] flex items-start justify-center overflow-y-auto bg-background/80 px-4 py-6 sm:pt-16 sm:pb-6"
      role="presentation"
      data-testid="import-canvas-modal"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="import-canvas-modal-title"
        className="flex max-h-[calc(100vh-3rem)] w-full max-w-lg shrink-0 flex-col rounded-xl border border-border bg-background shadow-2xl sm:max-h-[calc(100vh-5.5rem)]"
        data-testid="import-canvas-dialog"
        onMouseDown={(event) => event.stopPropagation()}
        onSubmit={(event) => {
          event.preventDefault()
          if (canAskDirector) onAskDirector()
        }}
      >
        <div className="flex items-center justify-between border-b border-border-subtle px-4 py-3">
          <h2 id="import-canvas-modal-title" className="text-lg font-semibold text-foreground">
            {t('workspace.importCanvas.modalTitle')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-input-border bg-surface-1 text-muted-foreground transition-colors hover:border-input-border-hover hover:bg-surface-3 hover:text-foreground"
            aria-label={t('workspace.importCanvas.close')}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 overflow-y-auto px-4 py-4">
          {hasSources ? (
            <div className="space-y-4">
              <p className="text-xs font-medium text-muted-foreground">
                {t('workspace.importCanvas.helpText')}
              </p>
              <label className="block">
                <span className="mb-1.5 block text-xs font-medium text-muted-foreground">
                  {t('workspace.importCanvas.sourceLabel')}
                </span>
                <select
                  autoFocus
                  value={sourceCanvasId}
                  onChange={(event) => onSourceCanvasChange(event.target.value)}
                  className="h-9 w-full rounded-lg border border-input-border bg-input-bg px-3 text-sm text-foreground outline-none transition-colors hover:border-input-border-hover hover:bg-surface-3 focus:border-primary focus:ring-2 focus:ring-primary/20"
                  data-testid="import-canvas-source-select"
                >
                  {sourceCanvases.map((canvas) => (
                    <option key={canvas.id} value={canvas.id}>
                      {displayCanvasTitle(canvas)}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="mb-1.5 block text-xs font-medium text-muted-foreground">
                  {t('workspace.importCanvas.descriptionLabel')}
                </span>
                <textarea
                  value={description}
                  onChange={(event) => onDescriptionChange(event.target.value)}
                  className="min-h-24 w-full resize-none rounded-lg border border-input-border bg-input-bg px-3 py-2 text-sm text-foreground outline-none transition-colors placeholder:text-text-muted hover:border-input-border-hover hover:bg-surface-3 focus:border-primary focus:ring-2 focus:ring-primary/20"
                  placeholder={t('workspace.importCanvas.descriptionPlaceholder')}
                  data-testid="import-canvas-description"
                />
              </label>

              {error && (
                <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {error}
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-lg border border-border-subtle bg-surface-0 px-3 py-2 text-sm text-muted-foreground">
              {t('workspace.importCanvas.noOtherCanvases')}
            </div>
          )}
        </div>

        <div className="flex flex-wrap justify-end gap-2 border-t border-border-subtle px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="h-9 rounded-lg border border-input-border bg-surface-1 px-4 text-sm font-medium text-foreground transition-colors hover:border-input-border-hover hover:bg-surface-3"
          >
            {t('workspace.importCanvas.cancel')}
          </button>
          <button
            type="submit"
            disabled={!canAskDirector}
            className="inline-flex h-9 items-center gap-2 rounded-lg border border-primary/40 bg-primary/10 px-4 text-sm font-medium text-primary transition-colors hover:border-primary/60 hover:bg-primary/15 disabled:cursor-not-allowed disabled:border-input-border disabled:bg-surface-1 disabled:text-text-muted"
            data-testid="import-canvas-ask-director"
          >
            <MessageSquare className="h-4 w-4" />
            {t('workspace.importCanvas.askDirector')}
          </button>
        </div>
      </form>
    </div>
  )
}

function cloneDataForCopiedEdges(
  nodeType: string | undefined,
  data: unknown,
): RawNodeDataRecord {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return {}
  const cloned = structuredClone(data) as RawNodeDataRecord
  for (const field of getDirectAssetInputPortWrites(nodeType, cloned)) {
    delete cloned[field]
  }
  return cloned
}

function normalizeStringArray(value: string[] | undefined): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : []
}

function areStringSetsEqual(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false
  for (const value of a) {
    if (!b.has(value)) return false
  }
  return true
}

function setStringSetIfChanged(
  setState: Dispatch<SetStateAction<Set<string>>>,
  keys: string[],
): void {
  const next = new Set(keys)
  setState((prev) => areStringSetsEqual(prev, next) ? prev : next)
}

function getCanvasRegenCueStorageKey(
  projectId: string | undefined,
  canvasId: string,
): string | null {
  if (!projectId) return null
  return `dw:canvas-regen-cue:v${CANVAS_REGEN_CUE_STORE_VERSION}:${projectId}:${canvasId}`
}

function getCanvasRegenCueTraceId(
  projectId: string | undefined,
  canvasId: string,
): string {
  return `canvas-regen-cue:${projectId ?? 'unknown'}:${canvasId}`
}

function readCanvasRegenCueStore(
  storageKey: string,
): NormalizedCanvasRegenCueStore | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(storageKey)
    if (!raw) return null
    const parsed: CanvasRegenCueStore = JSON.parse(raw)
    return {
      known: normalizeStringArray(parsed.known),
      acknowledged: normalizeStringArray(parsed.acknowledged),
    }
  } catch {
    return null
  }
}

function writeCanvasRegenCueStore(
  storageKey: string,
  store: NormalizedCanvasRegenCueStore,
): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(
      storageKey,
      JSON.stringify({
        version: CANVAS_REGEN_CUE_STORE_VERSION,
        known: store.known,
        acknowledged: store.acknowledged,
      }),
    )
  } catch {
    // localStorage can be unavailable in restricted browser contexts.
  }
}

function hasCanvasRegenOutputValue(value: ReactFlowFieldValue): boolean {
  if (value === null || value === undefined || value === '') return false
  if (Array.isArray(value)) return value.length > 0
  if (value instanceof Y.Array) return value.length > 0
  if (value instanceof Y.Map) return value.size > 0
  if (typeof value === 'object') return Object.keys(value).length > 0
  return true
}

function serializeCanvasRegenOutputValue(value: ReactFlowFieldValue): string {
  if (value === null) return 'null'
  if (value === undefined) return 'undefined'
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return JSON.stringify(value)
  }
  if (Array.isArray(value)) {
    return `[${value.map((item) => serializeCanvasRegenOutputValue(item)).join(',')}]`
  }
  if (value instanceof Y.Array) {
    return serializeCanvasRegenOutputValue(value.toArray())
  }
  if (value instanceof Y.Map) {
    const entries: string[] = []
    value.forEach((entryValue, entryKey) => {
      entries.push(`${entryKey}:${serializeCanvasRegenOutputValue(entryValue)}`)
    })
    return `{${entries.sort().join(',')}}`
  }
  if (typeof value === 'object') {
    return `{${Object.entries(value)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, entryValue]) => `${key}:${serializeCanvasRegenOutputValue(entryValue)}`)
      .join(',')}}`
  }
  return String(value)
}

function canvasRegenOutputSignature(node: WorkspaceNode): string | null {
  const outputParts: string[] = []
  for (const [key, value] of Object.entries(node.data ?? {})) {
    if (!key.startsWith('out_')) continue
    if (!hasCanvasRegenOutputValue(value)) continue
    outputParts.push(`${key}=${serializeCanvasRegenOutputValue(value)}`)
  }
  if (outputParts.length === 0) return null
  return outputParts.sort().join(CANVAS_REGEN_CUE_KEY_SEPARATOR)
}

function canvasRegenCompletionKey(nodeId: string, outputSignature: string): string {
  return `${nodeId}${CANVAS_REGEN_CUE_NODE_SEPARATOR}${outputSignature}`
}

function canvasRegenNodeIdFromKey(key: string): string {
  const separatorIndex = key.indexOf(CANVAS_REGEN_CUE_NODE_SEPARATOR)
  return separatorIndex === -1 ? key : key.slice(0, separatorIndex)
}

function canvasRegenCompletedKeys(nodes: WorkspaceNode[]): string[] {
  const keys: string[] = []
  for (const node of nodes) {
    if (stringDataField(node.data, 'status') !== 'completed') continue
    const outputSignature = canvasRegenOutputSignature(node)
    if (outputSignature) keys.push(canvasRegenCompletionKey(node.id, outputSignature))
  }
  return keys
}

function nodeDataRefMap(nodes: WorkspaceNode[]): Map<string, unknown> {
  const refs = new Map<string, unknown>()
  for (const node of nodes) {
    refs.set(node.id, node.data)
  }
  return refs
}

function haveNodeDataRefsChanged(nodes: WorkspaceNode[], previousRefs: Map<string, unknown>): boolean {
  if (nodes.length !== previousRefs.size) return true
  for (const node of nodes) {
    if (previousRefs.get(node.id) !== node.data) return true
  }
  return false
}

function isCanvasRegenRunningStatus(status: string | undefined): boolean {
  return status === 'running' ||
    status === 'pending_execution' ||
    status === 'pending_cost_confirmation'
}

function canvasRegenClassName(className: string | undefined, enabled: boolean): string | undefined {
  const parts = (className ?? '')
    .split(/\s+/)
    .filter((part) => part.length > 0 && part !== CANVAS_REGEN_CUE_CLASS)
  if (enabled) parts.push(CANVAS_REGEN_CUE_CLASS)
  return parts.length > 0 ? parts.join(' ') : undefined
}

function childNodeIdsField(
  data: unknown,
): (string | null)[] | undefined {
  const value = dataValue(data, 'in_child_node_ids')
  return Array.isArray(value)
    ? value.filter((item): item is string | null => typeof item === 'string' || item === null)
    : undefined
}

function childNodeIds(data: unknown): (string | null)[] {
  return childNodeIdsField(data) ?? []
}

function summarizePortValue(value: ReactFlowFieldValue | undefined): string {
  if (value === undefined) return 'undefined'
  if (value === null) return 'null'
  if (typeof value === 'string') {
    return value.length > 160 ? `${value.slice(0, 160)}...(${value.length})` : value
  }
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  if (value instanceof Y.Array) {
    const items = value.toArray().map((item) => summarizePortValue(item as ReactFlowFieldValue))
    const head = items.slice(0, 8).join('|')
    return value.length > 8 ? `${head}|...(${value.length})` : head
  }
  if (value instanceof Y.Map) {
    try {
      const json = JSON.stringify(value.toJSON())
      return json.length > 160 ? `${json.slice(0, 160)}...(${json.length})` : json
    } catch {
      return `Y.Map(${[...value.keys()].join(',')})`
    }
  }
  if (Array.isArray(value)) {
    const items = value.map((item) => summarizePortValue(item))
    const head = items.slice(0, 8).join('|')
    return value.length > 8 ? `${head}|...(${value.length})` : head
  }
  if (typeof File !== 'undefined' && value instanceof File) {
    return `File(${value.name},${value.size})`
  }
  try {
    const json = JSON.stringify(value)
    return json.length > 160 ? `${json.slice(0, 160)}...(${json.length})` : json
  } catch {
    return Object.prototype.toString.call(value)
  }
}

function summarizeEdgeRefs(edgeRefs: PortEdgeRef[], targetId?: string, targetHandle?: string): string {
  if (edgeRefs.length === 0) return '[]'
  return edgeRefs
    .slice(0, 12)
    .map((edge) => {
      const fromHandle = edge.sourceHandle ?? 'unknown'
      const toHandle = targetHandle ?? 'unknown'
      const toIndex = edge.toIndex === undefined ? '' : `[${edge.toIndex}]`
      const fromIndex = edge.fromIndex === undefined ? '' : `[${edge.fromIndex}]`
      const target = targetId ? ` -> ${targetId}.${toHandle}${toIndex}` : ''
      return `${edge.source}.${fromHandle}${fromIndex}${target}`
    })
    .join('|')
}

function backendTypeForFrontendType(frontendType: string | null | undefined): string | undefined {
  if (!frontendType) return undefined
  return FE_TYPE_TO_BACKEND[frontendType] ?? frontendType.replace(/([A-Z])/g, '_$1').toLowerCase()
}

function isMultiInputPort(
  targetNodeType: string | null | undefined,
  targetHandle: string | undefined,
): boolean {
  if (!targetHandle) return false
  const backendType = backendTypeForFrontendType(targetNodeType)
  return backendType ? YDOC_NODE_FIELDS[backendType]?.[targetHandle]?.isMulti === true : false
}

function requiresFromIndexForConnection(
  sourceNodeType: string | null | undefined,
  sourceHandle: string | undefined,
  targetNodeType: string | null | undefined,
  targetHandle: string | undefined,
): boolean {
  if (!sourceHandle || !targetHandle) return false
  const sourceBackendType = backendTypeForFrontendType(sourceNodeType)
  const targetBackendType = backendTypeForFrontendType(targetNodeType)
  if (!sourceBackendType || !targetBackendType) return false

  const sourceSpec = YDOC_NODE_FIELDS[sourceBackendType]?.[sourceHandle]
  const targetSpec = YDOC_NODE_FIELDS[targetBackendType]?.[targetHandle]
  return sourceSpec?.wireType === 'array' && targetSpec?.isMulti !== true
}

function edgeTargetMatches(
  edge: WorkspaceEdge,
  target: string,
  targetHandle: string | undefined,
): boolean {
  return edge.target === target && (edge.targetHandle ?? undefined) === targetHandle
}

function edgeToPortRef(edge: WorkspaceEdge): PortEdgeRef {
  return {
    source: edge.source,
    sourceHandle: edge.sourceHandle ?? undefined,
    toIndex: edge.data?.toIndex,
    fromIndex: edge.data?.fromIndex,
  }
}

function portEdgeRefsForTarget(
  edges: WorkspaceEdge[],
  target: string,
  targetHandle: string | undefined,
): PortEdgeRef[] {
  return edges.filter((edge) => edgeTargetMatches(edge, target, targetHandle)).map(edgeToPortRef)
}

function rememberTargetPort(
  ports: TargetPortRef[],
  target: string,
  targetHandle: string | undefined,
): void {
  const exists = ports.some(
    (port) => port.target === target && port.targetHandle === targetHandle,
  )
  if (!exists) ports.push({ target, targetHandle })
}

function isInsufficientSparksPayload(
  value: object,
): value is { code: 'insufficient_sparks' } {
  return (
    'code' in value &&
    value.code === 'insufficient_sparks'
  )
}

function readLegacyAutoConfirmCostDefault(): boolean | undefined {
  if (typeof window === 'undefined') return undefined
  try {
    const legacyRaw = window.localStorage.getItem(LEGACY_AUTO_CONFIRM_COST_KEY)
    if (legacyRaw === null) return undefined
    const legacyValue = JSON.parse(legacyRaw)
    return typeof legacyValue === 'boolean' ? legacyValue : undefined
  } catch {
    return undefined
  }
}

function shouldSyncToBackend(type: string): boolean {
  return type in FE_TYPE_TO_BACKEND && !CANVAS_HIDDEN_FE_TYPES.has(type)
}

// ─── ReactFlow Instance Capture ───────────────────────────────────────────────

function ReactFlowCapture({
  instanceRef,
  onReady,
}: {
  instanceRef: React.MutableRefObject<ReactFlowInstance<ReactFlowCanvasNodeData, ReactFlowCanvasEdgeData> | null>
  onReady?: () => void
}) {
  const instance = useReactFlow<ReactFlowCanvasNodeData, ReactFlowCanvasEdgeData>()
  useEffect(() => {
    instanceRef.current = instance
    onReady?.()
  }, [instance, instanceRef, onReady])
  return null
}

const IMAGE_MODEL_DEFAULT_NODE_TYPES = new Set([
  'characterDesigner',
  'locationDesigner',
  'objectDesigner',
  'imageGenerator',
  'storyboardDesigner',
  'storyboardNoEnhanceDesigner',
])

const imagePreferenceLog = createNadDebug('dw:preferred-image-model')
const multiFileUploadLog = createNadDebug('dw:multi-file-upload')
/** How long a queued Director upload node may take to appear in canvas state. */
const DIRECTOR_UPLOAD_NODE_APPEAR_TIMEOUT_MS = 10_000

function applyDefaultImageModel(
  nodeType: string,
  data: RawNodeDataRecord | undefined,
  defaultImageModel: string | null,
  explicitData: RawNodeDataRecord | undefined = data,
): RawNodeDataRecord | undefined {
  if (!defaultImageModel || !IMAGE_MODEL_DEFAULT_NODE_TYPES.has(nodeType)) {
    return data
  }
  if (
    explicitData
    && typeof explicitData.in_model === 'string'
    && explicitData.in_model.trim()
  ) {
    return data
  }
  return { ...(data ?? {}), in_model: defaultImageModel }
}

// ─── InteractiveMiniMap ───────────────────────────────────────────────────────

function InteractiveMiniMap() {
  const rf = useReactFlow<ReactFlowCanvasNodeData, ReactFlowCanvasEdgeData>()
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const svg = container.querySelector('svg')
    if (!svg) return

    let pressed = false

    function panTo(e: PointerEvent) {
      const rect = svg!.getBoundingClientRect()
      const vb = svg!.viewBox.baseVal
      const x = vb.x + ((e.clientX - rect.left) / rect.width) * vb.width
      const y = vb.y + ((e.clientY - rect.top) / rect.height) * vb.height
      rf.setCenter(x, y, { zoom: rf.getZoom(), duration: 0 })
    }

    const onDown = (e: PointerEvent) => { pressed = true; panTo(e) }
    const onMove = (e: PointerEvent) => { if (pressed) panTo(e) }
    const onUp = () => { pressed = false }

    svg.addEventListener('pointerdown', onDown)
    svg.addEventListener('pointermove', onMove)
    svg.addEventListener('pointerup', onUp)
    window.addEventListener('pointerup', onUp)

    return () => {
      svg.removeEventListener('pointerdown', onDown)
      svg.removeEventListener('pointermove', onMove)
      svg.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointerup', onUp)
    }
  }, [rf])

  return (
    <div ref={containerRef}>
      <MiniMap
        className="!relative !m-0 !bg-card !border !border-border !rounded-lg !shadow-sm overflow-hidden"
        nodeColor={(node) => {
          const status = stringDataField(node.data, 'status')
          if (status === 'running') return '#22c55e'
          if (status === 'error') return '#ef4444'
          if (status === 'success') return '#3b82f6'
          return '#6b7280'
        }}
        maskColor="rgba(0, 0, 0, 0.5)"
        style={{ width: 160, height: 100 }}
      />
    </div>
  )
}

// ─── WorkspaceV2Inner ─────────────────────────────────────────────────────────

/** Load the canonical canvas and confirm seeded uploads before the first Director turn. */
function WorkspaceV2Inner() {
  // Must run before the `useLocalStorage` calls below for `snapToGrid` and the
  // completion chime — those snapshot storage in their state initializer, so a
  // migration applied any later would not be seen until the next page load.
  const defaultOnResolution = useMemo(applyDefaultOnMigrations, [])
  const { t } = useTranslation('pages')
  const { canvasId: routeCanvasId, projectId: routeProjectId } = useParams<{
    canvasId?: string
    projectId?: string
  }>()
  const projectId = routeProjectId
  // Warm the user+environment-scoped catalog as soon as the authenticated
  // workspace mounts. Video nodes share this React Query entry, so a node
  // arriving later from Director/Yjs never has to initiate the first request.
  useVideoModels()
  const location = useLocation()
  const navigate = useNavigate()
  const navigationType = useNavigationType()
  const [searchParams] = useSearchParams()
  const activeCanvasId = resolveWorkspaceCanvasId(searchParams, routeCanvasId)
  const previousActiveCanvasIdRef = useRef(activeCanvasId)
  useEffect(() => {
    const from = previousActiveCanvasIdRef.current
    previousActiveCanvasIdRef.current = activeCanvasId
    if (from === activeCanvasId) return
    canvasTabNavLog({
      event: 'route-change',
      trace_id: canvasTabNavTraceId(activeCanvasId),
      projectId,
      from,
      to: activeCanvasId,
      navigationType,
      routeCanvasId: routeCanvasId ?? null,
      queryCanvasId: searchParams.get('canvas'),
    })
  }, [activeCanvasId, navigationType, projectId, routeCanvasId, searchParams])
  const isMobileViewport = useIsMobileViewport()
  const [mobilePane, setMobilePane] = useState<MobileWorkspacePane>('chat')
  const [canvasTabs, setCanvasTabs] = useState<CanvasTab[]>(() => normalizeCanvasTabs([], activeCanvasId))
  const [canvasTabsLoading, setCanvasTabsLoading] = useState(false)
  const [canvasTabsError, setCanvasTabsError] = useState<string | null>(null)
  const [multiCanvasEnabled, setMultiCanvasEnabled] = useState(false)
  const [creatingCanvas, setCreatingCanvas] = useState(false)
  const [deletingCanvasId, setDeletingCanvasId] = useState<string | null>(null)
  const [pendingCanvasDelete, setPendingCanvasDelete] = useState<PendingCanvasDelete | null>(null)
  const canvasDeleteAttemptRef = useRef(0)
  const [renamingCanvasId, setRenamingCanvasId] = useState<string | null>(null)
  const [editingCanvasId, setEditingCanvasId] = useState<string | null>(null)
  const [editingCanvasTitle, setEditingCanvasTitle] = useState('')
  const renameCommitInFlightRef = useRef<string | null>(null)
  const renameCancelledRef = useRef(false)
  // Canvas switcher dropdown (Workplace post 1640954430995187). The list stays
  // mounted while closed (just hidden) so selection state is always in the DOM.
  const [canvasSwitcherOpen, setCanvasSwitcherOpen] = useState(false)
  const canvasSwitcherRef = useRef<HTMLDivElement>(null)
  const setCanvasSwitcherOpenLogged = useCallback((
    open: boolean,
    reason: 'trigger' | 'pick' | 'delete' | 'outside-click' | 'escape',
    // The canvas the close acted on (picked / delete-requested), when there is one.
    targetCanvasId?: string,
  ) => {
    canvasTabNavLog({
      event: open ? 'switcher-open' : 'switcher-close',
      trace_id: canvasTabNavTraceId(activeCanvasId),
      pageLoadId: CANVAS_TAB_NAV_PAGE_LOAD_ID,
      projectId,
      reason,
      activeCanvasId,
      ...(targetCanvasId ? { targetCanvasId } : {}),
      canvasCount: canvasTabs.length,
    })
    setCanvasSwitcherOpen(open)
  }, [activeCanvasId, canvasTabs.length, projectId])
  useEffect(() => {
    if (!canvasSwitcherOpen) return
    function handlePointerDown(event: MouseEvent) {
      const target = event.target
      if (target instanceof Node && canvasSwitcherRef.current && !canvasSwitcherRef.current.contains(target)) {
        setCanvasSwitcherOpenLogged(false, 'outside-click')
      }
    }
    function handleKeyDown(event: KeyboardEvent) {
      // Escape inside the rename input cancels the rename, not the menu.
      if (event.key === 'Escape' && !(event.target instanceof HTMLInputElement)) {
        setCanvasSwitcherOpenLogged(false, 'escape')
      }
    }
    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [canvasSwitcherOpen, setCanvasSwitcherOpenLogged])
  const [newCanvasModalOpen, setNewCanvasModalOpen] = useState(false)
  const [newCanvasTitle, setNewCanvasTitle] = useState('')
  const [newCanvasPrompt, setNewCanvasPrompt] = useState('')
  const [newCanvasSourceNodeIds, setNewCanvasSourceNodeIds] = useState<Set<string>>(() => new Set())
  const [newCanvasSourceSelectorOpen, setNewCanvasSourceSelectorOpen] = useState(false)
  const [importCanvasModalOpen, setImportCanvasModalOpen] = useState(false)
  const [importSourceCanvasId, setImportSourceCanvasId] = useState('')
  const [importDescription, setImportDescription] = useState('')
  const [storedCanvasPerformanceSettings, setStoredCanvasPerformanceSettings] = useLocalStorage<
    Partial<CanvasPerformanceSettings>
  >(CANVAS_PERFORMANCE_SETTINGS_STORAGE_KEY, DEFAULT_CANVAS_PERFORMANCE_SETTINGS)
  const canvasPerformanceSettings = useMemo(
    () => normalizeCanvasPerformanceSettings(storedCanvasPerformanceSettings),
    [storedCanvasPerformanceSettings],
  )
  const setCanvasPerformanceSetting = useCallback(
    (key: keyof CanvasPerformanceSettings, value: boolean) => {
      setStoredCanvasPerformanceSettings({
        ...canvasPerformanceSettings,
        [key]: value,
      })
    },
    [canvasPerformanceSettings, setStoredCanvasPerformanceSettings],
  )

  const locationState = useMemo(
    () => workspaceLocationState(location.state, landingUploadLog),
    [location.state],
  )
  const initialUploadReferences = locationState.initialUploadReferences
  const [initialUploadsSeeded, setInitialUploadsSeeded] = useState(
    initialUploadReferences.length === 0,
  )
  const [initialUploadSeedError, setInitialUploadSeedError] = useState<string | null>(null)
  const initialUploadsSeededRef = useRef(initialUploadReferences.length === 0)

  // Read initial prompt from navigation state. Project defaults live in WorkflowState.
  const initialPrompt = initialUploadsSeeded ? locationState.initialPrompt : undefined

  const navigateToCanvas = useCallback(
    (canvasId: string, state?: { initialPrompt?: string }) => {
      if (!projectId || canvasId === activeCanvasId) return
      const nextSearchParams = new URLSearchParams(searchParams)
      nextSearchParams.delete('canvas')
      const query = nextSearchParams.toString()
      canvasTabNavLog({
        event: 'navigate',
        trace_id: canvasTabNavTraceId(canvasId),
        projectId,
        from: activeCanvasId,
        to: canvasId,
        caller: new Error().stack?.split('\n').slice(2, 5).map((line) => line.trim()).join(' <- '),
      })
      navigate(
        `/project/${projectId}/workspace/${canvasId}${query ? `?${query}` : ''}`,
        state ? { state } : undefined,
      )
    },
    [activeCanvasId, navigate, projectId, searchParams],
  )

  // Reconcile the canvas tab list against server truth. Three callers:
  //   1. mount / activeCanvas change — hydrates the initial tab list.
  //   2. chat WS (re)connect — recovers `canvas_created` broadcasts missed
  //      while AgentChat was unmounted or the WS was still handshaking.
  //   3. director-event upsert (belt-and-suspenders) — refills any fields
  //      absent from the broadcast payload.
  // Dedupe: an in-flight promise is reused (concurrent callers wait on it),
  // and non-force calls are dropped inside a 500 ms window so reconnect
  // storms and event bursts don't spam REST. Force-refresh is reserved for
  // the mount path which must always run.
  const refreshCanvasTabsInFlightRef = useRef<Promise<void> | null>(null)
  const refreshCanvasTabsLastAtRef = useRef(0)
  const refreshCanvasTabsCancelledRef = useRef(false)
  const REFRESH_CANVAS_TABS_MIN_INTERVAL_MS = 500

  const refreshCanvasTabs = useCallback(
    async (opts: { force?: boolean; reason: 'mount' | 'ws-connected' | 'director-event' }) => {
      if (!projectId) return
      if (
        !opts.force &&
        Date.now() - refreshCanvasTabsLastAtRef.current < REFRESH_CANVAS_TABS_MIN_INTERVAL_MS
      ) {
        canvasTabSyncLog({
          event: 'refresh-skipped',
          reason: opts.reason,
          sinceLastMs: Date.now() - refreshCanvasTabsLastAtRef.current,
        })
        return
      }
      const inFlight = refreshCanvasTabsInFlightRef.current
      if (inFlight) {
        canvasTabSyncLog({ event: 'refresh-coalesced', reason: opts.reason })
        return inFlight
      }
      refreshCanvasTabsLastAtRef.current = Date.now()
      const started = Date.now()
      canvasTabSyncLog({ event: 'refresh-start', reason: opts.reason, activeCanvasId })
      setCanvasTabsLoading(true)
      setCanvasTabsError(null)
      const promise = (async () => {
        try {
          const response = await listCanvases(projectId)
          if (refreshCanvasTabsCancelledRef.current) return
          const enabled = response.multi_canvas_enabled === true
          setMultiCanvasEnabled(enabled)
          setCanvasTabs((previous) => {
            const next = normalizeCanvasTabs(
              response.canvases,
              enabled ? activeCanvasId : DEFAULT_CANVAS_ID,
            )
            const prevIds = new Set(previous.map((t) => t.id))
            const nextIds = new Set(next.map((t) => t.id))
            canvasTabSyncLog({
              event: 'refresh-applied',
              reason: opts.reason,
              activeCanvasId,
              prevCount: previous.length,
              nextCount: next.length,
              added: next.filter((t) => !prevIds.has(t.id)).map((t) => t.id),
              removed: previous.filter((t) => !nextIds.has(t.id)).map((t) => t.id),
              multiCanvasEnabled: enabled,
              elapsedMs: Date.now() - started,
            })
            return next
          })
          if (!enabled && activeCanvasId !== DEFAULT_CANVAS_ID) {
            navigateToCanvas(DEFAULT_CANVAS_ID)
          }
        } catch (error) {
          if (refreshCanvasTabsCancelledRef.current) return
          const message =
            error instanceof Error ? error.message : t('workspace.newCanvas.loadError')
          setMultiCanvasEnabled(false)
          setCanvasTabsError(message)
          setCanvasTabs((previous) => normalizeCanvasTabs(previous, DEFAULT_CANVAS_ID))
          canvasTabSyncLog({
            event: 'refresh-failed',
            reason: opts.reason,
            errorMessage: message,
            elapsedMs: Date.now() - started,
          })
        } finally {
          if (!refreshCanvasTabsCancelledRef.current) {
            setCanvasTabsLoading(false)
          }
          refreshCanvasTabsInFlightRef.current = null
        }
      })()
      refreshCanvasTabsInFlightRef.current = promise
      return promise
    },
    [activeCanvasId, navigateToCanvas, projectId, t],
  )

  useEffect(() => {
    refreshCanvasTabsCancelledRef.current = false
    void refreshCanvasTabs({ force: true, reason: 'mount' })
    return () => {
      refreshCanvasTabsCancelledRef.current = true
    }
  }, [refreshCanvasTabs])

  const initialReferencedNodeIds = initialUploadsSeeded
    ? locationState.initialReferencedNodeIds
    : undefined
  const initialReplayScenario = initialUploadsSeeded
    ? locationState.initialReplayScenario
    : undefined

  // Project data
  const {
    data: project,
    error: projectErrorDetails,
    isLoading: projectLoading,
    isError: projectError,
  } = useProject(projectId || '')
  // Project defaults (visual style + "Output Quality" resolution) stamped onto
  // newly-created nodes as the client twin of the server-side
  // GraphManager._apply_add_node stamp. BOTH read from the project-config endpoint
  // (WorkflowState.{style_id, video_resolution}) — the SAME source the
  // ProjectConfigGear gear and the Director's _apply_add_node read/write — so a new
  // node inherits the project's *configured* style. (Previously the style stamp
  // read the stale legacy Project.style enum off /projects/{id}/full, which is
  // never synced when the user changes style via the gear; that value was not a
  // catalog slug, so backend resolution fell back to the "bold-manhwa" default —
  // Workplace post 1573282384429059.)
  // Per-canvas config: pass the local activeCanvasId EXPLICITLY. This component
  // mounts ActiveCanvasIdProvider only in its returned subtree, so a hook-internal
  // context read here would resolve to the default ("agent") canvas and stamp the
  // wrong style/resolution onto nodes created on a secondary canvas (the multi-
  // canvas half of the "style change doesn't follow through" bug).
  const {
    data: projectConfig,
    isLoading: projectConfigLoading,
    isError: projectConfigError,
  } = useProjectConfig(projectId || '', activeCanvasId)
  const workspaceStyleId = projectConfig?.style_id
  const projectVideoResolution = projectConfig?.video_resolution
  const projectAspectRatio = projectConfig?.aspect_ratio
  const { defaultModel: accountDefaultImageModel } = useImageModels()
  const preferredImageModel = projectConfig?.preferred_image_model ?? 'auto'
  const defaultImageModel = preferredImageModel === 'auto' ? accountDefaultImageModel : preferredImageModel

  // Agent conversation initialization
  const [conversationLoading, setConversationLoading] = useState(false)

  useEffect(() => {
    if (!projectId) return
    const initConversation = async () => {
      setConversationLoading(true)
      try {
        const { getOrCreateAgentConversation } = await import('../api/client')
        await getOrCreateAgentConversation(projectId)
      } catch (error) {
        console.error('[WorkspaceV2] Failed to load agent conversation:', error)
      } finally {
        setConversationLoading(false)
      }
    }
    initConversation()
  }, [projectId])

  // Project config gates the canvas too. Every toolbox add stamps the project
  // defaults (style / resolution / aspect ratio) onto the new node from
  // `projectConfig`, and each stamp no-ops when the value is undefined — so a
  // node added between "canvas interactive" and "config arrived" silently keeps
  // schema defaults. The other two queries already gate the render; this one
  // was simply never added (found by Codex review on #2855).
  //
  // But the config query is keyed PER CANVAS, so it goes cold on every first
  // visit to a canvas tab (and again after gcTime eviction). Gating the whole
  // page on it swapped the entire workspace — tab bar, chat, header — for the
  // full-screen LoadingState and remounted it, which users saw as the tab
  // click "reloading the current canvas" (Workplace post 1634032408354056).
  // So config only blocks the page until the shell has rendered once for this
  // project; after that a cold per-canvas config gates the canvas surface
  // instead (`canvasConfigReady` below), leaving the shell mounted.
  const [shellReadyProjectId, setShellReadyProjectId] = useState<string | null>(null)
  const shellReady = shellReadyProjectId != null && shellReadyProjectId === projectId
  const configBlocksPage = projectConfigLoading && !shellReady
  const isLoading = projectLoading || conversationLoading || configBlocksPage
  useEffect(() => {
    if (!isLoading && projectId) setShellReadyProjectId(projectId)
  }, [isLoading, projectId])
  const workspaceMountLoggedRef = useRef(false)
  useEffect(() => {
    if (workspaceMountLoggedRef.current) return
    workspaceMountLoggedRef.current = true
    const documentLoad = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined
    canvasTabNavLog({
      event: 'workspace-mount',
      trace_id: canvasTabNavTraceId(activeCanvasId),
      pageLoadId: CANVAS_TAB_NAV_PAGE_LOAD_ID,
      projectId,
      canvasId: activeCanvasId,
      // 'reload' = browser reload; 'navigate' = fresh document or in-app route entry.
      documentLoadType: documentLoad?.type ?? 'unknown',
    })
  }, [activeCanvasId, projectId])
  useEffect(() => {
    canvasTabNavLog({
      event: 'workspace-loading-gate',
      trace_id: canvasTabNavTraceId(activeCanvasId),
      pageLoadId: CANVAS_TAB_NAV_PAGE_LOAD_ID,
      projectId,
      canvasId: activeCanvasId,
      fullPageSpinner: isLoading,
      // Joined string, not an array: the NAD logger collapses arrays to "[N items]".
      blockedBy: [
        projectLoading && 'project',
        conversationLoading && 'conversation',
        configBlocksPage && 'canvas-config',
      ].filter(Boolean).join(',') || 'none',
      canvasConfigLoading: projectConfigLoading,
      shellReady,
    })
  }, [activeCanvasId, configBlocksPage, conversationLoading, isLoading, projectConfigLoading, projectId, projectLoading, shellReady])

  // Director settings (persisted to localStorage)
  const legacyAutoConfirmCostDefault = useMemo(readLegacyAutoConfirmCostDefault, [])
  const [transientAutoConfirmCostSettings, setTransientAutoConfirmCostSettings] =
    useState<AutoConfirmCostSettings | null>(
      () => locationState.initialAutoConfirmCostSettings ?? null,
    )
  const [autoConfirmImageCosts, setAutoConfirmImageCosts] = useLocalStorage(
    AUTO_CONFIRM_IMAGE_COST_KEY,
    legacyAutoConfirmCostDefault ?? DEFAULT_AUTO_CONFIRM_COST_SETTINGS.image,
  )
  const [autoConfirmVideoCosts, setAutoConfirmVideoCosts] = useLocalStorage(
    AUTO_CONFIRM_VIDEO_COST_KEY,
    legacyAutoConfirmCostDefault ?? DEFAULT_AUTO_CONFIRM_COST_SETTINGS.video,
  )
  const [taskCompletionChimeEnabled, setTaskCompletionChimeEnabled] = useLocalStorage(
    TASK_COMPLETION_CHIME_STORAGE_KEY,
    true,
  )

  // Emit how each default-on setting resolved, once per workspace mount.
  // `setting_changed` alone cannot evidence this feature: a trace showing
  // `enabled: true` is equally explainable as a stale stored preference or a
  // user toggle. `source: "code_default"` with `stored_value_before: null` is
  // the line that proves the raised default actually reached the user.
  const defaultOnResolutionLogged = useRef(false)
  useEffect(() => {
    if (defaultOnResolutionLogged.current) return
    defaultOnResolutionLogged.current = true
    const traceId = `default-on:${projectId ?? 'unknown'}:${activeCanvasId}`
    snapLog({
      event: 'canvas-snap-to-grid:setting_initialized',
      trace_id: traceId,
      project_id: projectId ?? null,
      canvas_id: activeCanvasId,
      stored_value_before: defaultOnResolution.snapToGrid.storedValueBefore,
      effective_value: defaultOnResolution.snapToGrid.effectiveValue,
      source: defaultOnResolution.snapToGrid.source,
      migration_applied: defaultOnResolution.snapToGrid.migrationApplied,
    })
    taskCompletionChimeLog({
      event: 'task-completion-chime:setting_initialized',
      trace_id: traceId,
      project_id: projectId ?? null,
      canvas_id: activeCanvasId,
      stored_value_before: defaultOnResolution.taskCompletionChime.storedValueBefore,
      effective_value: defaultOnResolution.taskCompletionChime.effectiveValue,
      source: defaultOnResolution.taskCompletionChime.source,
      migration_applied: defaultOnResolution.taskCompletionChime.migrationApplied,
    })
  }, [defaultOnResolution, projectId, activeCanvasId])
  const updateTransientAutoConfirmCostSetting = useCallback(
    (key: keyof AutoConfirmCostSettings, value: boolean) => {
      setTransientAutoConfirmCostSettings((previous) => previous
        ? { ...previous, [key]: value }
        : previous
      )
    },
    [],
  )
  const handleAutoConfirmImageCostChange = useCallback(
    (value: boolean) => {
      updateTransientAutoConfirmCostSetting('image', value)
      setAutoConfirmImageCosts(value)
    },
    [setAutoConfirmImageCosts, updateTransientAutoConfirmCostSetting],
  )
  const handleAutoConfirmVideoCostChange = useCallback(
    (value: boolean) => {
      updateTransientAutoConfirmCostSetting('video', value)
      setAutoConfirmVideoCosts(value)
    },
    [setAutoConfirmVideoCosts, updateTransientAutoConfirmCostSetting],
  )
  // Director may turn cost auto-confirm OFF (never on) — mirror it locally.
  useDirectorAutoConfirmOff({
    projectId,
    onImageOff: handleAutoConfirmImageCostChange,
    onVideoOff: handleAutoConfirmVideoCostChange,
  })
  const handleTaskCompletionChimeEnabledChange = useCallback((value: boolean) => {
    setTaskCompletionChimeEnabled(value)
    taskCompletionChimeLog({
      event: 'task-completion-chime:setting_changed',
      trace_id: `task-completion-chime:${projectId ?? 'unknown'}:${activeCanvasId}:setting`,
      project_id: projectId ?? null,
      canvas_id: activeCanvasId,
      chime_enabled: value,
      persisted: true,
      persistence_scope: 'local_storage',
    })
    if (value) {
      void primeTaskCompletionChime().then((result) => {
        const audioReadyLog: {
          event: string
          trace_id: string
          project_id: string | null
          canvas_id: string
          readiness_status: string
          failure_reason?: string
        } = {
          event: 'task-completion-chime:audio_ready',
          trace_id: `task-completion-chime:${projectId ?? 'unknown'}:${activeCanvasId}:setting`,
          project_id: projectId ?? null,
          canvas_id: activeCanvasId,
          readiness_status: result.status,
        }
        if (result.reason) audioReadyLog.failure_reason = result.reason
        taskCompletionChimeLog(audioReadyLog)
      })
    }
  }, [activeCanvasId, projectId, setTaskCompletionChimeEnabled])
  const autoConfirmCostSettings = useMemo(
    () => transientAutoConfirmCostSettings ?? {
      image: autoConfirmImageCosts,
      video: autoConfirmVideoCosts,
    },
    [
      autoConfirmImageCosts,
      autoConfirmVideoCosts,
      transientAutoConfirmCostSettings,
    ],
  )

  // Usage & cost modal (RFC-0103)
  const [showUsageCost, setShowUsageCost] = useState(false)

  // Canvas state
  const nodesRef = useRef<WorkspaceNode[]>([])
  const edgesRef = useRef<WorkspaceEdge[]>([])
  const previousOutputSnapshotsRef = useRef<Map<string, Map<string, string>>>(new Map())
  const previousCanvasRegenRuntimeRef = useRef<Map<string, CanvasRegenNodeRuntime>>(new Map())
  const canvasRegenOutputAtRunStartRef = useRef<Map<string, string>>(new Map())
  const previousCanvasRegenDataRefsRef = useRef<Map<string, unknown>>(new Map())
  const previousOutputSyncDataRefsRef = useRef<Map<string, unknown>>(new Map())
  const previousOutputSyncEdgesRef = useRef<WorkspaceEdge[] | null>(null)
  const [nodes, setNodes, onNodesChangeBase] = useNodesState<ReactFlowCanvasNodeData>([])
  const [edges, setEdges, onEdgesChangeBase] = useEdgesState<ReactFlowCanvasEdgeData>([])
  const [pendingNodeDelete, setPendingNodeDelete] = useState<PendingNodeDelete | null>(null)
  const pendingNodeDeleteRef = useRef<PendingNodeDelete | null>(null)
  const recentlyDeletedNodeIdsRef = useRef<Set<string>>(new Set())
  const [unseenCanvasRegenKeys, setUnseenCanvasRegenKeys] = useState<Set<string>>(() => new Set())
  const unseenCanvasRegenKeysRef = useRef<Set<string>>(new Set())
  const { anyBottomPanelVisible } = useCanvasBottomPanelVisibility()
  const { emitNuxEvent } = useNuxTooltipEvents()

  // Keep refs in sync with state
  useEffect(() => { nodesRef.current = nodes }, [nodes])
  useEffect(() => { edgesRef.current = edges }, [edges])
  useEffect(() => { pendingNodeDeleteRef.current = pendingNodeDelete }, [pendingNodeDelete])
  useEffect(() => { unseenCanvasRegenKeysRef.current = unseenCanvasRegenKeys }, [unseenCanvasRegenKeys])

  useEffect(() => {
    const dataRefsChanged = haveNodeDataRefsChanged(nodes, previousCanvasRegenDataRefsRef.current)
    previousCanvasRegenDataRefsRef.current = nodeDataRefMap(nodes)
    if (!dataRefsChanged) return

    const storageKey = getCanvasRegenCueStorageKey(projectId, activeCanvasId)
    const traceId = getCanvasRegenCueTraceId(projectId, activeCanvasId)
    const previousRuntime = previousCanvasRegenRuntimeRef.current
    const currentRuntime = new Map<string, CanvasRegenNodeRuntime>()
    const currentCompletedKeys = canvasRegenCompletedKeys(nodes)
    const candidateCompletedKeys: string[] = []

    for (const node of nodes) {
      const status = stringDataField(node.data, 'status')
      const outputSignature = canvasRegenOutputSignature(node)
      const previous = previousRuntime.get(node.id)
      const wasRunning = isCanvasRegenRunningStatus(previous?.status)
      const isRunning = isCanvasRegenRunningStatus(status)

      if (isRunning && !wasRunning) {
        if (outputSignature) {
          canvasRegenOutputAtRunStartRef.current.set(node.id, outputSignature)
          canvasRegenCueLog({
            event: 'canvas-regen-cue:run_started',
            trace_id: traceId,
            node_id: node.id,
            project_id: projectId ?? null,
            canvas_id: activeCanvasId,
            node_type: node.type ?? null,
            had_existing_output: true,
          })
        } else {
          canvasRegenOutputAtRunStartRef.current.delete(node.id)
        }
      }

      if (status === 'completed' && wasRunning && outputSignature) {
        emitNuxEvent({ type: 'nodeReady', nodeId: node.id, nodeType: node.type })
        const runStartOutput = canvasRegenOutputAtRunStartRef.current.get(node.id)
        if (runStartOutput !== outputSignature) {
          candidateCompletedKeys.push(canvasRegenCompletionKey(node.id, outputSignature))
        }
        canvasRegenOutputAtRunStartRef.current.delete(node.id)
      } else if (!isRunning && status !== previous?.status) {
        canvasRegenOutputAtRunStartRef.current.delete(node.id)
      }

      currentRuntime.set(node.id, { status, outputSignature })
    }

    previousCanvasRegenRuntimeRef.current = currentRuntime

    if (!storageKey || currentCompletedKeys.length === 0) {
      setStringSetIfChanged(setUnseenCanvasRegenKeys, [])
      return
    }

    const stored = readCanvasRegenCueStore(storageKey)
    if (!stored) {
      writeCanvasRegenCueStore(storageKey, {
        known: currentCompletedKeys,
        acknowledged: currentCompletedKeys,
      })
      setStringSetIfChanged(setUnseenCanvasRegenKeys, [])
      canvasRegenCueLog({
        event: 'canvas-regen-cue:baseline_initialized',
        trace_id: traceId,
        project_id: projectId ?? null,
        canvas_id: activeCanvasId,
        completed_node_count: currentCompletedKeys.length,
      })
      return
    }

    const currentKeySet = new Set(currentCompletedKeys)
    const knownKeySet = new Set(stored.known)
    const candidateKeySet = new Set(candidateCompletedKeys)
    const acknowledgedKeySet = new Set(
      stored.acknowledged.filter((key) => currentKeySet.has(key)),
    )
    const newKeys = currentCompletedKeys.filter((key) => !knownKeySet.has(key))
    const nextAcknowledgedKeys = currentCompletedKeys.filter((key) =>
      acknowledgedKeySet.has(key) ||
      (newKeys.includes(key) && !candidateKeySet.has(key)),
    )
    const nextAcknowledgedKeySet = new Set(nextAcknowledgedKeys)
    const nextUnseenKeys = currentCompletedKeys.filter((key) => !nextAcknowledgedKeySet.has(key))
    const newUnseenKeys = newKeys.filter((key) => candidateKeySet.has(key))

    writeCanvasRegenCueStore(storageKey, {
      known: currentCompletedKeys,
      acknowledged: nextAcknowledgedKeys,
    })
    setStringSetIfChanged(setUnseenCanvasRegenKeys, nextUnseenKeys)

    if (newUnseenKeys.length > 0) {
      const previousUnseenCount = unseenCanvasRegenKeysRef.current.size
      const newUnseenNodeIds = newUnseenKeys.map(canvasRegenNodeIdFromKey)
      canvasRegenCueLog({
        event: 'canvas-regen-cue:regen_cue_shown',
        trace_id: traceId,
        project_id: projectId ?? null,
        canvas_id: activeCanvasId,
        new_unseen_node_ids: newUnseenNodeIds.join(','),
        new_unseen_count: newUnseenKeys.length,
        previous_unseen_count: previousUnseenCount,
        unseen_count_after: nextUnseenKeys.length,
      })
    }
  }, [activeCanvasId, emitNuxEvent, nodes, projectId])

  const unseenCanvasRegenNodeIdsSignature = useMemo(
    () => Array.from(unseenCanvasRegenKeys)
      .map(canvasRegenNodeIdFromKey)
      .sort()
      .join(CANVAS_REGEN_CUE_KEY_SEPARATOR),
    [unseenCanvasRegenKeys],
  )

  useEffect(() => {
    const unseenNodeIds = new Set(
      unseenCanvasRegenNodeIdsSignature
        ? unseenCanvasRegenNodeIdsSignature.split(CANVAS_REGEN_CUE_KEY_SEPARATOR)
        : [],
    )
    setNodes((prevNodes) => {
      let changed = false
      const nextNodes = prevNodes.map((node) => {
        const nextClassName = canvasRegenClassName(node.className, unseenNodeIds.has(node.id))
        if (nextClassName === node.className) return node
        changed = true
        return { ...node, className: nextClassName }
      })
      if (changed) nodesRef.current = nextNodes
      return changed ? nextNodes : prevNodes
    })
  }, [setNodes, unseenCanvasRegenNodeIdsSignature])

  const acknowledgeCanvasRegenCue = useCallback((nodeId: string, interaction: string) => {
    const matchingKeys = Array.from(unseenCanvasRegenKeysRef.current)
      .filter((key) => canvasRegenNodeIdFromKey(key) === nodeId)
    if (matchingKeys.length === 0) return

    const previousUnseenCount = unseenCanvasRegenKeysRef.current.size
    const storageKey = getCanvasRegenCueStorageKey(projectId, activeCanvasId)
    const currentCompletedKeys = canvasRegenCompletedKeys(nodesRef.current)
    if (storageKey) {
      const stored = readCanvasRegenCueStore(storageKey)
      const acknowledgedKeySet = new Set(stored?.acknowledged ?? [])
      for (const key of matchingKeys) {
        acknowledgedKeySet.add(key)
      }
      writeCanvasRegenCueStore(storageKey, {
        known: currentCompletedKeys,
        acknowledged: currentCompletedKeys.filter((key) => acknowledgedKeySet.has(key)),
      })
    }

    setUnseenCanvasRegenKeys((prev) => {
      const next = new Set(prev)
      for (const key of matchingKeys) {
        next.delete(key)
      }
      return areStringSetsEqual(prev, next) ? prev : next
    })
    canvasRegenCueLog({
      event: 'canvas-regen-cue:regen_cue_acknowledged',
      trace_id: getCanvasRegenCueTraceId(projectId, activeCanvasId),
      node_id: nodeId,
      project_id: projectId ?? null,
      canvas_id: activeCanvasId,
      interaction,
      was_unseen_before: true,
      cleared_unseen: true,
      previous_unseen_count: previousUnseenCount,
      unseen_count_after: Math.max(0, previousUnseenCount - matchingKeys.length),
    })
  }, [activeCanvasId, projectId])

  // ─── Multi-select (single source of truth) ─────────────────────────
  // Selection is lifted out of the distributed node.selected booleans
  // into a Set. The ref provides synchronous reads (inside
  // onNodesChange, applySelection); the state triggers re-renders and
  // the stamping useEffect.
  const selectedNodeIdsRef = useRef<Set<string>>(new Set())
  const [selectedNodeIds, setSelectedNodeIds] = useState<Set<string>>(new Set())
  const [viewMode, setViewMode] = useWorkspaceViewPreference()
  // Shared with undo history: never mint a different room key for UI memory.
  // The empty string means no project has been loaded yet.
  const canvasRoomKey = projectId ? canvasRoomKeyOf(projectId, activeCanvasId) : ''
  const { rememberNode, captureNodeInteraction, listEntryNodeId } = useLastInteractedNode(canvasRoomKey, nodesRef)
  useProjectVisit(projectId) // RFC-1577 project_visit_id (no event of its own)
  useProjectTeamAnalytics(projectId) // owner_type / team_id for project-scoped click events
  const [assetManagerOpen, setAssetManagerOpen] = useState(false)
  // Drawers opened from <CanvasBottomPalette>. The asset manager reuses
  // `assetManagerOpen` above, which also drives <AssetManagerDrawer> and
  // SimplifiedWorkspace's "open library" affordance.
  const [paletteClipDownloadOpen, setPaletteClipDownloadOpen] = useState(false)
  const [paletteShortcutsOpen, setPaletteShortcutsOpen] = useState(false)
  const [paletteAddNodeOpen, setPaletteAddNodeOpen] = useState(false)
  // Which gesture a plain left-drag performs. Session-only, like viewMode:
  // every workspace opens with the hand tool rather than restoring whatever
  // was last picked, so a drag never does something unexpected on load.
  const [canvasTool, setCanvasTool] = useState<CanvasTool>(DEFAULT_CANVAS_TOOL)
  // Subscribed to below for marquee coverage. A `useStore` selector on the
  // RECTANGLE would re-render the whole workspace on every pointer move of a
  // drag; the store handle does not subscribe by itself.
  const reactFlowStore = useStoreApi()
  // Marks the one emission that is allowed to change selection during a
  // marquee — see the drop in onNodesChange and the subscriber below.
  const applyingMarqueeSelectionRef = useRef(false)
  // What the marquee last asked for. Diffing against this instead of against
  // the store is what keeps a drag from re-emitting no-op `select` changes
  // every pointer move -- nodeInternals lags our own writes by a tick, so it
  // cannot answer "did I already ask for this?". Null between drags.
  const marqueeSelectionRef = useRef<Set<string> | null>(null)
  // `userSelectionActive` is safe to subscribe to: unlike the rectangle it
  // flips only at marquee start and end, so this costs two renders per drag.
  // Gates the UI that must not react while a selection is being drawn.
  const isMarqueeSelecting = useStore(marqueeSelectingSelector)

  const selectSimplifiedNode = useCallback((id: string) => {
    rememberNode(id)
    const selection = new Set([id])
    selectedNodeIdsRef.current = selection
    setSelectedNodeIds(selection)
    setNodes((current) => current.map((node) => {
      const selected = node.id === id
      return Boolean(node.selected) === selected ? node : { ...node, selected }
    }))
    simplifiedViewLog({ event: 'simplified-view:node-selected', feature: 'simplified-view', project_id: projectId, node_id: id })
  }, [projectId, rememberNode, setNodes])

  const changeViewMode = useCallback((next: WorkspaceViewMode) => {
    const restoredNodeId = next === 'simplified' && viewMode !== next
      ? listEntryNodeId(selectedNodeIdsRef.current)
      : undefined
    if (restoredNodeId) selectSimplifiedNode(restoredNodeId)
    simplifiedViewLog({
      event: 'workspace-view:changed',
      feature: 'simplified-view',
      project_id: projectId,
      canvas_id: activeCanvasId,
      previous_mode: viewMode,
      mode: next,
      persisted: true,
      restored_node_id: restoredNodeId ?? null,
      selected_node_ids: Array.from(selectedNodeIdsRef.current),
    })
    setViewMode(next)
  }, [projectId, activeCanvasId, listEntryNodeId, selectSimplifiedNode, setViewMode, viewMode])

  // One palette for both views. The list view has no canvas to drag, so it
  // passes `withToolPicker=false` and the palette drops the hand/select slot.
  const renderBottomPalette = (withToolPicker: boolean) => (
    <CanvasBottomPalette
      activeTool={canvasTool}
      onToolChange={withToolPicker ? setCanvasTool : undefined}
      isAssetManagerOpen={assetManagerOpen}
      onOpenAssetManager={() => setAssetManagerOpen((prev) => !prev)}
      isAddNodeOpen={paletteAddNodeOpen}
      onOpenAddNode={() => setPaletteAddNodeOpen((prev) => !prev)}
      isDownloadAssetsOpen={paletteClipDownloadOpen}
      onOpenDownloadAssets={() => setPaletteClipDownloadOpen((prev) => !prev)}
      isKeyboardShortcutsOpen={paletteShortcutsOpen}
      onOpenKeyboardShortcuts={() => setPaletteShortcutsOpen((prev) => !prev)}
      viewMode={viewMode}
      onViewModeChange={changeViewMode}
      analyticsSurface={withToolPicker ? 'canvas_palette' : 'list_palette'}
    />
  )

  const renderActiveEdgesOnly = canvasPerformanceSettings.renderActiveEdgesOnly
  const hoveredVisualEdgeNodeSelector = useCallback(
    (id: string | null) => (renderActiveEdgesOnly ? id : null),
    [renderActiveEdgesOnly],
  )
  const hoveredNodeId = useHoveredNodeIdSelector(hoveredVisualEdgeNodeSelector)
  const setHoveredNodeId = useSetHoveredNodeId()
  const shiftHeldRef = useRef(false)
  const [isShiftSelecting, setIsShiftSelecting] = useState(false)
  // Physical Cmd/Meta state, reconciled from keyboard `getModifierState('Meta')`.
  // The custom wheel-zoom handler uses THIS instead of the wheel event's own
  // `metaKey`, which goes stale on macOS after Cmd+V and caused scroll to zoom.
  const metaKeyDownRef = useRef(false)

  // Stamp node.selected from the set whenever it changes.
  // For user clicks this is a backup — onNodesChange stamps synchronously.
  // For programmatic changes (openPanelToTab, duplicateNode) this ensures
  // ReactFlow's visual selection stays in sync.
  useEffect(() => {
    setNodes((prev) => {
      let changed = false
      const next = prev.map((node) => {
        const shouldSelect = selectedNodeIds.has(node.id)
        if (Boolean(node.selected) === shouldSelect) return node
        changed = true
        return { ...node, selected: shouldSelect }
      })
      if (changed) nodesRef.current = next
      return changed ? next : prev
    })
  }, [selectedNodeIds, setNodes])

  // Handle stop: set all running nodes to idle
  const handleStop = useCallback(() => {
    setNodes(nds => {
      const updated = nds.map(n => {
        const status = stringDataField(n.data, 'status')
        if (status === 'running') {
          return { ...n, data: { ...n.data, status: 'idle' as const } }
        }
        return n
      })
      nodesRef.current = updated
      return updated
    })
  }, [setNodes])

  // Pan & zoom the canvas to center a specific node (used by mention pill clicks)
  const handleMentionClick = useCallback((nodeId: string) => {
    const rf = reactFlowInstance.current
    if (!rf) return
    const target = nodesRef.current.find(n => n.id === nodeId)
    if (!target) return
    rememberNode(nodeId)
    rf.fitView({ nodes: [target], padding: 0.3, duration: 500, maxZoom: 1 })
  }, [rememberNode])

  // Pan to a node the user just added, then select it so it shows the
  // selection highlight. The pan keeps the current zoom (it only zooms out
  // if the node would not fit).
  //
  // Two ordering constraints:
  // - ReactFlow's fitView is a silent no-op until the node has been
  //   measured, which is after the frame that created it, so wait on the
  //   store for its width/height.
  // - Selecting opens the node's UnifiedBottomPanel, which runs its own pan
  //   to reveal itself. Selecting before our pan finishes makes the two
  //   viewport animations fight, so select only once the pan is done.
  //
  // A newer add cancels any pending one: the latest node wins.
  const pendingNewNodeFocusRef = useRef<(() => void) | null>(null)
  const focusNewNode = useCallback((nodeId: string) => {
    pendingNewNodeFocusRef.current?.()
    pendingNewNodeFocusRef.current = null
    const rf = reactFlowInstance.current
    if (!rf) return
    const zoom = rf.getZoom()
    let unsubscribe: (() => void) | null = null
    let timeoutTimer = 0
    let selectTimer = 0
    let frame = 0
    const cancel = () => {
      unsubscribe?.()
      window.clearTimeout(timeoutTimer)
      window.clearTimeout(selectTimer)
      window.cancelAnimationFrame(frame)
      if (pendingNewNodeFocusRef.current === cancel) pendingNewNodeFocusRef.current = null
    }
    const select = () => {
      rememberNode(nodeId)
      const selection = new Set([nodeId])
      selectedNodeIdsRef.current = selection
      setSelectedNodeIds(selection)
    }
    // Returns false (and does nothing) while the node is unmeasured.
    const panThenSelect = () => {
      const focused = rf.fitView({ nodes: [{ id: nodeId }], padding: 0.3, duration: NEW_NODE_FOCUS_PAN_MS, maxZoom: zoom })
      if (focused) selectTimer = window.setTimeout(select, NEW_NODE_FOCUS_PAN_MS)
      return focused
    }
    pendingNewNodeFocusRef.current = cancel
    if (panThenSelect()) {
      newNodeFocusLog({ event: 'new-node-focus:immediate', node_id: nodeId })
      return
    }
    unsubscribe = reactFlowStore.subscribe((state) => {
      const measured = state.nodeInternals.get(nodeId)
      if (!measured?.width || !measured.height) return
      unsubscribe?.()
      unsubscribe = null
      window.clearTimeout(timeoutTimer)
      // Out of the store listener: fitView writes the transform back to the
      // same store.
      frame = window.requestAnimationFrame(() => {
        newNodeFocusLog({ event: 'new-node-focus:after-measure', node_id: nodeId, focused: panThenSelect() })
      })
    })
    timeoutTimer = window.setTimeout(() => {
      // Never measured (e.g. added while the list view hides the canvas):
      // still select it, without the pan.
      newNodeFocusLog({ event: 'new-node-focus:timeout', node_id: nodeId })
      cancel()
      select()
    }, 5000)
  }, [reactFlowStore, rememberNode])

  // Expose focusNode for Playwright/agent-browser tests
  useEffect(() => {
    if (import.meta.env.DEV) {
      window.__dwFocusNode = (
        nodeId: string,
        options?: { maxZoom?: number; yOffset?: number },
      ) => {
        const rf = reactFlowInstance.current
        if (!rf) return 'no rf'
        const allNodes = rf.getNodes()
        const target = allNodes.find((n: { id: string }) => n.id === nodeId)
        if (!target) return 'not found in ' + allNodes.length + ' nodes: ' + allNodes.map((n: { id: string }) => n.id).join(',')
        rf.fitView({
          nodes: [target],
          padding: 0.5,
          duration: 0,
          maxZoom: options?.maxZoom ?? 1,
        })
        if (typeof options?.yOffset === 'number' && options.yOffset !== 0) {
          const viewport = rf.getViewport()
          rf.setViewport({ ...viewport, y: viewport.y + options.yOffset }, { duration: 0 })
        }
        return 'ok'
      };
      // Read a single field off a node's data — used by Playwright tests
      // to verify state without poking React internals.
      window.__dwReadNodeField = (nodeId: string, field: string) => {
        const rf = reactFlowInstance.current
        if (!rf) return undefined
        const target = rf.getNodes().find((n: { id: string }) => n.id === nodeId)
        return dataValue(target?.data, field)
      };
      window.__dwGetCanvasNodes = () => {
        const rf = reactFlowInstance.current
        if (!rf) return []
        return rf.getNodes().map((node) => {
          const childIdsValue = dataValue(node.data, 'in_child_node_ids')
          const childIds = Array.isArray(childIdsValue)
            ? childIdsValue.filter((value): value is string => typeof value === 'string')
            : []
          return {
            id: node.id,
            type: node.type ?? null,
            parentId: typeof node.parentId === 'string' ? node.parentId : null,
            x: node.position.x,
            y: node.position.y,
            childIds,
            selected: Boolean(node.selected),
          }
        })
      };
      window.__dwGetCanvasEdges = () => {
        const rf = reactFlowInstance.current
        if (!rf) return []
        return rf.getEdges().map((edge) => ({
          id: edge.id,
          source: edge.source,
          target: edge.target,
          sourceHandle: edge.sourceHandle ?? null,
          targetHandle: edge.targetHandle ?? null,
        }))
      };
      window.__dwGetSelectedNodeIds = () => Array.from(selectedNodeIdsRef.current);
      // Read/restore the exact viewport (pan + zoom) — used by canvas perf
      // benchmarks to jump the viewport instantly without changing zoom, so
      // offscreen-node reappearance latency can be measured deterministically.
      window.__dwGetViewport = () => {
        const rf = reactFlowInstance.current
        if (!rf) return null
        return rf.getViewport()
      };
      window.__dwSetViewport = (viewport) => {
        const rf = reactFlowInstance.current
        if (!rf) return 'no rf'
        rf.setViewport(viewport, { duration: 0 })
        return 'ok'
      };
      return () => {
        delete window.__dwFocusNode
        delete window.__dwReadNodeField
        delete window.__dwGetCanvasNodes
        delete window.__dwGetCanvasEdges
        delete window.__dwGetSelectedNodeIds
        delete window.__dwGetViewport
        delete window.__dwSetViewport
      }
    }
  }, [setNodes])

  // Stable node list for ChatSidebar @mention autocomplete — only updates when
  // node IDs or labels change, not on every position change during drag.
  const nodeIdentityKey = useMemo(
    () => nodes.map(n => {
      const label = firstStringDataField(n.data, [
        'name',
        'character_name',
        'location_name',
        'in_name',
        'label',
      ]) ?? ''
      return `${n.id}:${label}`
    }).join(','),
    [nodes],
  )
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const canvasNodesStable = useMemo(() => nodes, [nodeIdentityKey])
  const newCanvasSelectableNodes = useMemo(
    () => canvasNodesStable.filter((node) => !isGroupCanvasNode(node)),
    [canvasNodesStable],
  )
  const newCanvasSourceNodes = useMemo(
    () => newCanvasSelectableNodes.map(getNewCanvasSourceNode),
    [newCanvasSelectableNodes],
  )
  const newCanvasSelectedSourceReferences = useMemo<SelectedNodeReference[]>(
    () => newCanvasSourceNodes
      .filter((sourceNode) => newCanvasSourceNodeIds.has(sourceNode.id))
      .map((sourceNode) => ({
        nodeId: sourceNode.id,
        nodeType: sourceNode.type,
        label: sourceNode.label,
      })),
    [newCanvasSourceNodeIds, newCanvasSourceNodes],
  )
  // Other canvases in this project that can be imported FROM (never the active
  // one — copy_nodes_from_canvas rejects same-canvas copies).
  const importSourceCanvases = useMemo(
    () => canvasTabs.filter((canvas) => canvas.id !== activeCanvasId),
    [activeCanvasId, canvasTabs],
  )

  const handleOpenNewCanvasModal = useCallback(() => {
    if (!multiCanvasEnabled) return
    const nextIndex = canvasTabs.length + 1
    const nextTitle = t('workspace.newCanvas.defaultTitle', { number: nextIndex })
    const sourceNodeIds = new Set(newCanvasSourceNodes.map((sourceNode) => sourceNode.id))
    const selectedSourceIds = Array.from(selectedNodeIdsRef.current).filter((nodeId) =>
      sourceNodeIds.has(nodeId),
    )
    setNewCanvasTitle(nextTitle)
    setNewCanvasPrompt('')
    setNewCanvasSourceNodeIds(new Set(selectedSourceIds))
    setCanvasTabsError(null)
    setNewCanvasModalOpen(true)
  }, [canvasTabs.length, multiCanvasEnabled, newCanvasSourceNodes, t])

  const handleSaveNewCanvasSourceNodes = useCallback((references: SelectedNodeReference[]) => {
    setNewCanvasSourceNodeIds(new Set(references.map((reference) => reference.nodeId)))
    setNewCanvasSourceSelectorOpen(false)
  }, [])

  const handleAskDirectorToCreateCanvas = useCallback(() => {
    if (!multiCanvasEnabled) return
    const title = newCanvasTitle.trim()
    if (!title) return

    const selectedSources = newCanvasSourceNodes.filter((sourceNode) =>
      newCanvasSourceNodeIds.has(sourceNode.id),
    )
    const referencedNodeIds = selectedSources.map((sourceNode) => sourceNode.id)
    const message = buildNewCanvasDirectorHandoffMessage(title, newCanvasPrompt, selectedSources)

    setCanvasTabsError(null)
    setNewCanvasModalOpen(false)
    setMobilePane('chat')
    window.dispatchEvent(new CustomEvent('agent-chat:open'))
    window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent('agent-chat:send', {
        detail: {
          message,
          referencedNodeIds,
        },
      }))
    }, 100)
  }, [
    multiCanvasEnabled,
    newCanvasPrompt,
    newCanvasSourceNodeIds,
    newCanvasSourceNodes,
    newCanvasTitle,
  ])

  const handleOpenImportCanvasModal = useCallback(() => {
    if (!multiCanvasEnabled) return
    const candidates = canvasTabs.filter((canvas) => canvas.id !== activeCanvasId)
    if (candidates.length === 0) return
    crossCanvasImportLog({
      event: 'import-modal-opened',
      trace_id: `cross-canvas-import:${projectId ?? 'unknown'}:${activeCanvasId}`,
      project_id: projectId,
      active_canvas_id: activeCanvasId,
      candidate_source_canvas_count: candidates.length,
    })
    setImportSourceCanvasId(candidates[0].id)
    setImportDescription('')
    setCanvasTabsError(null)
    setImportCanvasModalOpen(true)
  }, [activeCanvasId, canvasTabs, multiCanvasEnabled, projectId])

  const handleAskDirectorToImport = useCallback(() => {
    if (!multiCanvasEnabled) return
    const sourceCanvas = canvasTabs.find((canvas) => canvas.id === importSourceCanvasId)
    if (!sourceCanvas || sourceCanvas.id === activeCanvasId) return

    const sourceTitle =
      sourceCanvas.id === DEFAULT_CANVAS_ID && sourceCanvas.title === 'Agent Canvas'
        ? t('workspace.defaultCanvasTitle')
        : sourceCanvas.title
    const message = buildImportCanvasDirectorHandoffMessage(
      sourceTitle,
      sourceCanvas.id,
      importDescription,
    )

    // Emit immediately before the handoff so the FE dispatch correlates with the
    // backend `cross_canvas_node_copy:*` trace via the shared source/target ids.
    crossCanvasImportLog({
      event: 'import-handoff-dispatched',
      trace_id: `cross-canvas-import:${projectId ?? 'unknown'}:${sourceCanvas.id}:${activeCanvasId}`,
      project_id: projectId,
      source_canvas_id: sourceCanvas.id,
      target_canvas_id: activeCanvasId,
      has_description: importDescription.trim().length > 0,
      message_length: message.length,
    })

    setCanvasTabsError(null)
    setImportCanvasModalOpen(false)
    setMobilePane('chat')
    window.dispatchEvent(new CustomEvent('agent-chat:open'))
    // Do NOT pass referencedNodeIds: the nodes live on the SOURCE canvas, but
    // @mention pills resolve against the CURRENT canvas and would dangle. The
    // source identity is encoded in the message body instead.
    window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent('agent-chat:send', {
        detail: {
          message,
        },
      }))
    }, 100)
  }, [
    activeCanvasId,
    canvasTabs,
    importDescription,
    importSourceCanvasId,
    multiCanvasEnabled,
    projectId,
    t,
  ])

  const handleSubmitNewCanvas = useCallback(async () => {
    if (!projectId || creatingCanvas || !multiCanvasEnabled) return

    const title = newCanvasTitle.trim()
    if (!title) return

    const selectedSources = newCanvasSourceNodes.filter((sourceNode) =>
      newCanvasSourceNodeIds.has(sourceNode.id),
    )
    const trimmedPrompt = newCanvasPrompt.trim()
    const initialPromptForDirector = trimmedPrompt || undefined
    const sourceNodeIds = selectedSources.map((sourceNode) => sourceNode.id)

    setCreatingCanvas(true)
    setCanvasTabsError(null)
    try {
      const canvas = await createCanvas(projectId, {
        title,
        prompt: initialPromptForDirector,
        source_node_ids: sourceNodeIds.length > 0 ? sourceNodeIds : undefined,
        // Always send the active canvas as the config-inheritance source (decoupled
        // from node copying) so the new canvas inherits its Style/aspect/etc.
        source_canvas_id: activeCanvasId,
      })
      setCanvasTabs((previous) => normalizeCanvasTabs([...previous, canvas], canvas.id))
      setNewCanvasModalOpen(false)
      navigateToCanvas(
        canvas.id,
        initialPromptForDirector ? { initialPrompt: initialPromptForDirector } : undefined,
      )
    } catch (error) {
      const message = error instanceof Error ? error.message : t('workspace.newCanvas.createError')
      setCanvasTabsError(message)
    } finally {
      setCreatingCanvas(false)
    }
  }, [
    creatingCanvas,
    multiCanvasEnabled,
    navigateToCanvas,
    newCanvasPrompt,
    newCanvasSourceNodeIds,
    newCanvasSourceNodes,
    newCanvasTitle,
    projectId,
    t,
    activeCanvasId,
  ])

  const handleStartRenameCanvas = useCallback((canvas: CanvasTab) => {
    setCanvasTabsError(null)
    renameCancelledRef.current = false
    setEditingCanvasId(canvas.id)
    setEditingCanvasTitle(canvas.title)
  }, [])

  const handleCommitRenameCanvas = useCallback(async (canvasId: string) => {
    if (!projectId || !multiCanvasEnabled || renamingCanvasId) return
    if (renameCommitInFlightRef.current) return
    if (editingCanvasId !== canvasId) return

    const title = editingCanvasTitle.trim()
    if (!title) {
      setCanvasTabsError(t('workspace.newCanvas.renameEmptyError'))
      return
    }

    const current = canvasTabs.find((canvas) => canvas.id === canvasId)
    if (!current || current.title === title) {
      setEditingCanvasId(null)
      setEditingCanvasTitle('')
      return
    }

    renameCommitInFlightRef.current = canvasId
    setRenamingCanvasId(canvasId)
    setCanvasTabsError(null)
    try {
      const renamed = await renameCanvas(projectId, canvasId, title)
      setCanvasTabs((previous) =>
        normalizeCanvasTabs(
          previous.map((canvas) => (
            canvas.id === canvasId
              ? {
                  ...canvas,
                  title: renamed.title,
                  updated_at: renamed.updated_at,
                  created_at: renamed.created_at ?? canvas.created_at,
                  node_count: renamed.node_count,
                }
              : canvas
          )),
          activeCanvasId,
        ),
      )
      setEditingCanvasId(null)
      setEditingCanvasTitle('')
    } catch (error) {
      const message = error instanceof Error ? error.message : t('workspace.newCanvas.renameError')
      setCanvasTabsError(message)
    } finally {
      if (renameCommitInFlightRef.current === canvasId) {
        renameCommitInFlightRef.current = null
      }
      setRenamingCanvasId(null)
    }
  }, [
    activeCanvasId,
    canvasTabs,
    editingCanvasId,
    editingCanvasTitle,
    multiCanvasEnabled,
    projectId,
    renamingCanvasId,
    t,
  ])

  const handleRenameKeyDown = useCallback((event: ReactKeyboardEvent<HTMLInputElement>, canvas: CanvasTab) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      void handleCommitRenameCanvas(canvas.id)
      return
    }
    if (event.key === 'Escape') {
      event.preventDefault()
      renameCancelledRef.current = true
      setEditingCanvasId(null)
      setEditingCanvasTitle('')
    }
  }, [handleCommitRenameCanvas])

  const handleCanvasTabNavigate = useCallback((canvasId: string) => {
    canvasTabNavLog({
      event: 'tab-click',
      trace_id: canvasTabNavTraceId(canvasId),
      projectId,
      from: activeCanvasId,
      to: canvasId,
    })
    if (editingCanvasId) {
      renameCancelledRef.current = true
      setEditingCanvasId(null)
      setEditingCanvasTitle('')
    }
    navigateToCanvas(canvasId)
  }, [activeCanvasId, editingCanvasId, navigateToCanvas, projectId])

  const handleDeleteCanvasTab = useCallback(async (
    canvasId: string,
    traceId: string,
  ): Promise<boolean> => {
    if (
      !projectId ||
      canvasId === DEFAULT_CANVAS_ID ||
      deletingCanvasId ||
      editingCanvasId ||
      renamingCanvasId ||
      !multiCanvasEnabled
    ) return false

    const canvasIndex = canvasTabs.findIndex((canvas) => canvas.id === canvasId)
    if (canvasIndex === -1) return false

    const remainingCanvases = canvasTabs.filter((canvas) => canvas.id !== canvasId)
    const fallbackCanvas =
      remainingCanvases[Math.min(canvasIndex, remainingCanvases.length - 1)] ??
      remainingCanvases[0]

    setDeletingCanvasId(canvasId)
    setCanvasTabsError(null)
    const startedAt = performance.now()
    try {
      await deleteCanvas(projectId, canvasId)
      const nextActiveCanvasId = activeCanvasId === canvasId
        ? fallbackCanvas?.id ?? DEFAULT_CANVAS_ID
        : activeCanvasId
      canvasTabDeleteLog({
        event: 'canvas-delete-succeeded',
        trace_id: traceId,
        project_id: projectId,
        target_canvas_id: canvasId,
        deletedCount: 1,
        duration_ms: Math.round(performance.now() - startedAt),
        delete_scope: 'durable-canvas-and-workspace',
        routePath: `/workspace/${nextActiveCanvasId}`,
      })
      deleteAgentChatClientSnapshot(projectId, canvasId)
      setCanvasTabs(normalizeCanvasTabs(remainingCanvases, nextActiveCanvasId))
      if (activeCanvasId === canvasId) {
        navigateToCanvas(nextActiveCanvasId)
      }
      return true
    } catch (error) {
      const message = error instanceof Error ? error.message : t('workspace.newCanvas.deleteError')
      canvasTabDeleteLog({
        event: 'canvas-delete-failed',
        trace_id: traceId,
        project_id: projectId,
        target_canvas_id: canvasId,
        error_message: message,
      })
      setCanvasTabsError(message)
      return false
    } finally {
      setDeletingCanvasId(null)
    }
  }, [
    activeCanvasId,
    canvasTabs,
    deletingCanvasId,
    editingCanvasId,
    multiCanvasEnabled,
    navigateToCanvas,
    projectId,
    renamingCanvasId,
    t,
  ])

  const pendingDeleteCanvas = pendingCanvasDelete
    ? canvasTabs.find((canvas) => canvas.id === pendingCanvasDelete.canvasId) ?? null
    : null

  useEffect(() => {
    if (pendingCanvasDelete && !pendingDeleteCanvas) {
      canvasTabDeleteLog({
        event: 'canvas-delete-dialog-target-removed',
        trace_id: pendingCanvasDelete.traceId,
        project_id: projectId,
        target_canvas_id: pendingCanvasDelete.canvasId,
      })
      setPendingCanvasDelete(null)
    }
  }, [pendingCanvasDelete, pendingDeleteCanvas, projectId])

  const handleRequestDeleteCanvasTab = useCallback((canvasId: string) => {
    const canvas = canvasTabs.find((candidate) => candidate.id === canvasId)
    if (
      !canvas ||
      canvasId === DEFAULT_CANVAS_ID ||
      deletingCanvasId ||
      editingCanvasId ||
      renamingCanvasId ||
      !multiCanvasEnabled
    ) return

    canvasDeleteAttemptRef.current += 1
    const traceId = `canvas-tab-delete:${projectId ?? 'unknown'}:${canvasId}:attempt-${canvasDeleteAttemptRef.current}`
    canvasTabDeleteLog({
      event: 'canvas-delete-dialog-opened',
      trace_id: traceId,
      project_id: projectId,
      target_canvas_id: canvasId,
      displayText: canvas.title,
    })
    setCanvasTabsError(null)
    setPendingCanvasDelete({ canvasId, traceId })
  }, [canvasTabs, deletingCanvasId, editingCanvasId, multiCanvasEnabled, projectId, renamingCanvasId])

  const handleCancelDeleteCanvasTab = useCallback(() => {
    if (pendingCanvasDelete) {
      canvasTabDeleteLog({
        event: 'canvas-delete-dialog-cancelled',
        trace_id: pendingCanvasDelete.traceId,
        project_id: projectId,
        target_canvas_id: pendingCanvasDelete.canvasId,
      })
    }
    setPendingCanvasDelete(null)
  }, [pendingCanvasDelete, projectId])

  const handleConfirmDeleteCanvasTab = useCallback(() => {
    if (!pendingCanvasDelete) return
    const { canvasId, traceId } = pendingCanvasDelete
    canvasTabDeleteLog({
      event: 'canvas-delete-confirmation-accepted',
      trace_id: traceId,
      project_id: projectId,
      target_canvas_id: canvasId,
    })
    void handleDeleteCanvasTab(canvasId, traceId).then((deleted) => {
      if (deleted) setPendingCanvasDelete(null)
    })
  }, [handleDeleteCanvasTab, pendingCanvasDelete, projectId])

  const handleReplayCreateCanvas = useCallback(async (title: string) => {
    if (!projectId) throw new Error('projectId is required to create a replay canvas')
    if (!multiCanvasEnabled) throw new Error('Multi-canvas is not enabled for this user')
    const canvas = await createCanvas(projectId, { title, source_canvas_id: activeCanvasId })
    setCanvasTabs((previous) => normalizeCanvasTabs([...previous, canvas], activeCanvasId))
    return { id: canvas.id, title: canvas.title }
  }, [activeCanvasId, multiCanvasEnabled, projectId])

  const handleDirectorCanvasCreated = useCallback((canvas: CanvasSummary) => {
    setCanvasTabs((previous) => {
      const wasKnown = previous.some((existing) => existing.id === canvas.id)
      const next = normalizeCanvasTabs(
        wasKnown
          ? previous.map((existing) => existing.id === canvas.id ? canvas : existing)
          : [...previous, canvas],
        activeCanvasId,
      )
      canvasTabSyncLog({
        event: 'director-event',
        canvasId: canvas.id,
        wasKnown,
        prevCount: previous.length,
        nextCount: next.length,
      })
      return next
    })
    // Belt-and-suspenders: even though the event payload just gave us the new
    // tab, refetch to guard against payload drift (e.g. new fields added
    // server-side without a matching client parse) and to reconcile any
    // concurrent server-side deletes/renames we might have missed. Dedupe in
    // refreshCanvasTabs coalesces this with any near-simultaneous ws-connected
    // refresh.
    void refreshCanvasTabs({ reason: 'director-event' })
  }, [activeCanvasId, refreshCanvasTabs])

  const handleChatSocketConnectedChange = useCallback(
    (connected: boolean) => {
      canvasTabSyncLog({
        event: 'ws-connected-change',
        connected,
        activeCanvasId,
      })
      if (!connected) return
      // The chat WebSocket just came up (initial connect OR reconnect).
      // `canvas_created` broadcasts fired while the socket was down are lost —
      // the backend does not queue chat events for late subscribers. Refetch
      // the canvas list from REST so any newly-created (or deleted) canvases
      // land in the tab bar without waiting for a page reload.
      void refreshCanvasTabs({ reason: 'ws-connected' })
    },
    [activeCanvasId, refreshCanvasTabs],
  )

  const handleReplayDeleteCanvas = useCallback(async (canvasId: string) => {
    if (!projectId) throw new Error('projectId is required to delete a replay canvas')
    if (!multiCanvasEnabled) throw new Error('Multi-canvas is not enabled for this user')
    if (canvasId === DEFAULT_CANVAS_ID) throw new Error('Cannot delete the agent canvas')

    await deleteCanvas(projectId, canvasId)
    deleteAgentChatClientSnapshot(projectId, canvasId)
    const nextActiveCanvasId = activeCanvasId === canvasId ? DEFAULT_CANVAS_ID : activeCanvasId
    setCanvasTabs((previous) =>
      normalizeCanvasTabs(
        previous.filter((canvas) => canvas.id !== canvasId),
        nextActiveCanvasId,
      ),
    )
    if (activeCanvasId === canvasId) {
      navigateToCanvas(nextActiveCanvasId)
    }
  }, [activeCanvasId, multiCanvasEnabled, navigateToCanvas, projectId])

  // ReactFlow instance
  const reactFlowInstance = useRef<ReactFlowInstance<ReactFlowCanvasNodeData, ReactFlowCanvasEdgeData> | null>(null)
  const [rfInstanceReady, setRfInstanceReady] = useState(false)
  const onRfInstanceReady = useCallback(() => setRfInstanceReady(true), [])

  // ─── Yjs Integration ─────────────────────────────────────────────────────

  const { isAuthenticated, user } = useAuth()

  const {
    ydoc,
    roomName: ydocRoomKey,
    synced: yjsSynced,
    connected: yjsConnected,
    error: yjsError,
    roomEpoch,
  } = useYDoc({
    projectId: projectId || '',
    canvasId: activeCanvasId,
    enabled: !!projectId && (isAuthenticated || IS_LOCAL_DEV),
  })
  const isBackendConnected = yjsConnected && yjsSynced

  useNodeActionAnalytics(ydoc, projectId)
  // Expose ydoc and Y for E2E test access (dev only)
  useEffect(() => {
    if (import.meta.env.DEV && ydoc) {
      window.__ydoc = ydoc
      window.__Y = Y
    }
    return () => {
      if (import.meta.env.DEV) {
        delete window.__ydoc
        delete window.__Y
      }
    }
  }, [ydoc])

  // Undo/Redo state (declared before useYCanvas since it needs isUndoRedo).
  //
  // History is BUCKETED per canvas room key, not global (T289182224). #3687
  // wiped a single global stack on every canvas switch to stop one Cmd+Z from
  // replaying canvas A's graph onto canvas B (T288688966); that made undo a
  // silent no-op after any tab switch. `createUndoHistory` parks each room's
  // history instead of dropping it, and refuses any entry whose recorded room
  // disagrees with the room the caller is on — see `workspaceUndoHistory.ts`.
  // Lazy `useState` initialiser so the store is built exactly once per mount
  // and keeps a stable identity in the callback dependency arrays below.
  const [undoHistory] = useState(() =>
    createUndoHistory<WorkspaceNode, WorkspaceEdge>(undoScopeLog),
  )
  const isUndoRedo = useRef(false)

  useEffect(() => {
    if (!import.meta.env.DEV) return
    // A read-only projection, not the store itself: assigning `undoHistory`
    // would put `switchToRoom`, `record`, `commit` and `noteLiveFingerprint` on
    // `window`, where devtools or a stray test could quietly clear or move a
    // user's history.
    window.__dwUndoHistory = {
      activeRoomKey: () => undoHistory.activeRoomKey(),
      depth: (roomKey?: string) => undoHistory.depth(roomKey),
      trackedRoomKeys: () => undoHistory.trackedRoomKeys(),
    }
    return () => {
      delete window.__dwUndoHistory
    }
  }, [undoHistory])

  /**
   * Digest of the canvas currently in the ReactFlow refs, in the shape the undo
   * history compares (see `fingerprintGraph`).
   *
   * The projection lives here rather than in the store because `n.data` is a
   * union over every generated node type; `dataValue` is the typed reader for
   * it, and keeping the reach-in on this side lets the store stay free of
   * ReactFlow types and independently testable.
   */
  const fingerprintLiveGraph = useCallback(
    () =>
      fingerprintGraph(
        nodesRef.current.map((n) => {
          const children = dataValue(n.data, 'in_child_node_ids')
          return {
            id: n.id,
            position: n.position,
            parentId: n.parentId ?? null,
            childNodeIds: Array.isArray(children) ? children.map(String) : null,
          }
        }),
        edgesRef.current,
      ),
    [],
  )
  /** Room the undo store was last pointed at, so a switch can park the one it leaves. */
  const activeUndoRoomKeyRef = useRef('')
  /** `${roomKey}#${epoch}` whose changed-while-parked check has already run. */
  const undoRestoreCheckedRef = useRef('')
  /** Room whose graph is in `nodesRef`/`edgesRef` — i.e. the last one rendered. */
  const refsRoomKeyRef = useRef('')

  // Replay suppression.
  //
  // `isUndoRedo` stops a replay's own YDoc writes from being recorded as fresh
  // user actions. It used to be cleared by a bare `setTimeout(..., 500)`, which
  // failed two ways: a replay that threw never scheduled the timer at all and
  // left recording dead for the session, and a timer armed by one replay could
  // fire after a later one started and clear ITS suppression early — including
  // across a canvas switch. A generation counter fixes both: only the timeout
  // that still owns the current generation may clear the flag, and every
  // replay, every switch and every failure cancels the pending one first.
  const replaySuppressionRef = useRef<{
    generation: number
    timeout: ReturnType<typeof setTimeout> | null
  }>({ generation: 0, timeout: null })

  const endReplaySuppression = useCallback(() => {
    const state = replaySuppressionRef.current
    state.generation += 1
    if (state.timeout !== null) {
      clearTimeout(state.timeout)
      state.timeout = null
    }
    isUndoRedo.current = false
  }, [])

  /** Suppress recording for the usual settling window, owned by this replay. */
  const beginReplaySuppression = useCallback(() => {
    const state = replaySuppressionRef.current
    state.generation += 1
    const generation = state.generation
    if (state.timeout !== null) clearTimeout(state.timeout)
    isUndoRedo.current = true
    state.timeout = setTimeout(() => {
      // A newer replay (or a canvas switch) has taken ownership since — its
      // suppression is still meant to be running, so leave the flag alone.
      if (replaySuppressionRef.current.generation !== generation) return
      replaySuppressionRef.current.timeout = null
      isUndoRedo.current = false
    }, 500)
  }, [])

  useEffect(() => endReplaySuppression, [endReplaySuppression])

  // Timestamp (ms epoch) of the user's most recent canvas interaction. Read by
  // useYCanvas's focus observers to suppress AUTOMATIC auto-focus while the user
  // is actively interacting (RFC: suppress auto-focus during canvas
  // interaction). Scoped per workspace/canvas — reset on canvasRoomKey change so
  // it never leaks across project switches. Declared before useYCanvas.
  const lastInteractionTsRef = useRef(0)

  const {
    ready: canvasReady,
    ydocRunningCount,
    ydocQueuedCount,
    ydocDirectorRunning,
    ydocStatusLive,
    writeNodePosition,
    addNodeToYDoc,
    removeNodeFromYDoc,
    writeNodeData,
    flushNodeInputFromEdgeRefs,
    writeNodeParent,
    addEdgeToYDoc,
    removeEdgeFromYDoc,
    removeEdgesFromYDocByPorts,
  } = useYCanvas({
    ydoc,
    synced: yjsSynced,
    setNodes,
    setEdges,
    nodesRef,
    edgesRef,
    isBackendConnected,
    isUndoRedo,
    reactFlowInstance,
    selectedNodeIdsRef,
    projectId,
    canvasId: activeCanvasId,
    lastInteractionTsRef,
  })

  const taskCompletionChimeScopeRef = useRef<string | null>(null)
  const taskCompletionPreviousSnapshotRef = useRef<TaskCompletionSnapshot | null>(null)
  const taskCompletionRunRef = useRef<{
    active: boolean
    completedNodeIds: Set<string>
  }>({ active: false, completedNodeIds: new Set() })
  const taskCompletionPreviousDataRefsRef = useRef<Map<string, unknown>>(new Map())
  const taskCompletionPreviousQueuedCountRef = useRef<number | null>(null)

  useEffect(() => {
    const scope = `${projectId ?? 'unknown'}:${activeCanvasId}`
    const traceId = `task-completion-chime:${projectId ?? 'unknown'}:${activeCanvasId}`
    let scopeChanged = false
    if (taskCompletionChimeScopeRef.current !== scope) {
      taskCompletionChimeScopeRef.current = scope
      taskCompletionPreviousSnapshotRef.current = null
      taskCompletionRunRef.current = { active: false, completedNodeIds: new Set() }
      taskCompletionPreviousDataRefsRef.current = new Map()
      taskCompletionPreviousQueuedCountRef.current = null
      scopeChanged = true
    }

    const dataRefsChanged = haveNodeDataRefsChanged(nodes, taskCompletionPreviousDataRefsRef.current)
    const queuedCountChanged = taskCompletionPreviousQueuedCountRef.current !== ydocQueuedCount
    taskCompletionPreviousDataRefsRef.current = nodeDataRefMap(nodes)
    taskCompletionPreviousQueuedCountRef.current = ydocQueuedCount
    if (
      !scopeChanged &&
      !dataRefsChanged &&
      !queuedCountChanged &&
      taskCompletionPreviousSnapshotRef.current
    ) {
      return
    }

    const snapshot = createTaskCompletionSnapshot(
      nodes.map((node) => ({
        id: node.id,
        type: node.type ?? null,
        status: stringDataField(node.data, 'status'),
      })),
      ydocQueuedCount,
    )
    const previous = taskCompletionPreviousSnapshotRef.current
    const run = taskCompletionRunRef.current

    if (!previous) {
      if (snapshot.activeWorkCount > 0) {
        run.active = true
        run.completedNodeIds.clear()
        taskCompletionChimeLog({
          event: 'task-completion-chime:tracked_work_started',
          trace_id: traceId,
          project_id: projectId ?? null,
          canvas_id: activeCanvasId,
          trigger: 'baseline_active',
          active_node_ids_csv: snapshot.activeNodeIds.join(','),
          active_node_count: snapshot.activeNodeIds.length,
          queued_node_count: snapshot.queuedCount,
        })
      }
      taskCompletionPreviousSnapshotRef.current = snapshot
      return
    }

    const wasWorkActive = previous.activeWorkCount > 0
    const isWorkActive = snapshot.activeWorkCount > 0

    if (!run.active && !wasWorkActive && isWorkActive) {
      run.active = true
      run.completedNodeIds.clear()
      taskCompletionChimeLog({
        event: 'task-completion-chime:tracked_work_started',
        trace_id: traceId,
        project_id: projectId ?? null,
        canvas_id: activeCanvasId,
        trigger: 'status_transition',
        active_node_ids_csv: snapshot.activeNodeIds.join(','),
        active_node_count: snapshot.activeNodeIds.length,
        queued_node_count: snapshot.queuedCount,
      })
    }

    const completedNow = completedNodeIdsFromPreviousActive(previous, snapshot)
    if (run.active && completedNow.length > 0) {
      for (const nodeId of completedNow) {
        run.completedNodeIds.add(nodeId)
        taskCompletionChimeLog({
          event: 'task-completion-chime:tracked_node_completed',
          trace_id: traceId,
          project_id: projectId ?? null,
          canvas_id: activeCanvasId,
          node_id: nodeId,
          node_type: snapshot.nodeTypeById[nodeId] ?? null,
          status_from: previous.nodeStatusById[nodeId] ?? null,
          status_to: snapshot.nodeStatusById[nodeId] ?? null,
          completed_node_count_since_start: run.completedNodeIds.size,
          active_node_count_after: snapshot.activeNodeIds.length,
          queued_node_count_after: snapshot.queuedCount,
        })
      }
    }

    if (run.active && wasWorkActive && !isWorkActive) {
      const completedNodeIds = Array.from(run.completedNodeIds)
      taskCompletionChimeLog({
        event: 'task-completion-chime:all_tracked_work_completed',
        trace_id: traceId,
        project_id: projectId ?? null,
        canvas_id: activeCanvasId,
        completed_node_ids_csv: completedNodeIds.join(','),
        completed_node_count: completedNodeIds.length,
        active_node_count_after: snapshot.activeNodeIds.length,
        queued_node_count_after: snapshot.queuedCount,
        chime_enabled: taskCompletionChimeEnabled,
        page_visibility: typeof document === 'undefined' ? 'unknown' : document.visibilityState,
      })

      if (completedNodeIds.length === 0) {
        taskCompletionChimeLog({
          event: 'task-completion-chime:playback_skipped_no_completed_nodes',
          trace_id: traceId,
          project_id: projectId ?? null,
          canvas_id: activeCanvasId,
        })
      } else if (!taskCompletionChimeEnabled) {
        taskCompletionChimeLog({
          event: 'task-completion-chime:playback_skipped_disabled',
          trace_id: traceId,
          project_id: projectId ?? null,
          canvas_id: activeCanvasId,
          completed_node_count: completedNodeIds.length,
        })
      } else {
        taskCompletionChimeLog({
          event: 'task-completion-chime:playback_requested',
          trace_id: traceId,
          project_id: projectId ?? null,
          canvas_id: activeCanvasId,
          completed_node_count: completedNodeIds.length,
        })
        void playTaskCompletionChime().then((result) => {
          const playbackLog: {
            event: string
            trace_id: string
            project_id: string | null
            canvas_id: string
            playback_result: string
            failure_reason?: string
          } = {
            event: result.status === 'played'
              ? 'task-completion-chime:playback_succeeded'
              : 'task-completion-chime:playback_failed',
            trace_id: traceId,
            project_id: projectId ?? null,
            canvas_id: activeCanvasId,
            playback_result: result.status,
          }
          if (result.reason) playbackLog.failure_reason = result.reason
          taskCompletionChimeLog(playbackLog)
        })
      }

      run.active = false
      run.completedNodeIds.clear()
    } else if (!isWorkActive) {
      run.active = false
      run.completedNodeIds.clear()
    }

    taskCompletionPreviousSnapshotRef.current = snapshot
  }, [activeCanvasId, nodes, projectId, taskCompletionChimeEnabled, ydocQueuedCount])

  useEffect(() => {
    if (!import.meta.env.DEV) return
    window.__dwSetNodeField = (nodeId: string, field: string, value: ReactFlowFieldValue) => {
      const existingNode = nodesRef.current.find((n) => n.id === nodeId)
      if (!existingNode) return 'not found'
      if (!isFeNodeType(existingNode.type)) return `unsupported type ${String(existingNode.type)}`
      const updates = { [field]: value } as NodeDataRecord<typeof existingNode.type>
      setNodes((nds) => {
        const updated = nds.map((n) =>
          n.id === nodeId ? { ...n, data: { ...n.data, ...updates } } : n
        )
        nodesRef.current = updated
        return updated
      })
      writeNodeData({ id: nodeId, type: existingNode.type }, updates)
      return 'ok'
    }
    return () => {
      delete window.__dwSetNodeField
    }
  }, [setNodes, writeNodeData])

  const viewportStorageKey = useMemo(
    () => projectId ? `dreamwright:workspace-viewport:v1:${projectId}:${activeCanvasId}` : null,
    [activeCanvasId, projectId],
  )
  const { viewport: savedViewport, writeViewport } = useYViewport(viewportStorageKey, yjsSynced)

  const flushResolvedPortData = useCallback(
    (targetId: string, targetHandle: string | undefined, edgeRefs: PortEdgeRef[]) => {
      if (!targetHandle) return

      const targetNode = nodesRef.current.find((node) => node.id === targetId)
      if (!targetNode) return

      const flushResult = (() => {
        try {
          return flushNodeInputFromEdgeRefs(
            targetId,
            targetHandle,
            nodesRef.current,
            edgeRefs,
          )
        } catch (error) {
          const message = error instanceof Error ? error.message : String(error)
          console.error('[WorkspaceV2] Failed to flush edge-resolved input', {
            targetId,
            targetHandle,
            edgeRefs,
            error: message,
          })
          refSyncLog({
            event: 'port-flush-invalid-edge',
            trace_id: referenceSyncTraceId(targetId),
            projectId,
            targetId,
            targetType: targetNode?.type,
            targetHandle,
            edgeCount: edgeRefs.length,
            edgeRefs: summarizeEdgeRefs(edgeRefs, targetId, targetHandle),
            error: message,
          })
          return undefined
        }
      })()
      if (!flushResult) return

      const previousValue = dataValue(targetNode.data, flushResult.field)
      const resolvedValue = flushResult.value
      const previousSummary = summarizePortValue(previousValue)
      const resolvedSummary = summarizePortValue(resolvedValue)
      const changed = previousSummary !== resolvedSummary
      refSyncLog({
        event: changed ? 'port-flush-applied' : 'port-flush-noop',
        trace_id: referenceSyncTraceId(targetId),
        projectId,
        targetId,
        targetType: targetNode.type,
        targetHandle,
        edgeCount: edgeRefs.length,
        edgeRefs: summarizeEdgeRefs(edgeRefs, targetId, targetHandle),
        previousValueSummary: previousSummary,
        resolvedValueSummary: resolvedSummary,
        flushResult: changed ? 'applied' : 'noop',
        changed,
      })
      const updates: Record<string, ReactFlowFieldValue> = { [flushResult.field]: resolvedValue }

      setNodes((prevNodes) => {
        const updatedNodes = prevNodes.map((node) =>
          node.id === targetId
            ? { ...node, data: { ...node.data, ...updates } }
            : node,
        )
        nodesRef.current = updatedNodes
        return updatedNodes
      })
    },
    [flushNodeInputFromEdgeRefs, projectId, setNodes],
  )

  useEffect(() => {
    const dataRefsChanged = haveNodeDataRefsChanged(nodes, previousOutputSyncDataRefsRef.current)
    const previousEdges = previousOutputSyncEdgesRef.current
    const edgesChanged = previousEdges !== edges
    previousOutputSyncDataRefsRef.current = nodeDataRefMap(nodes)
    previousOutputSyncEdgesRef.current = edges
    if (!dataRefsChanged && !edgesChanged) return

    const previousSnapshots = previousOutputSnapshotsRef.current
    const nextSnapshots = new Map<string, Map<string, string>>()
    const changedOutputHandlesByNode = new Map<string, Set<string>>()

    for (const node of nodes) {
      const outputSnapshot = new Map<string, string>()
      for (const [key, value] of Object.entries(node.data ?? {})) {
        if (key.startsWith('out_')) {
          outputSnapshot.set(key, summarizePortValue(value))
        }
      }

      const previousSnapshot = previousSnapshots.get(node.id)
      if (previousSnapshot) {
        const changedHandles = new Set<string>()
        for (const [key, value] of outputSnapshot) {
          if (previousSnapshot.get(key) !== value) {
            changedHandles.add(key)
          }
        }
        for (const key of previousSnapshot.keys()) {
          if (!outputSnapshot.has(key)) {
            changedHandles.add(key)
          }
        }
        if (changedHandles.size > 0) {
          changedOutputHandlesByNode.set(node.id, changedHandles)
        }
      } else if (outputSnapshot.size > 0) {
        changedOutputHandlesByNode.set(node.id, new Set(outputSnapshot.keys()))
      }

      nextSnapshots.set(node.id, outputSnapshot)
    }

    previousOutputSnapshotsRef.current = nextSnapshots

    const affectedPorts: TargetPortRef[] = []
    if (changedOutputHandlesByNode.size > 0) {
      for (const edge of edges) {
        const changedHandles = changedOutputHandlesByNode.get(edge.source)
        const sourceHandle = edge.sourceHandle ?? undefined
        if (sourceHandle && changedHandles?.has(sourceHandle)) {
          rememberTargetPort(affectedPorts, edge.target, edge.targetHandle ?? undefined)
        }
      }
    }
    if (edgesChanged) {
      for (const edge of edges) {
        const sourceHandle = edge.sourceHandle ?? undefined
        if (!sourceHandle) continue
        const sourceSnapshot = nextSnapshots.get(edge.source)
        if (sourceSnapshot?.has(sourceHandle)) {
          rememberTargetPort(affectedPorts, edge.target, edge.targetHandle ?? undefined)
        }
      }
    }

    if (affectedPorts.length === 0) return

    refSyncLog({
      event: 'source-output-port-flush',
      sourceCount: changedOutputHandlesByNode.size,
      affectedPortCount: affectedPorts.length,
      affectedPorts: affectedPorts
        .map((port) => `${port.target}.${port.targetHandle ?? 'unknown'}`)
        .join('|'),
    })

    for (const port of affectedPorts) {
      flushResolvedPortData(
        port.target,
        port.targetHandle,
        portEdgeRefsForTarget(edges, port.target, port.targetHandle),
      )
    }
  }, [edges, flushResolvedPortData, nodes])

  // Execution plan from YDoc — independent of chat messages
  const { batches: executionPlanBatches, confirmBatch: rawConfirmBatch, cancelBatch } = useExecutionPlan(
    ydoc,
    projectId || '',
    activeCanvasId,
    project?.can_view_credits === true,
    user?.id,
  )
  const showInsufficientSparks = useShowInsufficientSparks()
  const handleConfirmBatch = useCallback(
    async (quotes: ApprovalRef[]) => {
      try {
        await rawConfirmBatch(quotes)
      } catch (err) {
        if (err instanceof InsufficientSparksError) {
          showInsufficientSparks({})
          throw err
        }
        if (err && typeof err === 'object' && isInsufficientSparksPayload(err)) {
          showInsufficientSparks({})
          throw err
        }
        throw err
      }
    },
    [rawConfirmBatch, showInsufficientSparks],
  )

  // ─── Viewport Management ─────────────────────────────────────────────────

  // Cancel a pending new-node focus on canvas switch (and on unmount). The
  // workspace stays mounted across canvas tabs, so without this the 5s
  // timeout would select a node id that is not on the new canvas.
  useEffect(() => () => pendingNewNodeFocusRef.current?.(), [canvasRoomKey])
  const [canvasVisible, setCanvasVisible] = useState(false)
  const [renderedCanvasRoomKey, setRenderedCanvasRoomKey] = useState('')
  const canvasSurfaceRef = useRef<HTMLDivElement>(null)
  const savedViewportRestoredRef = useRef(false)
  // Multi-canvas keep-alive: rooms fully rendered at least once this session.
  // Switching BACK to such a room reuses a warm, already-synced Y.Doc from
  // canvasRoomPool, so the CanvasLoadOverlay must NOT flash during the brief
  // useYCanvas rebuild. First-time (cold) loads — and LRU/idle-evicted rooms
  // being cold-reloaded — are still gated by yjsSynced, so they show the overlay.
  const renderedRoomsRef = useRef<Set<string>>(new Set())
  const ydocMatchesActiveCanvas = ydocRoomKey === canvasRoomKey
  const activeCanvasRendered =
    renderedCanvasRoomKey === canvasRoomKey && ydocMatchesActiveCanvas && canvasReady
  // A warm switch-back: the target room's Y.Doc is already synced and this room
  // was rendered before, so suppress the overlay through the rebuild frames.
  const warmSwitchReady =
    ydocMatchesActiveCanvas && yjsSynced && renderedRoomsRef.current.has(canvasRoomKey)
  // The per-canvas project config (style / resolution / aspect ratio stamped
  // onto new nodes) no longer blocks the whole page after the first render — see
  // `configBlocksPage` — so it gates the canvas surface here instead. Same
  // semantics as the old page gate: a config ERROR does not block (it never did).
  const canvasConfigReady = !projectConfigLoading
  const canvasInteractive =
    canvasVisible &&
    activeCanvasRendered &&
    yjsConnected &&
    yjsSynced &&
    yjsError == null &&
    canvasConfigReady
  const canvasLoadOverlayVisible =
    yjsError != null || (!activeCanvasRendered && !warmSwitchReady) || !canvasConfigReady
  useEffect(() => {
    canvasTabNavLog({
      event: 'canvas-surface-state',
      trace_id: canvasTabNavTraceId(activeCanvasId),
      projectId,
      canvasId: activeCanvasId,
      interactive: canvasInteractive,
      overlay: canvasLoadOverlayVisible,
      waitingFor: [
        !canvasVisible && 'hidden',
        !activeCanvasRendered && 'render',
        !(yjsConnected && yjsSynced) && 'yjs-sync',
        yjsError != null && 'yjs-error',
        !canvasConfigReady && 'canvas-config',
      ].filter(Boolean).join(',') || 'none',
    })
  }, [activeCanvasId, activeCanvasRendered, canvasConfigReady, canvasInteractive, canvasLoadOverlayVisible, canvasVisible, projectId, yjsConnected, yjsError, yjsSynced])

  // Record a room as "rendered" once it has fully rendered, so a later
  // switch-back is recognised as a warm switch (see warmSwitchReady).
  useEffect(() => {
    if (activeCanvasRendered) {
      renderedRoomsRef.current.add(canvasRoomKey)
    }
  }, [activeCanvasRendered, canvasRoomKey])

  // ─── App-health: canvas hydration latch + canvas_load_finished (§5.4/§5.5) ──
  //
  // `canvasHydrated` = "the CURRENT canvas has first become interactive". Derived
  // SYNCHRONOUSLY from a key held in state, so on the render where canvasRoomKey
  // changes it is immediately false (the stored key is still the previous room) —
  // no one-render staleness that would misclassify a new-room asset's trigger.
  const [hydratedKey, setHydratedKey] = useState<string | null>(null)
  useEffect(() => {
    if (canvasInteractive && hydratedKey !== canvasRoomKey) setHydratedKey(canvasRoomKey)
  }, [canvasInteractive, canvasRoomKey, hydratedKey])
  const canvasHydrated = hydratedKey === canvasRoomKey

  // `canvas_load_finished`: one terminal (ready/error/timeout@30s) per canvas
  // load attempt, keyed by canvasRoomKey. A single effect. `ready` uses
  // `canvasInteractive` (inherently current-room-safe — it requires the CURRENT
  // room to be rendered), so even a warm/instant switch-back emits immediately and
  // never falls to a false timeout. `error` is accepted only when `yjsError` is
  // CORRELATED to the current room (`ydocMatchesActiveCanvas`), so a one-render
  // stale error from the previous room can't mislabel the new room, and a genuine
  // immediate error is still caught (its render has the ydoc matched).
  const canvasLoadAttemptRef = useRef<{
    key: string
    startedAt: number
    emitted: boolean
  } | null>(null)
  const canvasLoadTimerRef = useRef<number | null>(null)
  useEffect(() => {
    const clearCanvasLoadTimer = () => {
      if (canvasLoadTimerRef.current !== null) {
        window.clearTimeout(canvasLoadTimerRef.current)
        canvasLoadTimerRef.current = null
      }
    }
    // Need a real project + canvas identity to emit (§5.4 both Req). If identity
    // disappears mid-attempt (teardown), cancel the in-flight 30s timer and drop
    // the attempt so a stale timeout can't later fire against unrelated state.
    if (!projectId || !activeCanvasId) {
      clearCanvasLoadTimer()
      canvasLoadAttemptRef.current = null
      return
    }
    if (
      !canvasLoadAttemptRef.current ||
      canvasLoadAttemptRef.current.key !== canvasRoomKey
    ) {
      // New attempt for this canvas — reset + start the 30s timeout.
      clearCanvasLoadTimer()
      canvasLoadAttemptRef.current = {
        key: canvasRoomKey,
        startedAt: Date.now(),
        emitted: false,
      }
      const keyForTimer = canvasRoomKey
      canvasLoadTimerRef.current = window.setTimeout(() => {
        const a = canvasLoadAttemptRef.current
        if (a && a.key === keyForTimer && !a.emitted) {
          a.emitted = true
          canvasLoadTimerRef.current = null
          trackCanvasLoadFinished({
            project_id: projectId,
            canvas_id: activeCanvasId,
            duration_ms: 30000,
            outcome: 'timeout',
            node_count: nodesRef.current.length,
          })
        }
      }, 30000)
    }
    const attempt = canvasLoadAttemptRef.current
    if (attempt.emitted) return
    let outcome: 'ready' | 'error' | null = null
    if (canvasInteractive) outcome = 'ready'
    else if (yjsError != null && ydocMatchesActiveCanvas) outcome = 'error'
    if (outcome) {
      attempt.emitted = true
      clearCanvasLoadTimer()
      trackCanvasLoadFinished({
        project_id: projectId,
        canvas_id: activeCanvasId,
        duration_ms: Math.max(0, Math.round(Date.now() - attempt.startedAt)),
        outcome,
        node_count: nodesRef.current.length,
      })
    }
  }, [
    canvasRoomKey,
    canvasInteractive,
    yjsError,
    ydocMatchesActiveCanvas,
    projectId,
    activeCanvasId,
  ])

  useEffect(() => {
    return () => {
      if (canvasLoadTimerRef.current !== null) {
        window.clearTimeout(canvasLoadTimerRef.current)
        canvasLoadTimerRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    if (!isMobileViewport) return

    workspaceMobileLog({
      event: 'mobile-workspace-visibility-state',
      feature: 'mobile-ui-loading',
      trace_id: `mobile-workspace-visibility:${projectId}:${activeCanvasId}`,
      layout_mode: 'mobile',
      active_pane: mobilePane,
      canvas_visibility: mobilePane === 'chat' ? 'hidden' : 'visible',
      canvas_interactivity: mobilePane === 'chat' ? 'disabled' : 'enabled',
      project_id: projectId,
      canvas_id: activeCanvasId,
    })

    const frame = window.requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'))
    })
    const timeout = window.setTimeout(() => {
      window.dispatchEvent(new Event('resize'))
    }, 180)

    return () => {
      window.cancelAnimationFrame(frame)
      window.clearTimeout(timeout)
    }
  }, [activeCanvasId, isMobileViewport, mobilePane, projectId])

  useEffect(() => {
    savedViewportRestoredRef.current = false
    // Reset the interaction cooldown when switching projects/canvases so a click
    // in the previous workspace can't suppress auto-focus in the new one.
    lastInteractionTsRef.current = 0
  }, [canvasRoomKey])

  // Undo/redo history is per-canvas, and this component stays MOUNTED across
  // canvas tab switches (the route `/project/:projectId/workspace/:canvasId`
  // carries no `key`), so the store survives the switch. Point it at the new
  // room's bucket: the canvas being left keeps its history parked, and a canvas
  // being returned to gets its own history back (T289182224).
  //
  // A snapshot is a flat list of ReactFlow nodes/edges, and handleUndo feeds it
  // to syncStateDiffToBackend, which deletes every node on the CURRENT canvas
  // that is missing from the snapshot. Replaying one canvas's snapshot onto
  // another is therefore destructive — confirmed in prod-meta on 2026-09-15
  // (affordable-fashion-finds-a547856b/canvas-3ff59750 lost 9 nodes and gained
  // 66 from a sibling canvas in one flush, T288688966). What prevents that now
  // is the per-entry room stamp and room epoch checked in `peekUndo`/`peekRedo`
  // and again in handleUndo/handleRedo, NOT the wipe #3687 used to do here.
  //
  // The outgoing room's graph is fingerprinted HERE, before the switch, because
  // this effect still sees the canvas being left in `nodesRef`/`edgesRef` — the
  // room acquisition in `useYDoc` and the rebuild in `useYCanvas` are both
  // passive and have not run yet. That digest is what the restore-side check
  // below compares against, so a canvas that changed while the user was away
  // cannot have a stale snapshot replayed onto it.
  //
  // LAYOUT effect, deliberately. `main.tsx` enables react-router's
  // `v7_startTransition`, so navigation re-renders inside a transition and a
  // PASSIVE effect can trail the paint. The window `keydown` listener stays
  // live throughout and calls handleUndo synchronously, so a Cmd+Z landing in
  // that window would read the store while it still points at the old room.
  // Switching a pointer before paint costs nothing and removes the need to
  // reason about transition scheduling. (If it ever does trail, the room stamp
  // catches it: the popped entry is refused rather than replayed.) Kept
  // separate from the passive effect above so the viewport/interaction resets
  // keep their existing timing.
  useLayoutEffect(() => {
    // Fingerprint the room whose graph is ACTUALLY in the refs, which is the
    // last room that became interactive — not simply "the room we were last
    // pointed at". Switching A -> B -> C before B ever renders leaves the refs
    // holding A the whole way, so crediting B with that digest would store A's
    // graph under B and wipe B's history on return. Re-noting A is harmless:
    // its graph has not changed since it was last validated.
    const refsRoom = refsRoomKeyRef.current
    if (refsRoom && refsRoom !== canvasRoomKey) {
      undoHistory.noteLiveFingerprint(refsRoom, fingerprintLiveGraph())
    }
    if (activeUndoRoomKeyRef.current !== canvasRoomKey) {
      // Leaving a room invalidates its restore check: while we are away it can
      // change under us, and the latch would otherwise let A -> B -> A skip
      // revalidation entirely and replay history against a canvas that moved.
      undoRestoreCheckedRef.current = ''
    }
    activeUndoRoomKeyRef.current = canvasRoomKey
    // A replay in flight belongs to the canvas being left. Carrying its
    // suppression across the switch stopped the FIRST edit on the new canvas
    // from being recorded, which is the reported no-op arriving by a second
    // route (T289182224). Cancel it rather than let its timer land later and
    // clear a suppression the new canvas has meanwhile started.
    endReplaySuppression()
    if (!canvasRoomKey) return
    // Epoch and fingerprint are supplied by the restore effect below, once the
    // canvas is actually interactive. Passing them here would compare the
    // incoming room against the OUTGOING canvas's refs.
    undoHistory.switchToRoom(canvasRoomKey, null, null)
  }, [canvasRoomKey, endReplaySuppression, fingerprintLiveGraph, undoHistory])

  // Restore-side half of the changed-while-parked check.
  //
  // Runs once per (room, epoch) activation, gated on `canvasInteractive` — the
  // existing predicate that requires the CURRENT room's document, a rendered
  // canvas, connection and sync. Comparing any earlier would read the previous
  // canvas's refs, or a half-loaded document, and drop every history.
  //
  // Deliberately NOT re-run as the user edits: the guard's question is "did
  // this canvas change while nobody was looking at it", and re-validating
  // against the user's own edits would delete the very history they are
  // building. `nodesRef`/`edgesRef` are refs, so edits do not retrigger it, and
  // the latch makes a StrictMode double-invoke a no-op.
  //
  // LAYOUT effect, so it runs BEFORE paint. As a passive effect there was a
  // window after the canvas became interactive and before validation ran in
  // which the user could drag a node — their own edit would then move the live
  // fingerprint, validation would read it as "the canvas changed while you were
  // away", and it would silently discard the history they were building and
  // blame the Director for it. Running before paint means there is no frame the
  // user can interact with first. It is safe to hoist: the gate already
  // requires `canvasInteractive`, which requires the CURRENT room rendered and
  // synced, so the refs are this canvas's by the time this runs at all.
  useLayoutEffect(() => {
    if (!canvasRoomKey || roomEpoch === null || !canvasInteractive) return
    const activation = `${canvasRoomKey}#${roomEpoch}`
    if (undoRestoreCheckedRef.current === activation) return
    undoRestoreCheckedRef.current = activation
    // The refs now hold THIS room's graph; it is the one to fingerprint when
    // the user leaves.
    refsRoomKeyRef.current = canvasRoomKey
    undoHistory.switchToRoom(canvasRoomKey, roomEpoch, fingerprintLiveGraph())
  }, [canvasInteractive, canvasRoomKey, fingerprintLiveGraph, roomEpoch, undoHistory])

  // Record the user's most recent canvas interaction so useYCanvas can suppress
  // automatic auto-focus while the user is actively working (RFC: suppress
  // auto-focus during canvas interaction). We listen at the document level in
  // the CAPTURE phase because several canvas controls (reference selector,
  // config flyouts, @mention dropdowns) render via createPortal to
  // document.body — OUTSIDE the canvas surface DOM — so a canvas-surface-only
  // listener would miss "changing reference" and similar. Events whose target
  // is inside an element marked `data-canvas-interaction-ignore` (the Director
  // chatbox + its popovers) are excluded, satisfying "action in the Director
  // chatbox area doesn't count". Raw input events are used (not ReactFlow's
  // onMoveStart) so the programmatic auto-focus animation never self-marks an
  // interaction.
  useEffect(() => {
    const isIgnored = (target: EventTarget | null): boolean =>
      target instanceof Element &&
      target.closest(`[${CANVAS_INTERACTION_IGNORE_ATTR}]`) !== null

    // Discrete, low-frequency interactions: also log (useful in NAD traces).
    const onDiscrete = (e: Event) => {
      if (isIgnored(e.target)) return
      lastInteractionTsRef.current = Date.now()
      canvasInteractionLog({ event: 'canvas-interaction', kind: e.type })
    }
    // High-frequency interactions (drag/pan/zoom): mark the timestamp but do NOT
    // log, to avoid flooding NAD buffers.
    const onContinuous = (e: Event) => {
      // pointermove only counts as interaction while a button is held (active
      // drag/pan), so passive hovering doesn't keep the cooldown alive. Check
      // this cheap guard before the DOM .closest() walk on every pointermove.
      if (e.type === 'pointermove' && (e as PointerEvent).buttons === 0) return
      if (isIgnored(e.target)) return
      lastInteractionTsRef.current = Date.now()
    }

    const opts: AddEventListenerOptions = { capture: true, passive: true }
    document.addEventListener('pointerdown', onDiscrete, opts)
    document.addEventListener('keydown', onDiscrete, opts)
    document.addEventListener('wheel', onContinuous, opts)
    document.addEventListener('pointermove', onContinuous, opts)
    return () => {
      document.removeEventListener('pointerdown', onDiscrete, opts)
      document.removeEventListener('keydown', onDiscrete, opts)
      document.removeEventListener('wheel', onContinuous, opts)
      document.removeEventListener('pointermove', onContinuous, opts)
    }
  }, [])

  useEffect(() => {
    const canvasSurface = canvasSurfaceRef.current
    if (!canvasSurface) return
    if (canvasInteractive) {
      canvasSurface.removeAttribute('inert')
      return
    }
    canvasSurface.setAttribute('inert', '')
  }, [canvasInteractive])

  // Reset viewport guard on sync session change
  useEffect(() => {
    if (!yjsSynced || !ydocMatchesActiveCanvas) {
      savedViewportRestoredRef.current = false
    }
  }, [ydocMatchesActiveCanvas, yjsSynced])

  // Restore viewport from local browser storage on initial sync.
  useEffect(() => {
    if (
      savedViewport &&
      yjsSynced &&
      ydocMatchesActiveCanvas &&
      !savedViewportRestoredRef.current &&
      reactFlowInstance.current
    ) {
      if (!isDefaultViewport(savedViewport)) {
        reactFlowInstance.current.setViewport(savedViewport, { duration: 0 })
      }
      savedViewportRestoredRef.current = true
      setCanvasVisible(true)
      setRenderedCanvasRoomKey(canvasRoomKey)
    }
  }, [canvasRoomKey, ydocMatchesActiveCanvas, yjsSynced, savedViewport, rfInstanceReady])

  // Show canvas once canvasReady fires (even if no saved viewport)
  useEffect(() => {
    if (canvasReady && yjsSynced && ydocMatchesActiveCanvas) {
      // If the viewport wasn't already claimed, decide it from storage here.
      //
      // The plan reads localStorage inline rather than consulting
      // `savedViewport`, which is one render behind on a reconnect: `yjsSynced`
      // flipping back to true only *schedules* the re-read in `useYViewport`,
      // so `savedViewport` is still null in this commit. `canvasReady` does not
      // reset on a reconnect (the warm room pool reuses the same Y.Doc) and
      // neither do `nodes`, so this effect is pre-armed and used to fire one
      // render ahead of the restore, claim the shared latch, and replace the
      // user's pan/zoom with a fitView they never asked for.
      //
      // The latched case returns early: this effect re-runs on every
      // `nodes.length` change, and once the viewport is settled there is
      // nothing to decide and no reason to pay for a storage read on the
      // hottest component in the app.
      const rfInstance = reactFlowInstance.current
      if (!savedViewportRestoredRef.current && rfInstance) {
        const plan = planViewportOnSync(viewportStorageKey, nodes.length)
        viewportRestoreLog({
          event: 'sync-viewport-decision',
          action: plan.action,
          yjsSynced,
          canvasReady,
          nodeCount: nodes.length,
        })
        if (plan.action === 'restore') {
          rfInstance.setViewport(plan.viewport, { duration: 0 })
          savedViewportRestoredRef.current = true
        } else if (plan.action === 'fitview') {
          rfInstance.fitView({ padding: 0.2, maxZoom: 1, duration: 0 })
          savedViewportRestoredRef.current = true
        }
      }
      setCanvasVisible(true)
      setRenderedCanvasRoomKey(canvasRoomKey)
    }
  }, [
    canvasReady,
    canvasRoomKey,
    ydocMatchesActiveCanvas,
    yjsSynced,
    nodes.length,
    rfInstanceReady,
    viewportStorageKey,
  ])

  useEffect(() => {
    if (!canvasReady || !yjsSynced || !ydocMatchesActiveCanvas) return
    const key = `dreamwrite:desk-return:${projectId}:${activeCanvasId}`
    const saved = sessionStorage.getItem(key)
    if (!saved) return
    sessionStorage.removeItem(key)
    let parsed: ApiValue
    try { parsed = JSON.parse(saved) } catch { return }
    if (!isApiRecord(parsed) || !Array.isArray(parsed.selected)) return
    const ids: string[] = []
    for (const id of parsed.selected) {
      if (typeof id !== 'string') return
      ids.push(id)
    }
    const selection = new Set(ids)
    selectedNodeIdsRef.current = selection
    setSelectedNodeIds(selection)
    setNodes(previous => previous.map(node => ({ ...node, selected: selection.has(node.id) })))
  }, [canvasReady, yjsSynced, ydocMatchesActiveCanvas, projectId, activeCanvasId, setNodes])

  // A desk publish occurs while ReactFlow is unmounted. Consume its focus
  // intent only after nodes and viewport restoration have completed.
  useEffect(() => {
    if (!canvasReady || !canvasVisible || !yjsSynced || !ydocMatchesActiveCanvas || !rfInstanceReady || !savedViewportRestoredRef.current) return
    const state: unknown = location.state
    if (typeof state !== 'object' || state === null || !('directorDeskReturn' in state) || state.directorDeskReturn !== true || !('focusNodeId' in state) || typeof state.focusNodeId !== 'string') return
    const target = nodes.find(node => node.id === state.focusNodeId)
    const rf = reactFlowInstance.current
    if (!target || !rf) return
    const frame = requestAnimationFrame(() => {
      const focused = rf.fitView({ nodes: [target], padding: 0.3, duration: 0, maxZoom: 1 })
      viewportRestoreLog({ event: 'director-desk:focus-export', projectId, canvasId: activeCanvasId, nodeId: target.id, focused, width: target.width, height: target.height })
      if (!focused) {
        // Offscreen nodes may not have measured dimensions yet. Reveal the
        // target first; its dimension update will retry this effect and fit it.
        rf.setCenter(target.position.x, target.position.y, { zoom: 1, duration: 0 })
        return
      }
      // Clearing router state can remount the Canvas. Persist the actual focus
      // synchronously so the new mount cannot restore the pre-desk pan.
      writeViewport(rf.getViewport())
      const selection = new Set([target.id])
      selectedNodeIdsRef.current = selection
      setSelectedNodeIds(selection)
      navigate(location.pathname + location.search, { replace: true, state: null })
    })
    return () => cancelAnimationFrame(frame)
  }, [canvasReady, canvasVisible, yjsSynced, ydocMatchesActiveCanvas, rfInstanceReady, nodes, location, navigate, projectId, activeCanvasId, writeViewport])

  const [isPerformancePulseActive, setIsPerformancePulseActive] = useState(false)
  const performancePulseTimeoutRef = useRef<number | null>(null)
  const viewportInteractionTimeoutRef = useRef<number | null>(null)
  const viewportInteractingRef = useRef(false)
  const panePointerDownRef = useRef(false)
  const panePointerMovedRef = useRef(false)
  const panePointerStartRef = useRef<{ x: number; y: number } | null>(null)
  // Wheel-driven interaction (trackpad pan, pinch) disables node pointer events
  // like a drag-pan does, but the pointer is free while it runs. Once the user
  // moves it, they are aiming at something, so the canvas is released and the
  // rest of that scroll stream (the momentum tail) does not re-arm it.
  // Otherwise every click in the ~200ms after the last wheel event -- the
  // whole momentum tail on a trackpad -- lands on the pane, which deselects
  // the node instead of pressing its dock or video buttons.
  const wheelPointerStartRef = useRef<{ x: number; y: number } | null>(null)
  const wheelStreamReleasedRef = useRef(false)
  const lastWheelTimeRef = useRef(Number.NEGATIVE_INFINITY)
  const reactFlowRootRef = useRef<HTMLElement | null>(null)

  const getReactFlowRoot = useCallback(() => {
    const cached = reactFlowRootRef.current
    if (cached && canvasSurfaceRef.current?.contains(cached)) return cached
    const reactFlowRoot = canvasSurfaceRef.current?.querySelector('.react-flow') as HTMLElement | null
    reactFlowRootRef.current = reactFlowRoot
    return reactFlowRoot
  }, [])

  const updateViewportPreviewTransform = useCallback((viewport?: { x: number; y: number; zoom: number }) => {
    const nextViewport = viewport ?? reactFlowInstance.current?.getViewport()
    if (!nextViewport) return
    const reactFlowRoot = getReactFlowRoot()
    if (!reactFlowRoot) return
    reactFlowRoot.style.setProperty('--canvas-viewport-x', `${nextViewport.x}px`)
    reactFlowRoot.style.setProperty('--canvas-viewport-y', `${nextViewport.y}px`)
    reactFlowRoot.style.setProperty('--canvas-viewport-zoom', String(nextViewport.zoom))
  }, [getReactFlowRoot])

  const setReactFlowInteractionClass = useCallback((enabled: boolean) => {
    const prev = viewportInteractingRef.current
    const isTransition = prev !== enabled
    viewportInteractingRef.current = enabled
    // Hold new canvas media loads until the gesture settles. This is the single
    // choke point for "is the viewport being interacted with", so hanging the
    // image-loader gate here keeps one source of truth rather than a parallel
    // begin/end pair that could drift out of sync with the CSS classes below.
    //
    // Edge-triggered because this is called repeatedly with the same value.
    // Duration is not bounded here: the loader keeps deferring for as long as
    // the gesture is still producing move events (see `touchCanvasGesture` in
    // handleViewportMove), and only gives up once movement has actually
    // stopped — so a long pan stays smooth, and a lost end event still
    // recovers instead of leaving the canvas blank.
    if (isTransition) {
      if (enabled) beginCanvasGesture()
      else endCanvasGesture()
    }
    canvasSurfaceRef.current?.classList.toggle('canvas-surface-viewport-interacting', enabled)
    const reactFlowRoot = getReactFlowRoot()
    reactFlowRoot?.classList.toggle('canvas-viewport-interacting', enabled)
    const isNodeDragging = Boolean(reactFlowRoot?.classList.contains('canvas-node-dragging'))
    reactFlowRoot?.classList.toggle(
      'canvas-performance-active',
      enabled || isPerformancePulseActive || isNodeDragging,
    )
    canvasSurfaceRef.current?.classList.toggle(
      'canvas-surface-performance-active',
      enabled || isPerformancePulseActive || isNodeDragging,
    )
    // Guard hot-path logging: only emit on actual transition and only when NAD debug enabled
    if (isTransition && willChangeTextCrispnessLog.enabled) {
      willChangeTextCrispnessLog({
        event: enabled ? 'viewport-interaction-start' : 'viewport-interaction-end',
        enabled,
        prevEnabled: prev,
        hasViewportClass: Boolean(reactFlowRoot?.classList.contains('canvas-viewport-interacting')),
        hasNodeDragging: isNodeDragging,
        reason: 'will-change scoped to interaction for crisp text at idle zoom (post 1586138096476821)',
      })
    }
  }, [
    getReactFlowRoot,
    isPerformancePulseActive,
  ])

  const setReactFlowNodeDraggingClass = useCallback((enabled: boolean) => {
    const reactFlowRoot = getReactFlowRoot()
    const prevDragging = Boolean(reactFlowRoot?.classList.contains('canvas-node-dragging'))
    const isTransition = prevDragging !== enabled
    reactFlowRoot?.classList.toggle('canvas-node-dragging', enabled)
    reactFlowRoot?.classList.toggle('canvas-performance-active', enabled || isPerformancePulseActive || viewportInteractingRef.current)
    canvasSurfaceRef.current?.classList.toggle(
      'canvas-surface-performance-active',
      enabled || isPerformancePulseActive || viewportInteractingRef.current,
    )
    if (isTransition && willChangeTextCrispnessLog.enabled) {
      willChangeTextCrispnessLog({
        event: enabled ? 'node-drag-start' : 'node-drag-end',
        enabled,
        prevDragging,
        hasViewportClass: Boolean(reactFlowRoot?.classList.contains('canvas-viewport-interacting')),
        hasNodeDragging: Boolean(reactFlowRoot?.classList.contains('canvas-node-dragging')),
        reason: 'will-change scoped to interaction for crisp text at idle zoom (post 1586138096476821)',
      })
    }
  }, [getReactFlowRoot, isPerformancePulseActive])

  const clearViewportInteractionTimeout = useCallback(() => {
    if (viewportInteractionTimeoutRef.current != null) {
      window.clearTimeout(viewportInteractionTimeoutRef.current)
      viewportInteractionTimeoutRef.current = null
    }
  }, [])

  const scheduleViewportInteractionEnd = useCallback((delayMs: number) => {
    clearViewportInteractionTimeout()
    viewportInteractionTimeoutRef.current = window.setTimeout(() => {
      viewportInteractionTimeoutRef.current = null
      setReactFlowInteractionClass(false)
    }, delayMs)
  }, [clearViewportInteractionTimeout, setReactFlowInteractionClass])

  const beginViewportInteraction = useCallback((autoEndDelayMs?: number) => {
    clearViewportInteractionTimeout()
    setReactFlowInteractionClass(true)
    if (autoEndDelayMs != null && autoEndDelayMs > 0) {
      scheduleViewportInteractionEnd(autoEndDelayMs)
    }
  }, [clearViewportInteractionTimeout, scheduleViewportInteractionEnd, setReactFlowInteractionClass])

  const endViewportInteraction = useCallback((delayMs = 0) => {
    clearViewportInteractionTimeout()
    if (delayMs <= 0) {
      setReactFlowInteractionClass(false)
      return
    }
    scheduleViewportInteractionEnd(delayMs)
  }, [clearViewportInteractionTimeout, scheduleViewportInteractionEnd, setReactFlowInteractionClass])

  const handleViewportMove = useCallback((_event: unknown, viewport: { x: number; y: number; zoom: number }) => {
    // Keep the media gate's idle watchdog alive so a long continuous pan defers
    // for as long as it actually lasts, while a lost move-end still recovers.
    // One timestamp write per frame — no timer churn.
    touchCanvasGesture()
    updateViewportPreviewTransform(viewport)
  }, [updateViewportPreviewTransform])

  useEffect(() => {
    updateViewportPreviewTransform()
  }, [rfInstanceReady, canvasVisible, updateViewportPreviewTransform])

  const pulseCanvasPerformanceMode = useCallback((durationMs = 1200) => {
    setIsPerformancePulseActive(true)
    if (performancePulseTimeoutRef.current != null) {
      window.clearTimeout(performancePulseTimeoutRef.current)
    }
    performancePulseTimeoutRef.current = window.setTimeout(() => {
      performancePulseTimeoutRef.current = null
      setIsPerformancePulseActive(false)
      canvasSurfaceRef.current?.classList.toggle(
        'canvas-surface-performance-active',
        viewportInteractingRef.current || Boolean(getReactFlowRoot()?.classList.contains('canvas-node-dragging')),
      )
    }, durationMs)
  }, [getReactFlowRoot])

  useEffect(() => () => {
    if (performancePulseTimeoutRef.current != null) {
      window.clearTimeout(performancePulseTimeoutRef.current)
    }
    if (viewportInteractionTimeoutRef.current != null) {
      window.clearTimeout(viewportInteractionTimeoutRef.current)
    }
  }, [])

  const onMoveStart = useCallback(() => {
    if (panePointerDownRef.current && !panePointerMovedRef.current) return
    // ReactFlow ends a scroll pan after 150ms without wheel events, so a later
    // event of a stream the pointer already released starts a new move here.
    // Respect the release, or the momentum tail re-arms it with nothing left
    // to clear it.
    const msSinceLastWheel = performance.now() - lastWheelTimeRef.current
    if (
      !panePointerDownRef.current &&
      wheelStreamReleasedRef.current &&
      msSinceLastWheel < WHEEL_STREAM_GAP_MS
    ) {
      canvasPointerReleaseLog({
        event: 'released-stream-rearm-skipped',
        feature: 'canvas:pointer-release',
        trigger: 'onMoveStart',
        msSinceLastWheel: Math.round(msSinceLastWheel),
        nodePointerEvents: 'auto',
      })
      return
    }
    beginViewportInteraction(panePointerDownRef.current ? undefined : 240)
  }, [beginViewportInteraction])

  // Persist viewport on move end
  const onMoveEndSave = useCallback(() => {
    emitNuxEvent({ type: 'controlActivated', controlId: 'canvasInteracted' })
    panePointerDownRef.current = false
    panePointerMovedRef.current = false
    panePointerStartRef.current = null
    endViewportInteraction()
    if (reactFlowInstance.current) {
      const viewport = reactFlowInstance.current.getViewport()
      updateViewportPreviewTransform(viewport)
      writeViewport(viewport)
    }
  }, [emitNuxEvent, endViewportInteraction, updateViewportPreviewTransform, writeViewport])

  const handleCanvasPointerDownCapture = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    captureNodeInteraction(event)
    if (event.button !== 0) return
    const target = event.target
    if (!(target instanceof Element)) return
    if (!target.closest('.react-flow')) return
    if (target.closest('.react-flow__node, .react-flow__controls, .react-flow__minimap, .react-flow__panel, .nopan, .nodrag')) return
    panePointerDownRef.current = true
    panePointerMovedRef.current = false
    panePointerStartRef.current = { x: event.clientX, y: event.clientY }
    // Arm will-change a frame before the pan starts so first-drag prewarm benefit
    // of RFC-0754 is retained while still allowing crisp re-raster at idle zoom
    // (post 1586138096476821 regression of 8f7e24db8). Clears on pointerup.
    beginViewportInteraction()
  }, [beginViewportInteraction, captureNodeInteraction])

  const handleCanvasPointerMoveCapture = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const wheelStart = wheelPointerStartRef.current
    if (wheelStart && event.buttons === 0 && !panePointerDownRef.current) {
      if (!viewportInteractingRef.current) {
        // The scroll already settled on its own.
        wheelPointerStartRef.current = null
        return
      }
      const wdx = event.clientX - wheelStart.x
      const wdy = event.clientY - wheelStart.y
      if ((wdx * wdx) + (wdy * wdy) >= VIEWPORT_PAN_ACTIVATION_DISTANCE_PX * VIEWPORT_PAN_ACTIVATION_DISTANCE_PX) {
        wheelPointerStartRef.current = null
        wheelStreamReleasedRef.current = true
        endViewportInteraction()
        canvasPointerReleaseLog({
          event: 'wheel-interaction-released',
          feature: 'canvas:pointer-release',
          trigger: 'pointermove',
          distancePx: Math.round(Math.sqrt((wdx * wdx) + (wdy * wdy))),
          msSinceLastWheel: Math.round(event.timeStamp - lastWheelTimeRef.current),
          nodePointerEvents: 'auto',
        })
      }
      return
    }
    if (!panePointerDownRef.current || panePointerMovedRef.current) return
    if ((event.buttons & 1) !== 1) return
    const start = panePointerStartRef.current
    if (!start) return
    const dx = event.clientX - start.x
    const dy = event.clientY - start.y
    if ((dx * dx) + (dy * dy) < VIEWPORT_PAN_ACTIVATION_DISTANCE_PX * VIEWPORT_PAN_ACTIVATION_DISTANCE_PX) return
    panePointerMovedRef.current = true
    beginViewportInteraction()
  }, [beginViewportInteraction, endViewportInteraction])

  const handleCanvasPointerEndCapture = useCallback(() => {
    if (!panePointerDownRef.current) return
    panePointerDownRef.current = false
    panePointerMovedRef.current = false
    panePointerStartRef.current = null
    endViewportInteraction()
  }, [endViewportInteraction])

  const handleCanvasWheelCapture = useCallback((event: React.WheelEvent<HTMLDivElement>) => {
    const target = event.target
    if (!(target instanceof Element)) return
    if (!target.closest('.react-flow')) return
    // Timed before the target filter: momentum events that land on the dock
    // or another .nopan control still belong to the stream (onMoveStart reads
    // this too).
    const sinceLastWheel = event.timeStamp - lastWheelTimeRef.current
    lastWheelTimeRef.current = event.timeStamp
    if (target.closest('.nowheel, .nopan, .nodrag, .react-flow__controls, .react-flow__minimap, .react-flow__panel')) return
    if (wheelStreamReleasedRef.current && sinceLastWheel < WHEEL_STREAM_GAP_MS) return
    wheelStreamReleasedRef.current = false
    wheelPointerStartRef.current = { x: event.clientX, y: event.clientY }
    beginViewportInteraction(WHEEL_STREAM_GAP_MS)
  }, [beginViewportInteraction])

  useEffect(() => {
    const handleWindowPointerEnd = () => {
      if (!panePointerDownRef.current) return
      panePointerDownRef.current = false
      panePointerMovedRef.current = false
      panePointerStartRef.current = null
      endViewportInteraction()
    }
    window.addEventListener('pointerup', handleWindowPointerEnd, true)
    window.addEventListener('pointercancel', handleWindowPointerEnd, true)
    return () => {
      window.removeEventListener('pointerup', handleWindowPointerEnd, true)
      window.removeEventListener('pointercancel', handleWindowPointerEnd, true)
    }
  }, [endViewportInteraction])

  // Save viewport on page unload (catches cases where onMoveEnd didn't fire)
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (reactFlowInstance.current) {
        writeViewport(reactFlowInstance.current.getViewport())
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [writeViewport])

  // ─── Node Execution ───────────────────────────────────────────────────────

  const handleExecuteNode = useCallback(
    async (node: WorkspaceNode) => {
      if (node.data?.status === 'running') return
      if (!projectId) return

      // Track active execution
      const activeNodes = window.__activeExecutingNodes ??= new Set<string>()
      activeNodes.add(node.id)

      // Set running state
      setNodes((nds) => {
        const updated = nds.map((n) =>
          n.id === node.id
            ? { ...n, data: { ...n.data, status: 'running' as const, error: undefined } }
            : n
        )
        nodesRef.current = updated
        return updated
      })
      if (!isFeNodeType(node.type)) {
        throw new Error(`Cannot execute node ${node.id}: unknown node type ${node.type ?? '<missing>'}`)
      }
      writeNodeData({ id: node.id, type: node.type }, { status: 'running', error: undefined })

      // Execute via REST (fire-and-forget — backend reads data from YDoc snapshot).
      // A Closing-frame video carries the handshake an older API refuses (RFC-1668).
      const stateUpdates = isTypedReactFlowNode(node, 'videoGenerator')
        ? videoExecuteStateUpdates(node.type, node.data.in_mode)
        : undefined
      try {
        await executeNode(projectId, node.id, {
          canvasId: activeCanvasId,
          ...(stateUpdates ? { stateUpdates } : {}),
        })
      } catch (error) {
        // A definitive refusal must not leave the node stuck in 'running' (the
        // guard above would then block every retry). An ambiguous failure may
        // follow admission, so it leaves the status to the backend.
        if (!(error instanceof Error) || !isDefinitiveExecuteNodeRefusal(error)) throw error
        activeNodes.delete(node.id)
        const message = error instanceof Error ? error.message : String(error)
        const displayed = isClosingFrameHandshakeRefusal(message)
          // An API without Closing frame refused the handshake (RFC-1668).
          ? t('videoConfigFlyout.closingFrameUnavailable', { ns: 'canvas' })
          : message
        setNodes((nds) => {
          const updated = nds.map((n) =>
            n.id === node.id
              ? { ...n, data: { ...n.data, status: 'error' as const, error: displayed } }
              : n
          )
          nodesRef.current = updated
          return updated
        })
        writeNodeData({ id: node.id, type: node.type }, { status: 'error', error: displayed })
      }
    },
    [activeCanvasId, projectId, setNodes, t, writeNodeData]
  )

  const requestNodesDelete = useCallback((
    requestedNodeIds: string[],
    trigger: NodeDeleteTrigger = 'toolbar',
  ): boolean => {
    const uniqueNodeIds = Array.from(new Set(requestedNodeIds))
    const traceId = `canvas-node-delete:${trigger}:${uniqueNodeIds.join(',')}:${Date.now()}`

    if (pendingNodeDeleteRef.current) {
      nodeDeleteConfirmLog({
        event: 'delete-request-ignored',
        feature: 'canvas-node-delete',
        trace_id: traceId,
        reason: 'dialog-already-open',
        trigger,
        nodeIdsCsv: uniqueNodeIds.join(','),
      })
      return true
    }

    if (uniqueNodeIds.length === 0) {
      nodeDeleteConfirmLog({
        event: 'delete-request-ignored',
        feature: 'canvas-node-delete',
        trace_id: traceId,
        reason: 'empty-selection',
        trigger,
      })
      return true
    }

    const currentNodes = nodesRef.current
    const foundNodes: WorkspaceNode[] = []
    const missingNodeIds: string[] = []
    for (const nodeId of uniqueNodeIds) {
      const node = currentNodes.find((candidate) => candidate.id === nodeId)
      if (node) foundNodes.push(node)
      else missingNodeIds.push(nodeId)
    }

    if (missingNodeIds.length > 0) {
      console.error('[canvas] delete confirmation requested for missing node(s)', { nodeIds: missingNodeIds })
      nodeDeleteConfirmLog({
        event: 'delete-request-ignored',
        feature: 'canvas-node-delete',
        trace_id: traceId,
        reason: 'missing-node',
        trigger,
        nodeIdsCsv: missingNodeIds.join(','),
      })
    }

    const groupNodeIds = foundNodes
      .filter(isGroupCanvasNode)
      .map((node) => node.id)
    if (groupNodeIds.length > 0) {
      nodeDeleteConfirmLog({
        event: 'delete-request-ignored',
        feature: 'canvas-node-delete',
        trace_id: traceId,
        reason: 'group-node-owned-handler',
        trigger,
        nodeIdsCsv: groupNodeIds.join(','),
      })
    }

    const nodesToDelete = foundNodes.filter((node) => !isGroupCanvasNode(node))
    if (nodesToDelete.length === 0) return true

    const pending: PendingNodeDelete = {
      nodeIds: nodesToDelete.map((node) => node.id),
      nodes: nodesToDelete.map((node) => ({
        id: node.id,
        type: node.type,
        label: getNodeDeleteLabel(node),
      })),
      trigger,
      traceId,
    }

    nodeDeleteConfirmLog({
      event: 'delete-requested',
      feature: 'canvas-node-delete',
      trace_id: traceId,
      trigger,
      nodeIdsCsv: pending.nodeIds.join(','),
      nodeTypesCsv: pending.nodes.map((node) => node.type ?? '').join(','),
      selectedCount: selectedNodeIdsRef.current.size,
    })
    pendingNodeDeleteRef.current = pending
    setPendingNodeDelete(pending)
    nodeDeleteConfirmLog({
      event: 'delete-dialog-opened',
      feature: 'canvas-node-delete',
      trace_id: traceId,
      trigger,
      count: pending.nodeIds.length,
      nodeIdsCsv: pending.nodeIds.join(','),
      labelCount: pending.nodes.length,
    })
    return true
  }, [])

  const requestNodeDelete = useCallback((
    nodeId: string,
    trigger: NodeDeleteTrigger = 'toolbar',
  ): boolean => requestNodesDelete([nodeId], trigger), [requestNodesDelete])

  const pruneDeletedReactFlowNodes = useCallback((
    nodeIds: Iterable<string>,
    reason: string,
  ): void => {
    const ydocNodes = ydoc.getMap('nodes')
    const staleNodeIds = Array.from(new Set(nodeIds)).filter((nodeId) =>
      recentlyDeletedNodeIdsRef.current.has(nodeId) && !ydocNodes.has(nodeId)
    )
    if (staleNodeIds.length === 0) return

    const staleNodeIdSet = new Set(staleNodeIds)
    const filterNodes = (currentNodes: ReactFlowCanvasNode[]): ReactFlowCanvasNode[] =>
      currentNodes.filter((node) => !staleNodeIdSet.has(node.id))
    const applyNodePrune = (
      currentNodes: ReactFlowCanvasNode[],
      source: string,
    ): ReactFlowCanvasNode[] => {
      const nextNodes = filterNodes(currentNodes)
      if (nextNodes.length === currentNodes.length) return currentNodes
      nodesRef.current = nextNodes
      nodeDeleteLog({
        event: 'stale-reactflow-node-pruned',
        nodeIdsCsv: staleNodeIds.join(','),
        reason,
        source,
        beforeCount: currentNodes.length,
        afterCount: nextNodes.length,
      })
      return nextNodes
    }
    const applyEdgePrune = (currentEdges: ReactFlowCanvasEdge[]): ReactFlowCanvasEdge[] => {
      const nextEdges = currentEdges.filter((edge) =>
        !staleNodeIdSet.has(edge.source) && !staleNodeIdSet.has(edge.target)
      )
      if (nextEdges.length === currentEdges.length) return currentEdges
      edgesRef.current = nextEdges
      return nextEdges
    }

    setNodes((currentNodes) => applyNodePrune(currentNodes, 'workspace-state'))
    setEdges(applyEdgePrune)
  }, [setEdges, setNodes, ydoc])

  const scheduleDeletedReactFlowPrune = useCallback((
    nodeIds: Iterable<string>,
    reason: string,
  ): void => {
    const nodeIdList = Array.from(new Set(nodeIds))
    if (nodeIdList.length === 0) return

    pruneDeletedReactFlowNodes(nodeIdList, reason)
    queueMicrotask(() => pruneDeletedReactFlowNodes(nodeIdList, `${reason}:microtask`))
    requestAnimationFrame(() => {
      pruneDeletedReactFlowNodes(nodeIdList, `${reason}:raf1`)
      requestAnimationFrame(() => {
        pruneDeletedReactFlowNodes(nodeIdList, `${reason}:raf2`)
      })
    })
  }, [pruneDeletedReactFlowNodes])

  useEffect(() => {
    if (recentlyDeletedNodeIdsRef.current.size === 0) return
    const ydocNodes = ydoc.getMap('nodes')
    const staleNodeIds = nodes
      .filter((node) => recentlyDeletedNodeIdsRef.current.has(node.id) && !ydocNodes.has(node.id))
      .map((node) => node.id)
    if (staleNodeIds.length === 0) return
    scheduleDeletedReactFlowPrune(staleNodeIds, 'nodes-state-tombstone')
  }, [nodes, scheduleDeletedReactFlowPrune, ydoc])

  // ─── CanvasContext Value ──────────────────────────────────────────────────

  const canvasContextValue: CanvasContextValue = useMemo(
    () => ({
      executionActivity: { ydocRunningCount, ydocQueuedCount, ydocDirectorRunning, ydocStatusLive },
      addNode: (
        type: string,
        position: { x: number; y: number },
        data?: RawNodeDataRecord,
        options?: { autoExecute?: boolean; parentId?: string; id?: string }
      ) => {
        // Record BEFORE the node exists so Cmd+Z can walk back past a creation.
        // Without this, undo only unwinds delete/paste/duplicate/drag and
        // dead-ends on the first hand-added node, which reads to the user as
        // "undo stopped working". Undoing a creation is asset-safe: removal
        // touches YDoc only (`GraphManager.remove_node`), nothing calls
        // `delete_asset`, and there is no orphan sweep — so the generated files
        // survive in GCS and redo restores the node still pointing at them.
        if (!isUndoRedo.current) {
          undoHistory.record(canvasRoomKey, roomEpoch, { nodes: ydocSafeSnapshotNodes(nodesRef.current), edges: [...edgesRef.current] })
        }
        const imageDefaultData = applyDefaultImageModel(type, data, defaultImageModel)
        const styledData = applyWorkspaceStyleDefault(type, imageDefaultData, workspaceStyleId)
        const resolvedData = applyProjectResolutionDefault(
          type,
          styledData,
          projectVideoResolution,
          imageDefaultData,
        )
        const visualDefaultedData = applyProjectAspectRatioDefault(
          type,
          resolvedData,
          projectAspectRatio,
          imageDefaultData,
        )
        const initialData = withVideoGeneratorCreationAudioDefaults(
          type,
          visualDefaultedData,
          {
            explicitData: data ?? {},
          },
        )
        assertNoDirectAssetInputPortWrites(
          type,
          '<new>',
          initialData,
          'addNode',
        )
        const newNode = createNode(type, position, {
          ...initialData,
          projectId,
          cacheKey: project?.updated_at,
        }, undefined, options?.id)
        const typedNewNode: WorkspaceNode = options?.parentId
          ? { ...newNode, parentId: options.parentId }
          : newNode

        // Keep placement reads current for back-to-back node creation in the
        // same tick, such as multiple reference uploads from one picker.
        nodesRef.current = appendOrReplaceNodeById(nodesRef.current, typedNewNode)
        setNodes((nds) => {
          const updated = appendOrReplaceNodeById(nds, typedNewNode)
          nodesRef.current = updated
          return updated
        })

        // Pre-create in Y doc (include parent if provided so rebuild()
        // never sees a parentless intermediate state)
        if (shouldSyncToBackend(type)) {
          addNodeToYDoc(
            newNode.id,
            type,
            firstStringDataField(data, ['label', 'name']) ?? newNode.id,
            position.x,
            position.y,
            initialData,
            true,
            options?.parentId,
          )
        }

        // Group nodes are frontend-only (not synced to backend) but still need
        // a Y doc entry so that writeNodeData (used by updateNodeData) can find
        // the node and persist child_node_ids updates.
        if (type === 'group') {
          addNodeToYDoc(
            newNode.id,
            type,
            stringDataField(data, 'label') ?? 'Group',
            position.x,
            position.y,
            initialData,
          )
        }



        // Auto-execute if requested
        if (options?.autoExecute) {
          const schema = nodeSchemas[type]
          const hasBackendExecutor = !!(schema && 'backendNodeId' in schema)
          if (hasBackendExecutor) {
            setTimeout(() => handleExecuteNode(typedNewNode), 100)
          }
        }

        return newNode.id
      },

      prepareNode: (
        type: string,
        position: { x: number; y: number },
        data?: RawNodeDataRecord,
      ) => {
        // The record lives in the PREPARE half of the prepare/sync pair, not in
        // `syncPreparedNodeToYDoc`. Its one caller (GroupNode's add-panel insert)
        // runs `setNodes` between the two, so by sync time the snapshot would
        // already contain the new node and the undo entry would be a no-op.
        if (!isUndoRedo.current) {
          undoHistory.record(canvasRoomKey, roomEpoch, { nodes: ydocSafeSnapshotNodes(nodesRef.current), edges: [...edgesRef.current] })
        }
        const imageDefaultData = applyDefaultImageModel(type, data, defaultImageModel)
        const styledData = applyWorkspaceStyleDefault(type, imageDefaultData, workspaceStyleId)
        const resolvedData = applyProjectResolutionDefault(
          type,
          styledData,
          projectVideoResolution,
          imageDefaultData,
        )
        const visualDefaultedData = applyProjectAspectRatioDefault(
          type,
          resolvedData,
          projectAspectRatio,
          imageDefaultData,
        )
        const initialData = withVideoGeneratorCreationAudioDefaults(
          type,
          visualDefaultedData,
          {
            explicitData: data ?? {},
          },
        )
        assertNoDirectAssetInputPortWrites(type, '<new>', initialData, 'addNode')
        return createNode(type, position, {
          ...initialData,
          projectId,
          cacheKey: project?.updated_at,
        })
      },

      syncPreparedNodeToYDoc: (
        node: { id: string; type: string; position: { x: number; y: number }; data: RawNodeDataRecord },
        parentId?: string,
      ) => {
        if (shouldSyncToBackend(node.type)) {
          addNodeToYDoc(
            node.id,
            node.type,
            firstStringDataField(node.data, ['label', 'name']) ?? node.id,
            node.position.x,
            node.position.y,
            node.data,
            true,
            parentId,
          )
        }
      },

      addEdge: (source: string, sourceHandle: string, target: string, targetHandle: string) => {
        setEdges((eds) => {
          const newEdge: WorkspaceEdge = {
            id: `${source}-${target}-${Date.now()}`,
            source,
            sourceHandle,
            target,
            targetHandle,
          }
          const updated = [...eds, newEdge]
          edgesRef.current = updated
          return updated
        })
      },

      removeNode: (nodeId: string) => {
        const nodeToRemove = nodesRef.current.find((n) => n.id === nodeId)
        recentlyDeletedNodeIdsRef.current.add(nodeId)
        nodeOpsLog({
          event: 'remove-node-requested',
          feature: 'canvas:node-ops',
          nodeId,
          nodeType: nodeToRemove?.type,
          selectedCount: selectedNodeIdsRef.current.size,
        })
        // Record a YDoc-safe undo snapshot BEFORE removal so Cmd+Z restores the
        // node WITH its assets. Toolbar/button delete (useNodeActions.handleDelete)
        // routes here and previously recorded no undo at all. Snapshot must be
        // plain (no live Y.Map) or the delete tombstones the snapshot's assets.
        // Programmatic setNodes here does not fire ReactFlow onNodesChange, so this
        // does not double-record with the keyboard-delete path.
        if (!isUndoRedo.current) {
          undoHistory.record(canvasRoomKey, roomEpoch, { nodes: ydocSafeSnapshotNodes(nodesRef.current), edges: [...edgesRef.current] })
        }
        // Clean up parent group's in_child_node_ids (same logic as onNodesChange removal handler)
        const removedNode = nodesRef.current.find((n) => n.id === nodeId)
        if (removedNode?.parentId) {
          const parentId = removedNode.parentId
          const parentGroup = nodesRef.current.find((n) => n.id === parentId)
          if (parentGroup?.type === 'nodeGroup') {
            let childIds = childNodeIds(parentGroup.data)
            childIds = childIds.map((id) => (id === nodeId ? null : id))
            while (childIds.length > 0 && childIds[childIds.length - 1] === null) {
              childIds.pop()
            }
            // YDoc-first deletion: persist the parent's updated membership and
            // remove the node (and its edges) from YDoc. rebuild() re-derives
            // React node/edge state from YDoc — no optimistic setNodes/setEdges.
            // FAIL-LOUD: rebuild() can only drop a node that exists in YDoc.
            if (!ydoc.getMap('nodes').has(nodeId)) {
              console.error('[canvas] delete target absent from YDoc; React-only node will not be removed by rebuild', { nodeId })
            }
            // removeNodeFromYDoc also removes connected edges.
            ydoc.transact(() => {
              writeNodeData({ id: parentId, type: 'nodeGroup' }, { in_child_node_ids: childIds })
              removeNodeFromYDoc(nodeId)
            }, 'frontend')
            nodeOpsLog({
              event: 'remove-node-committed',
              feature: 'canvas:node-ops',
              nodeId,
              nodeType: removedNode.type,
              parentId,
              mode: 'single-child',
            })
            nodeDeleteLog({
              event: 'ydoc-first-remove-committed',
              nodeId,
              nodeType: removedNode.type,
              parentId,
              mode: 'single-child',
            })
            scheduleDeletedReactFlowPrune([nodeId], 'remove-node:single-child')

            return
          }
        }
        // Non-group-child nodes: YDoc-first deletion. rebuild() re-derives React
        // node/edge state from YDoc — no optimistic setNodes/setEdges.
        // FAIL-LOUD: rebuild() can only drop a node that exists in YDoc.
        if (!ydoc.getMap('nodes').has(nodeId)) {
          console.error('[canvas] delete target absent from YDoc; React-only node will not be removed by rebuild', { nodeId })
        }
        // removeNodeFromYDoc also removes connected edges.
        removeNodeFromYDoc(nodeId)
        nodeOpsLog({
          event: 'remove-node-committed',
          feature: 'canvas:node-ops',
          nodeId,
          nodeType: nodeToRemove?.type,
          mode: 'single',
        })
        nodeDeleteLog({
          event: 'ydoc-first-remove-committed',
          nodeId,
          nodeType: nodeToRemove?.type,
          mode: 'single',
        })
        scheduleDeletedReactFlowPrune([nodeId], 'remove-node:single')

      },

      removeNodes: (nodeIds: string[]) => {
        for (const nodeId of nodeIds) {
          recentlyDeletedNodeIdsRef.current.add(nodeId)
        }
        const nodeTypes = nodeIds
          .map((nodeId) => nodesRef.current.find((n) => n.id === nodeId)?.type)
          .filter((type): type is string => typeof type === 'string')
        nodeOpsLog({
          event: 'remove-nodes-requested',
          feature: 'canvas:node-ops',
          nodeIds,
          count: nodeIds.length,
          nodeTypes,
        })
        // Record a YDoc-safe undo snapshot BEFORE removal (see removeNode above).
        if (!isUndoRedo.current) {
          undoHistory.record(canvasRoomKey, roomEpoch, { nodes: ydocSafeSnapshotNodes(nodesRef.current), edges: [...edgesRef.current] })
        }
        const idsSet = new Set(nodeIds)
        const currentNodes = nodesRef.current

        // Clean up parent group's in_child_node_ids for removed children
        // (mirrors removeNode singular and onNodesChange removal handler)
        const parentUpdates = new Map<string, (string | null)[]>()
        for (const nodeId of nodeIds) {
          const removedNode = currentNodes.find((n) => n.id === nodeId)
          if (!removedNode?.parentId || idsSet.has(removedNode.parentId)) continue
          const parentGroup = currentNodes.find((n) => n.id === removedNode.parentId)
          if (!parentGroup || parentGroup.type !== 'nodeGroup') continue
          let childIds = parentUpdates.get(removedNode.parentId) ?? childNodeIds(parentGroup.data)
          childIds = childIds.map((id) => (id === nodeId ? null : id))
          while (childIds.length > 0 && childIds[childIds.length - 1] === null) {
            childIds.pop()
          }
          parentUpdates.set(removedNode.parentId, childIds)
        }

        // FAIL-LOUD: rebuild() can only drop nodes that exist in YDoc.
        const ydocNodes = ydoc.getMap('nodes')
        for (const nodeId of nodeIds) {
          if (!ydocNodes.has(nodeId)) {
            console.error('[canvas] delete target absent from YDoc; React-only node will not be removed by rebuild', { nodeId })
          }
        }
        ydoc.transact(() => {
          // Persist parent group updates and node removals in one frontend
          // transaction so observers never see a partially-deleted group subtree.
          // YDoc-first deletion: rebuild() re-derives React node/edge state from
          // YDoc — no optimistic setNodes/setEdges. removeNodeFromYDoc also
          // removes connected edges.
          for (const [parentId, newChildIds] of parentUpdates) {
            writeNodeData({ id: parentId, type: 'nodeGroup' }, { in_child_node_ids: newChildIds })
          }

          for (const nodeId of nodeIds) {
            removeNodeFromYDoc(nodeId)
          }
        }, 'frontend')
        nodeOpsLog({
          event: 'remove-nodes-committed',
          feature: 'canvas:node-ops',
          nodeIds,
          count: nodeIds.length,
          parentUpdateCount: parentUpdates.size,
        })
        nodeDeleteLog({
          event: 'ydoc-first-remove-nodes-committed',
          nodeIds,
          count: nodeIds.length,
          parentUpdateCount: parentUpdates.size,
          mode: 'group-cascade',
        })
        scheduleDeletedReactFlowPrune(nodeIds, 'remove-nodes')
      },

      requestNodeDelete,
      requestNodesDelete,

      removeEdge: (edgeId: string) => {
        setEdges((eds) => {
          const updated = eds.filter((e) => e.id !== edgeId)
          edgesRef.current = updated
          return updated
        })
      },

      duplicateNode: (nodeId: string, options: DuplicateNodeOptions) => {
        const traceId = options.traceId ?? newDuplicateTraceId()
        const sourceNode = nodesRef.current.find((n) => n.id === nodeId)
        if (!sourceNode) {
          nodeDuplicateLog({
            event: 'duplicate-node-aborted',
            feature: 'node-duplicate',
            trace_id: traceId,
            projectId,
            sourceNodeId: nodeId,
            mode: options.mode,
            reason: 'source-node-missing',
          })
          return undefined
        }

        // Collapse before recording undo so an invariant violation files no entry.
        let prepared: PreparedDuplicateNodeData
        try {
          prepared = prepareDuplicateNodeData(
            sourceNode.type ?? '',
            cloneDataForCopiedEdges(sourceNode.type, ydocSafeCloneNodeData(sourceNode.data)),
            options,
          )
        } catch (err) {
          if (!(err instanceof DuplicateNodeCollapseError)) throw err
          console.error('[canvas] duplicate current variant failed', { nodeId, error: err.message })
          nodeDuplicateLog({ event: 'duplicate-node-aborted', feature: 'node-duplicate', trace_id: traceId, projectId, sourceNodeId: nodeId, mode: options.mode, reason: err.message })
          toast.error(t('canvas:nodeDuplicateMenu.failed'), { description: err.message })
          return undefined
        }
        const clonedData = prepared.data
        if (prepared.report) {
          nodeDuplicateLog({ event: 'duplicate-node-collapse', feature: 'node-duplicate', trace_id: traceId, projectId, sourceNodeId: nodeId, nodeType: sourceNode.type, ...summarizeCollapseReport(prepared.report) })
        }

        // Single-node duplicate via the context. The multi-select twin
        // (`duplicateSelectedNodes`) already records through `recordForUndo`;
        // this path did not, so a duplicated node could not be undone.
        // Recorded after the early return so a miss files no entry.
        if (!isUndoRedo.current) {
          undoHistory.record(canvasRoomKey, roomEpoch, { nodes: ydocSafeSnapshotNodes(nodesRef.current), edges: [...edgesRef.current] })
        }

        const newId = `${sourceNode.type}-${Date.now()}`
        const newNode: WorkspaceNode = {
          ...sourceNode,
          id: newId,
          position: {
            x: sourceNode.position.x + PASTE_OFFSET,
            y: sourceNode.position.y + PASTE_OFFSET,
          },
          selected: true,
          data: {
            ...clonedData,
            projectId,
            status: 'idle',
            progress: undefined,
            progressMessage: undefined,
            error: undefined,
          },
        }

        // Re-target incoming edges
        const incomingEdges = edgesRef.current.filter((e) => e.target === nodeId)
        const newEdges: WorkspaceEdge[] = incomingEdges.map((edge, i) => ({
          ...edge,
          id: `dup-edge-${Date.now()}-${i}`,
          target: newId,
        }))

        setNodes((nds) => {
          const updated = [...nds.map((n) => ({ ...n, selected: false })), newNode]
          nodesRef.current = updated
          return updated
        })
        if (newEdges.length > 0) {
          setEdges((eds) => {
            const updated = [...eds, ...newEdges]
            edgesRef.current = updated
            return updated
          })
        }

        // Sync to Y doc + backend
        const syncedToYDoc = shouldSyncToBackend(sourceNode.type ?? '')
        if (syncedToYDoc) {
          addNodeToYDoc(
            newId,
            sourceNode.type ?? '',
            options.name,
            newNode.position.x,
            newNode.position.y,
            clonedData,
            true
          )
          const affectedPorts: TargetPortRef[] = []
          for (const e of newEdges) {
            rememberTargetPort(affectedPorts, e.target, e.targetHandle ?? undefined)
            addEdgeToYDoc({
              source: e.source,
              target: e.target,
              sourceHandle: e.sourceHandle ?? undefined,
              targetHandle: e.targetHandle ?? undefined,
              toIndex: e.data?.toIndex,
              fromIndex: e.data?.fromIndex,
            })
          }
          for (const port of affectedPorts) {
            flushResolvedPortData(
              port.target,
              port.targetHandle,
              portEdgeRefsForTarget(edgesRef.current, port.target, port.targetHandle),
            )
          }
          nodeOpsLog({
            event: 'duplicate-node',
            feature: 'canvas:node-ops',
            sourceNodeId: nodeId,
            newNodeId: newId,
            nodeType: sourceNode.type,
            edgeCount: newEdges.length,
          })
        }
        nodeDuplicateLog({
          event: 'duplicate-node',
          feature: 'node-duplicate',
          trace_id: traceId,
          projectId,
          result: 'created',
          sourceNodeId: nodeId,
          newNodeId: newId,
          nodeType: sourceNode.type,
          mode: options.mode,
          nameField: prepared.nameField,
          copyName: options.name,
          collapsedFieldCount: prepared.report?.fields.length ?? 0,
          nameAutogenCleared: prepared.nameAutogenCleared,
          edgeCount: newEdges.length,
          syncedToYDoc,
        })

        return newId
      },

      updateNodeData: <T extends FeNodeType>(nodeRef: { id: string; type: T }, updates: NodeDataRecord<T>) => {
        const nodeId = nodeRef.id
        const existingNode = nodesRef.current.find((n) => n.id === nodeId)
        nodeOpsLog({
          event: 'update-node-data-requested',
          feature: 'canvas:node-ops',
          nodeId,
          nodeType: existingNode?.type ?? nodeRef.type,
          fields: Object.keys(updates),
        })
        if (!ydoc.getMap('nodes').has(nodeId)) {
          const wasRecentlyDeleted = recentlyDeletedNodeIdsRef.current.has(nodeId)
          if (!wasRecentlyDeleted) {
            console.error('[canvas] ignoring updateNodeData for node missing from YDoc', {
              nodeId,
              nodeType: existingNode?.type ?? nodeRef.type,
              fields: Object.keys(updates),
            })
          }
          nodeDeleteLog({
            event: 'stale-update-ignored-missing-ydoc',
            nodeId,
            nodeType: existingNode?.type ?? nodeRef.type,
            fields: Object.keys(updates),
            reason: wasRecentlyDeleted ? 'recently-deleted' : 'missing-ydoc',
          })
          return
        }
        assertNoDirectAssetInputPortWrites(
          existingNode?.type ?? nodeRef.type,
          nodeId,
          updates,
          'updateNodeData',
        )
        assertNoBackendAuthoredOutputMapWrites(
          existingNode?.type ?? nodeRef.type,
          nodeId,
          updates,
          'updateNodeData',
        )

        // 1. Immediate ReactFlow state update
        setNodes((nds) => {
          const updated = nds.map((n) =>
            n.id === nodeId ? { ...n, data: { ...n.data, ...updates } } : n
          )
          nodesRef.current = updated
          return updated
        })

        // 2. Immediate Y doc write (no-op for nodes not in Y doc)
        // YDoc is the sole persistence path for in_*/out_* fields (RFC-0328).
        writeNodeData(nodeRef, updates)
      },

      persistNodeData: writeNodeData,

      updateNodeParent: (nodeId: string, parentId: string | null) => {
        writeNodeParent(nodeId, parentId)
      },

      // Run several YDoc writes inside ONE 'frontend' transaction so observers
      // see a single, consistent update. Used by the GroupNode ReferenceSelector
      // absorb to write in_child_node_ids + each child's parent link atomically
      // (T275550498); mirrors canvasDragMethods.transact used by the drag absorb.
      transact: (fn: () => void, origin: 'frontend' | 'ref-sync' = 'frontend') => {
        ydoc.transact(fn, origin)
      },

      executeNode: (nodeId: string) => {
        const node = nodesRef.current.find((n) => n.id === nodeId)
        if (node) {
          handleExecuteNode(node)
        } else {
          console.warn(`[WorkspaceV2] executeNode: Node not found: ${nodeId}`)
        }
      },

      getNode: (nodeId: string) => nodesRef.current.find((n) => n.id === nodeId),
      getNodes: () => nodesRef.current,
      getYNode: (nodeId: string) => {
        const yNodes = ydoc.getMap<Y.Map<unknown>>('nodes')
        return yNodes.get(nodeId) as RawCanvasYNode | undefined
      },
      updateNodePosition: writeNodePosition,

      replacePortEdges: (
        targetId: string,
        targetHandle: string,
        newEdges: Array<{
          source: string
          sourceHandle: string
          toIndex?: number
          fromIndex?: number
          referencePairKind?: ReferencePairKind
        }>
      ) => {
        const yEdges = ydoc.getMap<RawCanvasYNode>('edges')
        const yNodes = ydoc.getMap<RawCanvasYNode>('nodes')
        const targetNode = nodesRef.current.find((node) => node.id === targetId)
        refSyncLog({
          event: 'replace-port-edges-requested',
          trace_id: referenceSyncTraceId(targetId),
          projectId,
          targetId,
          targetType: targetNode?.type,
          targetHandle,
          edgeCount: newEdges.length,
          edgeRefs: summarizeEdgeRefs(newEdges, targetId, targetHandle),
          fromNodeIds: newEdges.map((edge) => edge.source).join(','),
          fromHandles: newEdges.map((edge) => edge.sourceHandle).join(','),
          toIndexes: newEdges.map((edge, index) => edge.toIndex ?? index).join(','),
          previousValueSummary: summarizePortValue(dataValue(targetNode?.data, targetHandle)),
        })
        ydoc.transact(() => {
          // Remove all existing edges on this target port
          const toDelete: string[] = []
          yEdges.forEach((_yedge, edgeId) => {
            const targetValue = _yedge.get('target')
            const targetHandleValue = _yedge.get('to_port')
            const tgt = typeof targetValue === 'string' ? targetValue : undefined
            const tgtH = typeof targetHandleValue === 'string' ? targetHandleValue : undefined
            if (tgt === targetId && tgtH === targetHandle) {
              toDelete.push(edgeId)
            }
          })
          for (const edgeId of toDelete) {
            yEdges.delete(edgeId)
          }
          // Add new edges
          newEdges.forEach((e, i) => {
            const toIndex = e.toIndex ?? i
            const edgeId = `${e.source}.${e.sourceHandle}-${targetId}.${targetHandle}[${toIndex}]`
            const edgeData = new Y.Map<ReactFlowFieldValue>() as RawCanvasYNode
            edgeData.set('source', e.source)
            edgeData.set('target', targetId)
            edgeData.set('type', edgeTypeForReferencePairKind(e.referencePairKind))
            edgeData.set('from_port', e.sourceHandle)
            edgeData.set('to_port', targetHandle)
            edgeData.set('toIndex', toIndex)
            if (e.fromIndex !== undefined) edgeData.set('fromIndex', e.fromIndex)
            yEdges.set(edgeId, edgeData)
          })
          syncAutoSeededVideoEditorTimeline(yNodes, yEdges, targetId)
          refSyncLog({
            event: 'replace-port-edges-applied',
            trace_id: referenceSyncTraceId(targetId),
            projectId,
            targetId,
            targetType: targetNode?.type,
            targetHandle,
            deletedCount: toDelete.length,
            addedCount: newEdges.length,
            edgeRefs: summarizeEdgeRefs(newEdges, targetId, targetHandle),
            fromNodeIds: newEdges.map((edge) => edge.source).join(','),
            fromHandles: newEdges.map((edge) => edge.sourceHandle).join(','),
            toIndexes: newEdges.map((edge, index) => edge.toIndex ?? index).join(','),
          })
        }, 'ref-sync')
        flushResolvedPortData(targetId, targetHandle, newEdges)
        // ReactFlow state updates via the YDoc observer (rebuild).
        // Using 'ref-sync' origin (not 'frontend') so the observer fires and syncs.
      },
    }),
    [ydocRunningCount, ydocQueuedCount, ydocDirectorRunning, ydocStatusLive, projectId, project?.updated_at, workspaceStyleId, projectVideoResolution, projectAspectRatio, defaultImageModel, setNodes, setEdges, handleExecuteNode, addNodeToYDoc, addEdgeToYDoc, removeNodeFromYDoc, writeNodeData, writeNodeParent, writeNodePosition, ydoc, flushResolvedPortData, requestNodeDelete, requestNodesDelete, scheduleDeletedReactFlowPrune, canvasRoomKey, roomEpoch, undoHistory, t]
  )

  const handleCancelNodeDelete = useCallback(() => {
    const pending = pendingNodeDeleteRef.current
    if (pending) {
      nodeDeleteConfirmLog({
        event: 'delete-cancelled',
        feature: 'canvas-node-delete',
        trace_id: pending.traceId,
        trigger: pending.trigger,
        nodeIdsCsv: pending.nodeIds.join(','),
      })
    }
    pendingNodeDeleteRef.current = null
    setPendingNodeDelete(null)
  }, [])

  const handleConfirmNodeDelete = useCallback(() => {
    const pending = pendingNodeDeleteRef.current
    if (!pending) return
    pendingNodeDeleteRef.current = null
    setPendingNodeDelete(null)
    nodeDeleteConfirmLog({
      event: 'delete-confirmed',
      feature: 'canvas-node-delete',
      trace_id: pending.traceId,
      trigger: pending.trigger,
      count: pending.nodeIds.length,
      nodeIdsCsv: pending.nodeIds.join(','),
    })
    noteNodeActions(pending.nodeIds, 'delete', NODE_DELETE_TRIGGER_SOURCE[pending.trigger])

    if (pending.nodeIds.length === 1) {
      canvasContextValue.removeNode(pending.nodeIds[0])
      return
    }
    if (canvasContextValue.removeNodes) {
      canvasContextValue.removeNodes(pending.nodeIds)
      return
    }
    for (const nodeId of pending.nodeIds) {
      canvasContextValue.removeNode(nodeId)
    }
  }, [canvasContextValue])

  // ─── Undo/Redo ────────────────────────────────────────────────────────────

  const recordForUndo = useCallback(() => {
    if (isUndoRedo.current) return
    undoHistory.record(canvasRoomKey, roomEpoch, { nodes: ydocSafeSnapshotNodes(nodesRef.current), edges: [...edgesRef.current] })
  }, [canvasRoomKey, roomEpoch, undoHistory])


  const syncStateDiffToBackend = useCallback(
    (oldNodes: WorkspaceNode[], oldEdges: WorkspaceEdge[], newNodes: WorkspaceNode[], newEdges: WorkspaceEdge[]) => {
      if (!projectId) return

      const oldNodeIds = new Set(oldNodes.map((n) => n.id))
      const newNodeIds = new Set(newNodes.map((n) => n.id))

      // 1. Restored nodes — must come before edges so endpoints exist in YDoc.
      const restoredNodes = newNodes.filter((n) => !oldNodeIds.has(n.id))
      for (const n of restoredNodes) {
        if (shouldSyncToBackend(n.type ?? '')) {
          const ydocData = cloneDataForCopiedEdges(
            n.type,
            n.data ?? {},
          )
          addNodeToYDoc(
            n.id,
            n.type ?? '',
            firstStringDataField(n.data, ['label', 'name']) ?? n.id,
            n.position.x,
            n.position.y,
            ydocData,
            true
          )
          // Re-establish parent link for nodes that lived inside a group
          // (e.g. storyboard panels). Without this, the panel's position is
          // interpreted as canvas-absolute on next YDoc rebuild and the
          // panel renders detached from its group.
          if (n.parentId) {
            writeNodeParent(n.id, n.parentId)
          }
        }
      }

      // 2. Edge diff. Key on (source, sourceHandle, target, targetHandle,
      // fromIndex, toIndex) — not Edge.id, since IDs come from multiple
      // non-deterministic sources (paste-${ts}, dup-${ts},
      // ${source}-${target}-${ts}, auto). The YDoc edge ID is deterministic
      // from the port tuple, so this key matches YDoc's notion of edge
      // identity.
      //
      // Shared with the undo history's changed-while-parked fingerprint
      // (`edgeReplayKey`), so the digest can never disagree with the diff it
      // exists to guard. Fingerprinting ReactFlow ids instead would both miss a
      // `targetHandle`/`toIndex` change and report a phantom change when a cold
      // rebuild replaces a `paste-edge-*` id with the canonical one.
      const edgeKey = edgeReplayKey
      const oldEdgeKeys = new Set(oldEdges.map(edgeKey))
      const newEdgeKeys = new Set(newEdges.map(edgeKey))

      const addedEdges = newEdges.filter((e) => !oldEdgeKeys.has(edgeKey(e)))
      const removedEdges = oldEdges.filter((e) => !newEdgeKeys.has(edgeKey(e)))
      const affectedPorts: TargetPortRef[] = []

      for (const e of addedEdges) {
        const toIndex = e.data?.toIndex
        const fromIndex = e.data?.fromIndex
        rememberTargetPort(affectedPorts, e.target, e.targetHandle ?? undefined)
        addEdgeToYDoc({
          source: e.source,
          target: e.target,
          sourceHandle: e.sourceHandle ?? undefined,
          targetHandle: e.targetHandle ?? undefined,
          toIndex,
          fromIndex,
          type: e.type,
        })
        undoLog('edge-restore source=%s target=%s sourceHandle=%s targetHandle=%s fromIndex=%o toIndex=%o',
          e.source, e.target, e.sourceHandle, e.targetHandle, fromIndex, toIndex)
      }

      // 3. Restore parent group's in_child_node_ids. Only nodeGroup carries
      // this slot map; deletion of a child compacts the slot which the undo
      // snapshot still has intact.
      const oldNodeById = new Map(oldNodes.map((n) => [n.id, n]))
      for (const n of newNodes) {
        if (n.type !== 'nodeGroup') continue
        const oldNode = oldNodeById.get(n.id)
        if (!oldNode) continue
        const oldChildIds = childNodeIds(oldNode.data)
        const newChildIds = childNodeIdsField(n.data)
        if (newChildIds === undefined) continue
        const equal =
          oldChildIds.length === newChildIds.length &&
          oldChildIds.every((id, i) => id === newChildIds[i])
        if (!equal) {
          writeNodeData({ id: n.id, type: 'nodeGroup' }, { in_child_node_ids: newChildIds })
          undoLog('parent-restore parentId=%s newChildIds=%o', n.id, newChildIds)
        }
      }

      // 4. Removed edges. Batch into a single transaction.
      if (removedEdges.length > 0) {
        for (const e of removedEdges) {
          rememberTargetPort(affectedPorts, e.target, e.targetHandle ?? undefined)
        }
        removeEdgesFromYDocByPorts(
          removedEdges.map((e) => ({
            source: e.source,
            target: e.target,
            sourceHandle: e.sourceHandle ?? undefined,
            targetHandle: e.targetHandle ?? undefined,
          })),
        )
        for (const e of removedEdges) {
          undoLog('edge-remove source=%s target=%s', e.source, e.target)
        }
      }

      for (const port of affectedPorts) {
        flushResolvedPortData(
          port.target,
          port.targetHandle,
          portEdgeRefsForTarget(newEdges, port.target, port.targetHandle),
        )
      }

      // 5. Removed nodes. removeNodeFromYDoc cascades to delete touching
      // edges, which is fine since we already handled edge-only removals
      // above.
      const removedNodes = oldNodes.filter((n) => !newNodeIds.has(n.id))
      // Keep the removed nodes' generated assets listed in the Download Assets
      // modal. That modal projects the LIVE node list, so without this an undo
      // makes the image look deleted even though the file is untouched in GCS.
      // Captured before removal, while the node data still holds the paths.
      rememberRemovedAssets(canvasRoomKey, buildDownloadCandidates(removedNodes))
      const removedNodeIds = removedNodes.map((n) => n.id)
      for (const id of removedNodeIds) {
        removeNodeFromYDoc(id)
      }

      // 6. Parent changes for nodes present in both snapshots.
      //
      // Pre-existing gap, not introduced by the per-canvas work: `writeNodeParent`
      // ran only for RE-ADDED nodes, so dragging an existing child from one group
      // to another and undoing put React state and `in_child_node_ids` back while
      // YDoc kept the new parent — and the next rebuild reversed the undo. The
      // parent is part of the replay's own changed-while-parked fingerprint, so
      // leaving it unwritten here was inconsistent with what the guard measures.
      const oldParents = new Map(oldNodes.map((n) => [n.id, n.parentId ?? null]))
      for (const n of newNodes) {
        if (!oldParents.has(n.id)) continue
        const nextParent = n.parentId ?? null
        if (oldParents.get(n.id) !== nextParent) {
          writeNodeParent(n.id, nextParent)
        }
      }

      // 7. Position changes for nodes present in both snapshots.
      const oldPositions = new Map(oldNodes.map((n) => [n.id, n.position]))
      for (const n of newNodes) {
        const oldPos = oldPositions.get(n.id)
        if (
          oldPos &&
          (Math.abs(oldPos.x - n.position.x) > 0.5 || Math.abs(oldPos.y - n.position.y) > 0.5)
        ) {
          writeNodePosition(n.id, n.position.x, n.position.y)
        }
      }
    },
    [projectId, addNodeToYDoc, removeNodeFromYDoc, writeNodePosition, addEdgeToYDoc, removeEdgesFromYDocByPorts, writeNodeData, writeNodeParent, flushResolvedPortData, canvasRoomKey]
  )

  /**
   * Apply a peeked history entry, then commit it.
   *
   * Shared by undo and redo because every guard below has to hold for both, and
   * the previous copy-paste pair had already started to drift.
   *
   * Two things are deliberate here.
   *
   * The entry is only COMMITTED after the snapshot has been applied. The store
   * used to pop the entry and push the replacement before the caller's own
   * guards ran, so a late refusal left a spurious redo entry and a lost undo
   * entry with nothing applied. Note this is not transactional — `syncStateDiff\
   * ToBackend` is synchronous `void` with no backend acknowledgement, so
   * "committed" means the local YDoc writes returned without throwing, not that
   * anything reached the server.
   *
   * And the snapshot supplies TOPOLOGY, not stale outputs, for entities that
   * still exist.
   *
   * For NODES: `flushResolvedPortData` resolves a target's `in_*` by reading
   * `nodesRef.current`, which is the snapshot by the time it runs — so a replay
   * that re-adds an edge would otherwise write the SNAPSHOT's `out_*` into the
   * live downstream input, rolling back a generation the user never asked to
   * undo. Live data therefore wins for a surviving node — EXCEPT where the
   * snapshot's group membership differs, because `in_child_node_ids` is a field
   * the replay is genuinely supposed to restore: taking live data there would
   * make the membership diff a no-op and leave a restored child parented to a
   * group that does not list it.
   *
   * For EDGES: the live edge object wins when an edge with the same replay key
   * is still present, so a cold rebuild's canonical YDoc edge id is not
   * replaced by the snapshot's synthetic `paste-…`/`dup-…` id. The semantic
   * diff would report no change, leaving ReactFlow and YDoc disagreeing about
   * the id and a later id-based removal targeting an edge the backend no longer
   * knows by that name.
   *
   * Snapshot data is used as-is for anything being RE-ADDED, where it is the
   * only data there is.
   */
  const applyHistoryCandidate = useCallback(
    (candidate: UndoCandidate<WorkspaceNode, WorkspaceEdge>) => {
      const currentNodes = ydocSafeSnapshotNodes(nodesRef.current)
      const currentEdges = [...edgesRef.current]
      const membershipOf = (data: WorkspaceNode['data']) =>
        JSON.stringify(dataValue(data, 'in_child_node_ids') ?? null)
      const liveNodeById = new Map(nodesRef.current.map((n) => [n.id, n]))
      const nextNodes = candidate.snapshot.nodes.map((n) => {
        const live = liveNodeById.get(n.id)
        if (!live) return n
        const snapshotMembership = dataValue(n.data, 'in_child_node_ids')
        if (snapshotMembership === undefined || membershipOf(live.data) === membershipOf(n.data)) {
          return { ...n, data: live.data }
        }
        // Membership is the one field a replay genuinely has to put back, but
        // everything else on this node is newer than the snapshot. Falling back
        // to the whole snapshot `data` here would restore a stale `out_*` on the
        // group — and an edge restored by the same replay would then flush that
        // stale output downstream, which is the defect the live-data preference
        // exists to prevent.
        return {
          ...n,
          data: { ...live.data, in_child_node_ids: snapshotMembership } as WorkspaceNode['data'],
        }
      })
      const liveEdgeByKey = new Map(edgesRef.current.map((e) => [edgeReplayKey(e), e]))
      const nextEdges = candidate.snapshot.edges.map(
        (e) => liveEdgeByKey.get(edgeReplayKey(e)) ?? e,
      )

      beginReplaySuppression()
      try {
        nodesRef.current = nextNodes
        edgesRef.current = nextEdges
        setNodes(nextNodes)
        setEdges(nextEdges)
        syncStateDiffToBackend(currentNodes, currentEdges, nextNodes, nextEdges)
      } catch (error) {
        // A throw leaves the canvas half-applied, which is bad, but leaving
        // recording suppressed for the rest of the session is worse — that is
        // silent, and it is the shape of the bug being fixed here.
        endReplaySuppression()
        throw error
      }
      undoHistory.commit(candidate, { nodes: currentNodes, edges: currentEdges })
    },
    [
      beginReplaySuppression,
      endReplaySuppression,
      setEdges,
      setNodes,
      syncStateDiffToBackend,
      undoHistory,
    ],
  )

  /**
   * Is it safe to replay onto the canvas currently on screen?
   *
   * `canvasInteractive` is the gate rather than a bare room-name comparison: a
   * freshly acquired document reports the new room immediately while still
   * unsynced, and ReactFlow can still be holding the previous canvas's graph,
   * so a name check alone would let a snapshot be written into a half-loaded
   * canvas. `canvasInteractive` additionally requires the rendered room, the
   * connection, the sync and the absence of a terminal error.
   */
  const canReplayHistory = useCallback(
    (candidate: UndoCandidate<WorkspaceNode, WorkspaceEdge>, direction: 'undo' | 'redo') => {
      // The changed-while-parked check runs in a PASSIVE effect, so a Cmd+Z
      // landing between "canvas became interactive" and that effect flushing
      // would find the room and epoch matching and replay a snapshot against a
      // canvas that moved while it was parked. Require this activation's own
      // check to have completed. The latch carries the room AND the epoch and is
      // cleared on leaving a room, so a previous activation's pass cannot
      // unlock replay here.
      // FIRST, before any "try again" answer. A terminal Yjs error never
      // resolves, and it can strike before the canvas ever becomes interactive
      // — in which case the restore latch is never set and the pending-check
      // branch below would fire, telling the user to retry something that
      // cannot succeed.
      if (yjsError != null) {
        undoScopeLog({
          event: 'replay-refused-canvas-not-interactive',
          recordedOnCanvasRoomKey: candidate.snapshot.roomKey,
          nowOnCanvasRoomKey: canvasRoomKey,
          direction,
        })
        return 'not-replayable'
      }
      if (undoRestoreCheckedRef.current !== `${canvasRoomKey}#${roomEpoch}`) {
        undoScopeLog({
          event: 'replay-refused-restore-check-pending',
          recordedOnCanvasRoomKey: candidate.snapshot.roomKey,
          nowOnCanvasRoomKey: canvasRoomKey,
          direction,
        })
        return 'canvas-loading'
      }
      if (!canvasInteractive) {
        undoScopeLog({
          event: 'replay-refused-canvas-not-interactive',
          recordedOnCanvasRoomKey: candidate.snapshot.roomKey,
          nowOnCanvasRoomKey: canvasRoomKey,
          direction,
        })
        return 'canvas-loading'
      }
      // Second application of the store's own predicate, here on the
      // destructive boundary itself: syncStateDiffToBackend deletes every node
      // the snapshot lacks, so nothing reaches it that was not recorded on the
      // canvas we are standing on, in the generation we are standing in
      // (T288688966). Peek does not mutate, so refusing costs nothing.
      if (!isSnapshotReplayable(candidate.snapshot, canvasRoomKey, roomEpoch)) {
        undoScopeLog({
          event: 'replay-refused-foreign-room',
          recordedOnCanvasRoomKey: candidate.snapshot.roomKey,
          nowOnCanvasRoomKey: canvasRoomKey,
          direction,
        })
        return 'not-replayable'
      }
      return null
    },
    [canvasInteractive, canvasRoomKey, roomEpoch, yjsError],
  )

  /**
   * Tell the user WHY a Cmd+Z did nothing.
   *
   * Undo can now decline for several good reasons — the canvas changed while
   * they were on another one, it was restored to a different version, it is
   * still loading. All of them look identical from the keyboard: nothing
   * happens. That is the exact complaint T289182224 was filed about, so a
   * refusal the user cannot account for reads as the bug returning.
   *
   * Deliberately silent for an ordinary EMPTY history. "Nothing left to undo"
   * needs no announcement, and toasting it would train people to ignore the
   * toast that matters.
   *
   * One stable toast id, so holding Cmd+Z replaces the message instead of
   * stacking a column of them.
   */
  const explainRefusedReplay = useCallback(
    (refusal: ReplayRefusal) => {
      toast.info(t(refusalMessageKey(refusal)), { id: 'canvas-undo-refused' })
    },
    [t],
  )

  /**
   * Explain a peek that handed back nothing.
   *
   * Three different situations look identical here. The store REFUSED an entry
   * it holds (wrong room or generation — it applies the same predicate the page
   * does, so this is the only place those refusals are observable); the history
   * was DISCARDED earlier and the user has not been told; or there is simply
   * nothing left, which needs no announcement.
   */
  const explainEmptyPeek = useCallback(
    (direction: 'undo' | 'redo') => {
      const held = undoHistory.depth(canvasRoomKey)
      const remaining = direction === 'undo' ? held.undo : held.redo
      if (remaining > 0) {
        // Entries exist but were not handed back — a refusal, not an empty stack.
        explainRefusedReplay('not-replayable')
        return
      }
      const dropped = undoHistory.lastDropReason(canvasRoomKey)
      if (dropped) explainRefusedReplay(dropped)
    },
    [canvasRoomKey, explainRefusedReplay, undoHistory],
  )

  const handleUndo = useCallback(() => {
    const candidate = undoHistory.peekUndo(canvasRoomKey, roomEpoch)
    if (!candidate) {
      explainEmptyPeek('undo')
      return
    }
    const refusal = canReplayHistory(candidate, 'undo')
    if (refusal) {
      explainRefusedReplay(refusal)
      return
    }
    applyHistoryCandidate(candidate)
  }, [
    applyHistoryCandidate,
    canReplayHistory,
    canvasRoomKey,
    explainEmptyPeek,
    explainRefusedReplay,
    roomEpoch,
    undoHistory,
  ])

  const handleRedo = useCallback(() => {
    const candidate = undoHistory.peekRedo(canvasRoomKey, roomEpoch)
    if (!candidate) {
      explainEmptyPeek('redo')
      return
    }
    const refusal = canReplayHistory(candidate, 'redo')
    if (refusal) {
      explainRefusedReplay(refusal)
      return
    }
    applyHistoryCandidate(candidate)
  }, [
    applyHistoryCandidate,
    canReplayHistory,
    canvasRoomKey,
    explainEmptyPeek,
    explainRefusedReplay,
    roomEpoch,
    undoHistory,
  ])

  const recordArrangementUndo = useCallback(() => {
    const previous = undoHistory.peekUndo(canvasRoomKey, roomEpoch)
    recordForUndo()
    const candidate = undoHistory.peekUndo(canvasRoomKey, roomEpoch)
    if (!candidate || candidate.snapshot === previous?.snapshot) return null
    const isCurrent = () => undoHistory.peekUndo(canvasRoomKey, roomEpoch)?.snapshot === candidate.snapshot
    return {
      isCurrent,
      restore: () => {
        // Check the exact stack head BEFORE replay writes anything; commit's
        // own stale-entry check happens after application and cannot protect it.
        if (!isCurrent() || canReplayHistory(candidate, 'undo')) return false
        applyHistoryCandidate(candidate)
        return true
      },
    }
  }, [undoHistory, canvasRoomKey, roomEpoch, recordForUndo, canReplayHistory, applyHistoryCandidate])

  const { arrange, arranging, awaitingDecision, keepArrangement, restoreArrangement } = useArrangeCanvas({ projectId, enabled: canvasInteractive && project?.is_demo !== true, canvasId: activeCanvasId, ydoc, instance: reactFlowInstance, recordForUndo: recordArrangementUndo, writeNodePosition })
  // Arrange rejects its plan if measured media dimensions change mid-request, so
  // it is unavailable until the room is synced and canvas media has settled.
  const canvasMediaLoading = useSyncExternalStore(subscribeCanvasMediaLoading, isCanvasMediaLoading)
  const arrangeLoading = !canvasInteractive || canvasMediaLoading

  // ─── Copy / Paste / Duplicate ─────────────────────────────────────────────

  const clipboardRef = useRef<{ nodes: WorkspaceNode[]; edges: WorkspaceEdge[] }>({
    nodes: [],
    edges: [],
  })

  const setCanvasClipboard = useCallback((nextClipboard: { nodes: WorkspaceNode[]; edges: WorkspaceEdge[] }) => {
    clipboardRef.current = nextClipboard
  }, [])

  const getSelectedNodes = useCallback(() => {
    const selectedIds = selectedNodeIdsRef.current
    return nodesRef.current.filter((node) => node.selected || selectedIds.has(node.id))
  }, [])

  const handleCopy = useCallback(() => {
    const selectedNodes = getSelectedNodes()
    if (selectedNodes.length === 0) return
    const selectedIds = new Set(selectedNodes.map((n) => n.id))
    const relevantEdges = edgesRef.current.filter(
      (e) => selectedIds.has(e.target) || (selectedIds.has(e.source) && selectedIds.has(e.target))
    )
    setCanvasClipboard({ nodes: ydocSafeSnapshotNodes(selectedNodes), edges: relevantEdges })
  }, [getSelectedNodes, setCanvasClipboard])

  const handlePaste = useCallback((source: NodeMutationSource = 'paste') => {
    const clipboard = clipboardRef.current
    if (clipboard.nodes.length === 0) return
    recordForUndo()

    const oldToNew = new Map<string, string>()
    const newNodes: WorkspaceNode[] = clipboard.nodes.map((node, index) => {
      const newId = `${node.type}-${Date.now()}-${index}`
      oldToNew.set(node.id, newId)
      const clonedData = cloneDataForCopiedEdges(
        node.type,
        ydocSafeCloneNodeData(node.data),
      )
      return {
        ...node,
        id: newId,
        position: {
          x: node.position.x + PASTE_OFFSET,
          y: node.position.y + PASTE_OFFSET,
        },
        selected: true,
        data: {
          ...clonedData,
          projectId,
          status: 'idle',
          progress: undefined,
          progressMessage: undefined,
          error: undefined,
        },
      }
    })

    const newEdges: WorkspaceEdge[] = clipboard.edges.map((edge, index) => ({
      ...edge,
      id: `paste-edge-${Date.now()}-${index}`,
      source: oldToNew.get(edge.source) ?? edge.source,
      target: oldToNew.get(edge.target)!,
    }))
    noteNodeActions(newNodes.map((n) => n.id), 'add', source)

    setNodes((nds) => {
      const updated = [...nds.map((n) => ({ ...n, selected: false })), ...newNodes]
      nodesRef.current = updated
      return updated
    })
    if (newEdges.length > 0) {
      setEdges((eds) => {
        const updated = [...eds, ...newEdges]
        edgesRef.current = updated
        return updated
      })
    }

    // Sync to Y doc + backend
    for (const n of newNodes) {
      if (shouldSyncToBackend(n.type ?? '')) {
        const ydocData = cloneDataForCopiedEdges(
          n.type,
          n.data ?? {},
        )
        addNodeToYDoc(
          n.id,
          n.type ?? '',
          firstStringDataField(n.data, ['label', 'name']) ?? n.id,
          n.position.x,
          n.position.y,
          ydocData,
          true
        )
      }
    }
    const pasteAffectedPorts: TargetPortRef[] = []
    for (const e of newEdges) {
      rememberTargetPort(pasteAffectedPorts, e.target, e.targetHandle ?? undefined)
      addEdgeToYDoc({
        source: e.source,
        target: e.target,
        sourceHandle: e.sourceHandle ?? undefined,
        targetHandle: e.targetHandle ?? undefined,
        toIndex: e.data?.toIndex,
        fromIndex: e.data?.fromIndex,
      })
    }
    for (const port of pasteAffectedPorts) {
      flushResolvedPortData(
        port.target,
        port.targetHandle,
        portEdgeRefsForTarget(edgesRef.current, port.target, port.targetHandle),
      )
    }
    nodeOpsLog({
      event: 'paste-nodes',
      feature: 'canvas:node-ops',
      nodeCount: newNodes.length,
      edgeCount: newEdges.length,
    })

    const newSelectedIds = new Set(newNodes.map((node) => node.id))
    selectedNodeIdsRef.current = newSelectedIds
    setSelectedNodeIds(newSelectedIds)

    // Update clipboard for sequential pastes
    setCanvasClipboard({ nodes: newNodes, edges: newEdges })
  }, [setNodes, setEdges, recordForUndo, projectId, addNodeToYDoc, addEdgeToYDoc, flushResolvedPortData, setSelectedNodeIds, setCanvasClipboard])

  const handleDuplicate = useCallback((source: NodeMutationSource = 'keyboard') => {
    const selectedNodes = getSelectedNodes()
    if (selectedNodes.length === 0) return
    recordForUndo()

    const selectedIds = new Set(selectedNodes.map((n) => n.id))
    const relevantEdges = edgesRef.current.filter(
      (e) => selectedIds.has(e.target) || (selectedIds.has(e.source) && selectedIds.has(e.target))
    )

    const oldToNew = new Map<string, string>()
    const newNodes: WorkspaceNode[] = selectedNodes.map((node, index) => {
      const newId = `${node.type}-${Date.now()}-${index}`
      oldToNew.set(node.id, newId)
      const clonedData = cloneDataForCopiedEdges(
        node.type,
        ydocSafeCloneNodeData(node.data),
      )
      return {
        ...node,
        id: newId,
        position: {
          x: node.position.x + PASTE_OFFSET,
          y: node.position.y + PASTE_OFFSET,
        },
        selected: true,
        data: {
          ...clonedData,
          projectId,
          status: 'idle',
          progress: undefined,
          progressMessage: undefined,
          error: undefined,
        },
      }
    })

    const newEdges: WorkspaceEdge[] = relevantEdges.map((edge, index) => ({
      ...edge,
      id: `dup-edge-${Date.now()}-${index}`,
      source: oldToNew.get(edge.source) ?? edge.source,
      target: oldToNew.get(edge.target)!,
    }))
    noteNodeActions(newNodes.map((n) => n.id), 'duplicate', source)

    setNodes((nds) => {
      const updated = [...nds.map((n) => ({ ...n, selected: false })), ...newNodes]
      nodesRef.current = updated
      return updated
    })
    if (newEdges.length > 0) {
      setEdges((eds) => {
        const updated = [...eds, ...newEdges]
        edgesRef.current = updated
        return updated
      })
    }

    // Sync to Y doc + backend
    for (const n of newNodes) {
      if (shouldSyncToBackend(n.type ?? '')) {
        const ydocData = cloneDataForCopiedEdges(
          n.type,
          n.data ?? {},
        )
        addNodeToYDoc(
          n.id,
          n.type ?? '',
          firstStringDataField(n.data, ['label', 'name']) ?? n.id,
          n.position.x,
          n.position.y,
          ydocData,
          true
        )
      }
    }
    const dupAffectedPorts: TargetPortRef[] = []
    for (const e of newEdges) {
      rememberTargetPort(dupAffectedPorts, e.target, e.targetHandle ?? undefined)
      addEdgeToYDoc({
        source: e.source,
        target: e.target,
        sourceHandle: e.sourceHandle ?? undefined,
        targetHandle: e.targetHandle ?? undefined,
        toIndex: e.data?.toIndex,
        fromIndex: e.data?.fromIndex,
      })
    }
    for (const port of dupAffectedPorts) {
      flushResolvedPortData(
        port.target,
        port.targetHandle,
        portEdgeRefsForTarget(edgesRef.current, port.target, port.targetHandle),
      )
    }
    nodeOpsLog({
      event: 'duplicate-nodes',
      feature: 'canvas:node-ops',
      nodeCount: newNodes.length,
      edgeCount: newEdges.length,
    })

    const newSelectedIds = new Set(newNodes.map((node) => node.id))
    selectedNodeIdsRef.current = newSelectedIds
    setSelectedNodeIds(newSelectedIds)
  }, [getSelectedNodes, setNodes, setEdges, recordForUndo, projectId, addNodeToYDoc, addEdgeToYDoc, flushResolvedPortData, setSelectedNodeIds])

  // ─── onNodesChange (undo recording + dimension sync + backend removal) ────

  const onNodesChange = useCallback(
    (changes: Parameters<typeof onNodesChangeBase>[0]) => {
      // Invariant for reset reconciliation: ReactFlow v11 calls this handler
      // synchronously right after `getNodes()` fed the caller's setNodes
      // payload, so reading the store first yields the exact snapshot objects
      // the reset items were derived from (identity is how untouched nodes are
      // detected). Read it before anything else in this handler.
      const resetSnapshot: WorkspaceNode[] = changes.some((c) => c.type === 'reset')
        ? reactFlowStore.getState().getNodes()
        : []
      const ydocNodes = ydoc.getMap('nodes')
      const staleChangeIds = changes
        .map((change) => ('id' in change ? change.id : undefined))
        .filter((nodeId): nodeId is string =>
          typeof nodeId === 'string' &&
          recentlyDeletedNodeIdsRef.current.has(nodeId) &&
          !ydocNodes.has(nodeId)
        )
      const staleChangeIdSet = new Set(staleChangeIds)
      if (staleChangeIdSet.size > 0) {
        nodeDeleteLog({
          event: 'stale-reactflow-change-ignored',
          nodeIdsCsv: Array.from(staleChangeIdSet).join(','),
          changeTypesCsv: changes
            .filter((change) => 'id' in change && staleChangeIdSet.has(change.id))
            .map((change) => change.type)
            .join(','),
        })
        scheduleDeletedReactFlowPrune(staleChangeIdSet, 'reactflow-change-tombstone')
      }
      const liveChanges = staleChangeIdSet.size === 0
        ? changes
        : changes.filter((change) => !('id' in change) || !staleChangeIdSet.has(change.id))

      // ─── Marquee: a single writer ────────────────────────────────────
      // While a marquee is live, ReactFlow's Pane emits its own Partial-based
      // `select` changes and the subscriber below emits the corrected >=50%
      // set. Two writers meant the SUPERSET could paint for a frame before
      // the correction shrank it — the selection box visibly flashing on the
      // drags where the two disagree. Dropping ReactFlow's emission makes the
      // subscriber the only source, so no uncorrected set ever reaches the
      // DOM. Everything else in the change list (position, dimensions,
      // removal) still flows through untouched.
      const marqueeLive =
        reactFlowStore.getState().userSelectionActive && !applyingMarqueeSelectionRef.current
      const droppedSelects = marqueeLive
        ? liveChanges.filter((change) => change.type === 'select')
        : []
      if (droppedSelects.length > 0 && marqueeLogOn.enabled) {
        marqueeLog({
          event: 'marquee-dropped-reactflow-selects',
          feature: 'canvas:marquee',
          count: droppedSelects.length,
          ids: droppedSelects
            .map((c) => ('id' in c ? `${c.id}:${'selected' in c && c.selected ? 1 : 0}` : '?'))
            .join(','),
        })
      }
      // A select change arriving while a marquee is live but NOT dropped means
      // the gate is wrong — that is the path that can select a group the
      // coverage rule never asked for.
      if (!marqueeLive) {
        const leaked = liveChanges.filter((change) => change.type === 'select')
        if (leaked.length > 0 && marqueeLogOn.enabled && reactFlowStore.getState().userSelectionRect !== null) {
          marqueeLog({
            event: 'marquee-select-leaked',
            feature: 'canvas:marquee',
            user_selection_active: reactFlowStore.getState().userSelectionActive,
            applying_ours: applyingMarqueeSelectionRef.current,
            count: leaked.length,
            ids: leaked
              .map((c) => ('id' in c ? `${c.id}:${'selected' in c && c.selected ? 1 : 0}` : '?'))
              .join(','),
          })
        }
      }
      const effectiveChanges = marqueeLive
        ? liveChanges.filter((change) => change.type !== 'select')
        : liveChanges
      if (effectiveChanges.length === 0) return

      // W2 observation point: a ReactFlow `reset` item whose label disagrees
      // with React state / YDoc is a stale store-snapshot copy (reconciled below).
      if (labelApplyLog.enabled) {
        const resetChanges = effectiveChanges.filter((c) => c.type === 'reset')
        const stateById = resetChanges.length > 0 ? new Map(nodesRef.current.map((n) => [n.id, n])) : null
        for (const change of resetChanges) {
          if (change.type !== 'reset' || !stateById) continue
          const ynode = ydocNodes.get(change.item.id)
          const ydocLabel = ynode instanceof Y.Map ? String(ynode.get('label') ?? '') : '(absent)'
          const stateLabel = String(stateById.get(change.item.id)?.data.label ?? '(absent)')
          const itemLabel = String(change.item.data.label ?? '')
          if (itemLabel !== ydocLabel || itemLabel !== stateLabel) {
            labelApplyLog({ event: 'label-reset-change', nodeId: change.item.id, itemLabel, stateLabel, ydocLabel, resetCount: resetChanges.length })
          }
        }
      }

      // ─── Multi-select: let ReactFlow's native selection flow through ──
      // Sync our selection set from ReactFlow's selection change events.
      const hasSelectionChange = effectiveChanges.some(c => c.type === 'select')


      const hasRemoval = effectiveChanges.some((c) => c.type === 'remove')
      if (hasRemoval && !isUndoRedo.current) {
        undoHistory.record(canvasRoomKey, roomEpoch, { nodes: ydocSafeSnapshotNodes(nodesRef.current), edges: [...edgesRef.current] })
      }

      // Clean up parent group's in_child_node_ids when a child node is removed
      if (hasRemoval) {
        const removedIds = effectiveChanges
          .filter((c): c is { type: 'remove'; id: string } => c.type === 'remove')
          .map((c) => c.id)

        // Check each removed node for a parentId (group membership)
        const currentNodes = nodesRef.current
        const parentUpdates = new Map<string, (string | null)[]>()

        for (const removedId of removedIds) {
          const removedNode = currentNodes.find((n) => n.id === removedId)
          if (!removedNode?.parentId) continue

          const parentId = removedNode.parentId
          const parentGroup = currentNodes.find((n) => n.id === parentId)
          if (!parentGroup || parentGroup.type !== 'nodeGroup') continue

          // Get or initialize the child IDs for this parent
          let childIds = parentUpdates.get(parentId) ?? childNodeIds(parentGroup.data)

          // Replace the removed child's ID with null (empty slot), then trim trailing nulls
          childIds = childIds.map((id) => (id === removedId ? null : id))
          while (childIds.length > 0 && childIds[childIds.length - 1] === null) {
            childIds.pop()
          }
          parentUpdates.set(parentId, childIds)
        }

        // Persist parent updates to YDoc. YDoc-first deletion: rebuild()
        // re-derives React node state from YDoc — no optimistic setNodes.
        if (parentUpdates.size > 0) {
          for (const [parentId, newChildIds] of parentUpdates) {
            writeNodeData({ id: parentId, type: 'nodeGroup' }, { in_child_node_ids: newChildIds })
          }
        }
      }

      // Let changes flow through to ReactFlow — EXCEPT removals. Removals are
      // applied YDoc-first below (removeNodeFromYDoc → rebuild), so they must
      // not be applied optimistically here.
      const passThroughChanges = hasRemoval ? effectiveChanges.filter((c) => c.type !== 'remove') : effectiveChanges
      const resetItems = passThroughChanges.flatMap((c) => (c.type === 'reset' ? [c.item] : []))
      if (resetItems.length > 0) {
        // `useReactFlow().setNodes()` (v11, controlled) computed these resets
        // from ReactFlow's internal store snapshot, which lags React state.
        // Applying them verbatim reverts any newer React update (e.g. a YDoc
        // label merge from useYCanvas). Reconcile against the latest state
        // instead — see reconcileReactFlowReset.
        setNodes((latest) => {
          const { nodes: reconciled, stats } = reconcileReactFlowReset(latest, resetSnapshot, resetItems)
          reactFlowResetLog({ event: 'reset-reconciled', items: resetItems.length, latest: latest.length, ...stats })
          nodesRef.current = reconciled
          return reconciled
        })
      }
      const otherChanges = resetItems.length > 0 ? passThroughChanges.filter((c) => c.type !== 'reset') : passThroughChanges
      if (otherChanges.length > 0) onNodesChangeBase(otherChanges)

      // After ReactFlow processes selection changes, sync our ref/state
      if (hasSelectionChange) {
        const newSelected = new Set<string>()
        for (const n of nodesRef.current) {
          if (n.selected) newSelected.add(n.id)
        }
        // Apply the pending selection changes on top
        for (const c of effectiveChanges) {
          if (c.type === 'select') {
            if (c.selected) newSelected.add(c.id)
            else newSelected.delete(c.id)
          }
        }
        selectionLog('onNodesChange count=%d selected=%o', newSelected.size, Array.from(newSelected))
        selectionNadLog({
          event: 'selection-changed',
          feature: 'canvas:selection',
          source: 'onNodesChange',
          selectedCount: newSelected.size,
          selectedIdsCsv: Array.from(newSelected).join(','),
        })
        selectedNodeIdsRef.current = newSelected
        setSelectedNodeIds(newSelected)
      }

      // Backend node removal + YDoc cleanup
      if (hasRemoval) {
        const removedIds = effectiveChanges
          .filter((c): c is { type: 'remove'; id: string } => c.type === 'remove')
          .map((c) => c.id)
        if (removedIds.length > 0) {
          for (const id of removedIds) {
            recentlyDeletedNodeIdsRef.current.add(id)
          }
          nodeOpsLog({
            event: 'reactflow-remove-nodes-requested',
            feature: 'canvas:node-ops',
            nodeIdsCsv: removedIds.join(','),
            count: removedIds.length,
            selectedIdsCsv: Array.from(selectedNodeIdsRef.current).join(','),
          })
          // YDoc-first deletion: remove from YDoc so rebuild() drops the node
          // from React state. The native remove change is filtered out above.
          for (const id of removedIds) {
            removeNodeFromYDoc(id)
          }
          nodeOpsLog({
            event: 'reactflow-remove-nodes-committed',
            feature: 'canvas:node-ops',
            nodeIdsCsv: removedIds.join(','),
            count: removedIds.length,
          })
          scheduleDeletedReactFlowPrune(removedIds, 'reactflow-remove')
        }
      }

      // Dimension sync: deliberately nothing.
      //
      // `writeNodeDimensions` has been an empty function since YDoc dimension
      // writes were removed for oscillating (write -> observe -> re-render ->
      // re-measure -> write). The call and its per-change `dimSyncLog` stayed
      // behind, and they are not free: ReactFlow emits a `dimensions` change
      // per node per frame while the canvas settles, so on a 97-node canvas
      // this ran ~5,800 console.debug calls a second whenever
      // `localStorage.debug` was set. Each one makes V8 capture a stack trace
      // with DevTools attached — a performance trace of a marquee drag showed
      // 39,359 stack captures and ~116 dropped frames per second, which is
      // what the canvas "flashing" actually was.
      //
      // Schema layout dimensions are the source of truth; nothing here needs
      // the measured values. If per-dimension tracing is ever needed again,
      // sample it rather than logging every change.
    },
    [onNodesChangeBase, setNodes, writeNodeData, removeNodeFromYDoc, scheduleDeletedReactFlowPrune, setSelectedNodeIds, ydoc, canvasRoomKey, roomEpoch, undoHistory, reactFlowStore]
  )

  // ─── Marquee coverage rule (see marqueeSelection.ts) ──────────────────
  //
  // ReactFlow cannot express "select once the box covers half the node", and
  // its own `select` changes cannot be corrected in flight either. Two facts
  // from Pane.onMouseMove (@reactflow/core 11.11.4) rule that out:
  //
  //   1. It calls `onNodesChange` BEFORE `setState({ userSelectionRect })`,
  //      so a handler reading the store sees the PREVIOUS rectangle.
  //   2. It only emits at all when the candidate COUNT changes, so growing
  //      the box from 10% to 60% over a node it already counted emits
  //      nothing — the node could never be promoted.
  //
  // So the rule is driven from the rectangle instead: subscribe to
  // `userSelectionRect`, and on every update recompute the whole desired
  // selection. This runs after ReactFlow has stored the new rectangle, so it
  // always sees the current one, and it is independent of whether ReactFlow
  // chose to emit. Changes are routed through `onNodesChange` (not
  // `onNodesChangeBase`) because that wrapper also syncs `selectedNodeIds`.
  //
  // `onMouseUp` only resets the rectangle — it never recomputes — so the last
  // update here is what the user is left with.
  useEffect(() => {
    return reactFlowStore.subscribe((state, previousState) => {
      const rect = state.userSelectionRect
      if (rect === previousState.userSelectionRect) return
      if (!rect) {
        // Marquee over: forget the asked-for set so the next drag seeds from
        // whatever is actually selected rather than from stale history.
        marqueeSelectionRef.current = null
        return
      }

      const flowRect = selectionRectToFlow(rect, state.transform)
      const candidates: MarqueeCandidate[] = []
      state.nodeInternals.forEach((internal, id) => {
        candidates.push({
          id,
          x: internal.positionAbsolute?.x ?? internal.position.x,
          y: internal.positionAbsolute?.y ?? internal.position.y,
          width: internal.width,
          height: internal.height,
          selected: Boolean(internal.selected),
          selectable: internal.selectable !== false,
          hidden: Boolean(internal.hidden),
          // ReactFlow internals can still expose the legacy property. Read it
          // as a fallback, but keep outgoing node data on parentId.
          parentId: internal.parentId ?? internal.parentNode ?? null,
        })
      })

      const desired = computeMarqueeSelection(candidates, flowRect)
      const previous =
        marqueeSelectionRef.current ??
        new Set(candidates.filter((c) => c.selected).map((c) => c.id))
      const changes = diffSelection(previous, desired)
      marqueeSelectionRef.current = desired

      if (marqueeLogOn.enabled) {
        // Logged on EVERY tick, including no-op ones: the absence of output
        // is itself the signal that our geometry is not the thing selecting.
        const groupIds = collectParentIds(candidates)
        const desiredGroups = Array.from(desired).filter((nodeId) => groupIds.has(nodeId))
        marqueeLog({
          event: 'marquee-selection-emitted',
          feature: 'canvas:marquee',
          rect: `${Math.round(flowRect.x)},${Math.round(flowRect.y)} ${Math.round(flowRect.width)}x${Math.round(flowRect.height)}`,
          candidates: candidates.length,
          measured: candidates.filter((c) => c.width != null && c.height != null).length,
          desired_count: desired.size,
          desired_groups: desiredGroups.join(',') || '(none)',
          changes: changes.map((c) => `${c.id}:${c.selected ? 1 : 0}`).join(',') || '(none)',
          group_candidates: Array.from(groupIds).join(',') || '(none)',
        })
      }
      if (changes.length === 0) return
      applyingMarqueeSelectionRef.current = true
      try {
        onNodesChange(changes.map((change) => ({ type: 'select', ...change })))
      } finally {
        applyingMarqueeSelectionRef.current = false
      }
    })
  }, [reactFlowStore, onNodesChange])

  // Tracks ReactFlow's own selection-box flags. `nodesSelectionActive` gates
  // <NodesSelection> (the amber bounding box); it is raised at mouse-up from
  // ReactFlow's Partial candidate count and cleared by pane/node clicks. If
  // the box flashes after a drag, this says what cleared it.
  useEffect(() => {
    return reactFlowStore.subscribe((state, previousState) => {
      if (
        state.nodesSelectionActive === previousState.nodesSelectionActive &&
        state.userSelectionActive === previousState.userSelectionActive
      ) {
        return
      }
      if (!marqueeBoxLogOn.enabled) return
      marqueeBoxLog({
        event: 'marquee-box-flags',
        feature: 'canvas:marquee',
        nodes_selection_active: state.nodesSelectionActive,
        user_selection_active: state.userSelectionActive,
        has_rect: state.userSelectionRect !== null,
        selected_count: state.getNodes().filter((n) => n.selected).length,
      })
    })
  }, [reactFlowStore])

  // ─── onSelectionChange: sync set from programmatic setNodes ────────
  // Catches any programmatic setNodes that modifies `selected` outside the
  // set (e.g. openPanelToTab, duplicateNode, handlePaste) and syncs the
  // ref back.
  const onSelectionChange = useCallback(({ nodes: selectedNodes }: { nodes: WorkspaceNode[] }) => {
    const ydocNodes = ydoc.getMap('nodes')
    const staleSelectedIds = selectedNodes
      .map((node) => node.id)
      .filter((nodeId) => recentlyDeletedNodeIdsRef.current.has(nodeId) && !ydocNodes.has(nodeId))
    if (staleSelectedIds.length > 0) {
      nodeDeleteLog({
        event: 'stale-selection-ignored',
        nodeIdsCsv: staleSelectedIds.join(','),
        reason: 'recently-deleted',
      })
      scheduleDeletedReactFlowPrune(staleSelectedIds, 'selection-tombstone')
    }
    const staleSelectedIdSet = new Set(staleSelectedIds)
    const newIds = new Set(
      selectedNodes
        .map((node) => node.id)
        .filter((nodeId) => !staleSelectedIdSet.has(nodeId)),
    )
    const prevIds = selectedNodeIdsRef.current
    // Avoid unnecessary updates if the sets are identical
    if (newIds.size !== prevIds.size || [...newIds].some(id => !prevIds.has(id))) {
      selectionLog('onSelectionChange count=%d selected=%o', newIds.size, Array.from(newIds))
      selectionNadLog({
        event: 'selection-changed',
        feature: 'canvas:selection',
        source: 'onSelectionChange',
        selectedCount: newIds.size,
        selectedIdsCsv: Array.from(newIds).join(','),
      })
      pulseCanvasPerformanceMode()
      selectedNodeIdsRef.current = newIds
      setSelectedNodeIds(newIds)
      const firstSelectedNode = selectedNodes.find((node) => newIds.has(node.id))
      if (firstSelectedNode) {
        emitNuxEvent({
          type: 'nodeSelected',
          nodeId: firstSelectedNode.id,
          nodeType: firstSelectedNode.type,
        })
      }
    }
  }, [emitNuxEvent, pulseCanvasPerformanceMode, scheduleDeletedReactFlowPrune, ydoc])

  const handleNodeMouseEnter = useCallback<NodeMouseHandler>(
    (_event, node) => {
      if (!renderActiveEdgesOnly) return
      setHoveredNodeId(node.id)
    },
    [renderActiveEdgesOnly, setHoveredNodeId],
  )

  const handleNodeMouseLeave = useCallback<NodeMouseHandler>(
    () => {
      if (!renderActiveEdgesOnly) return
      setHoveredNodeId(null)
    },
    [renderActiveEdgesOnly, setHoveredNodeId],
  )

  useEffect(() => {
    if (!renderActiveEdgesOnly) setHoveredNodeId(null)
  }, [renderActiveEdgesOnly, setHoveredNodeId])

  // ─── onEdgesChange (undo recording + backend removal) ─────────────────────

  const onEdgesChange = useCallback(
    (changes: Parameters<typeof onEdgesChangeBase>[0]) => {
      const hasRemoval = changes.some((c) => c.type === 'remove')
      if (hasRemoval && !isUndoRedo.current) {
        undoHistory.record(canvasRoomKey, roomEpoch, { nodes: ydocSafeSnapshotNodes(nodesRef.current), edges: [...edgesRef.current] })
      }

      // Remove edges from YDoc directly
      if (hasRemoval) {
        const removedEdgeIds = changes
          .filter((c) => c.type === 'remove')
          .map((c) => c.id)
        for (const edgeId of removedEdgeIds) {
          removeEdgeFromYDoc(edgeId)
        }
      }

      onEdgesChangeBase(changes)
    },
    [onEdgesChangeBase, removeEdgeFromYDoc, canvasRoomKey, roomEpoch, undoHistory]
  )

  // ─── onConnect ────────────────────────────────────────────────────────────

  const onConnect = useCallback(
    (params: Connection | WorkspaceEdge) => {
      const source = params.source
      const target = params.target
      if (!source || !target) return
      const sourceHandle = params.sourceHandle ?? undefined
      const requestedTargetHandle = params.targetHandle ?? undefined
      const sourceNode = nodesRef.current.find((n) => n.id === source)
      const targetNode = nodesRef.current.find((n) => n.id === target)
      if (!sourceNode || !targetNode) return
      const targetHandle = referenceConnectionTargetHandle(
        targetNode.type, target, requestedTargetHandle, edgesRef.current,
      )
      if (requiresFromIndexForConnection(
        sourceNode.type,
        sourceHandle,
        targetNode.type,
        targetHandle,
      )) return

      // Record undo
      if (!isUndoRedo.current) {
        undoHistory.record(canvasRoomKey, roomEpoch, { nodes: ydocSafeSnapshotNodes(nodesRef.current), edges: [...edgesRef.current] })
      }

      const migration = buildReferenceMediaConnectionMigration(
        targetNode.type, target, targetHandle, edgesRef.current, { source, sourceHandle },
      )
      if (migration && targetHandle) {
        ydoc.transact(() => {
          canvasContextValue.replacePortEdges(target, targetHandle, migration.updates)
          for (const port of migration.legacyPorts) canvasContextValue.replacePortEdges(target, port, [])
        }, 'ref-sync')
        referenceMediaConnectionLog({ event: 'reference-media:connections-migrated', feature: 'reference-media-order',
          node_id: target, source_id: source, target_port: targetHandle,
          migrated_count: migration.updates.length - 1, to_index: migration.updates.length - 1 })
        return
      }

      // Check array ports
      const isMultiPort = isMultiInputPort(targetNode?.type, targetHandle)
      const currentPortEdges = edgesRef.current.filter((edge) =>
        edgeTargetMatches(edge, target, targetHandle),
      )
      const toIndex = isMultiPort
        ? Math.max(-1, ...currentPortEdges.map((edge, index) => edge.data?.toIndex ?? index)) + 1
        : 0
      if (targetHandle !== requestedTargetHandle) {
        referenceMediaConnectionLog({ event: 'reference-media:connection-routed', feature: 'reference-media-order',
          node_id: target, source_id: source, requested_port: requestedTargetHandle,
          target_port: targetHandle, to_index: toIndex })
      }
      const edgeToAdd: WorkspaceEdge = {
        id: `${source}.${sourceHandle ?? ''}-${target}.${targetHandle ?? ''}[${toIndex}]`,
        source,
        target,
        type: 'default',
        data: { toIndex },
      }
      if (sourceHandle) edgeToAdd.sourceHandle = sourceHandle
      if (targetHandle) edgeToAdd.targetHandle = targetHandle

      setEdges((eds) => {
        if (isMultiPort) {
          const updated = rfAddEdge(edgeToAdd, eds)
          edgesRef.current = updated
          return updated
        }
        const filtered = eds.filter(
          (edge) => !edgeTargetMatches(edge, target, targetHandle)
        )
        const updated = rfAddEdge(edgeToAdd, filtered)
        edgesRef.current = updated
        return updated
      })

      // Write edge to YDoc directly
      // For single ports, remove any existing YDoc edge on the same target+handle
      if (!isMultiPort && currentPortEdges.length > 0) {
        removeEdgesFromYDocByPorts(
          currentPortEdges.map((edge) => ({
            source: edge.source,
            target: edge.target,
            sourceHandle: edge.sourceHandle ?? undefined,
            targetHandle: edge.targetHandle ?? undefined,
          }))
        )
      }
      addEdgeToYDoc({
        source,
        target,
        sourceHandle,
        targetHandle,
        toIndex,
      })
      // ReactFlow only calls onConnect for a user's port-to-port drag.
      if (projectId && !isUndoRedo.current) {
        trackInteraction('canvas_edge_action', {
          project_id: projectId,
          action: 'connect',
          from_node_type: sourceNode.type ? analyticsNodeType(sourceNode.type) : undefined,
          to_node_type: targetNode.type ? analyticsNodeType(targetNode.type) : undefined,
        })
      }
      flushResolvedPortData(
        target,
        targetHandle,
        isMultiPort
          ? [...currentPortEdges.map(edgeToPortRef), { source, sourceHandle, toIndex }]
          : [{ source, sourceHandle, toIndex }],
      )
    },
    [setEdges, addEdgeToYDoc, removeEdgesFromYDocByPorts, flushResolvedPortData, canvasRoomKey, roomEpoch, undoHistory, canvasContextValue, ydoc, projectId]
  )

  // ─── Connection Validation ────────────────────────────────────────────────

  const isValidConnection = useCallback(
    (connection: Connection) => {
      const { source, target, sourceHandle, targetHandle } = connection
      if (!source || !target || !sourceHandle || !targetHandle) return false

      const sourceNode = nodesRef.current.find((n) => n.id === source)
      const targetNode = nodesRef.current.find((n) => n.id === target)
      if (!sourceNode || !targetNode) return false

      const sourceSchema = sourceNode.type ? nodeSchemas[sourceNode.type] : undefined
      const targetSchema = targetNode.type ? nodeSchemas[targetNode.type] : undefined
      if (!sourceSchema || !targetSchema) return true

      const sourcePort = sourceSchema.outputs?.find(
        (o: { id: string }) => o.id === sourceHandle
      )
      const targetPort = targetSchema.inputs?.find(
        (i: { id: string }) => i.id === targetHandle
      )
      if (!sourcePort || !targetPort) return true

      return areTypesCompatible(sourcePort.type, targetPort.type)
        && !requiresFromIndexForConnection(
          sourceNode.type,
          sourceHandle,
          targetNode.type,
          targetHandle,
        )
    },
    []
  )

  // ─── Drag Handlers ────────────────────────────────────────────────────────

  const [isDraggingForSnap, setIsDraggingForSnap] = useState(false)
  const nodeDragStartPositionsRef = useRef<Map<string, { x: number; y: number }>>(new Map())

  // Armed on the first drag MOVE, not on drag start. ReactFlow fires
  // onNodeDragStart on mousedown, so a plain click is a zero-distance drag;
  // arming there put `canvas-node-dragging` (which disables node pointer
  // events) on the canvas mid-click, the click landed on the pane, and the
  // node was never selected. The ref makes the per-move call a no-op.
  const nodeDragInteractionActiveRef = useRef(false)
  const beginNodeDragInteraction = useCallback(() => {
    if (nodeDragInteractionActiveRef.current) return
    nodeDragInteractionActiveRef.current = true
    setIsDraggingForSnap(true)
    setReactFlowNodeDraggingClass(true)
  }, [setReactFlowNodeDraggingClass])

  // ReactFlow's first drag move is not proof of a drag either: with
  // nodeDragThreshold 0 and snap-to-grid on, a press that wanders 1-3px across
  // a grid line already fires onNodeDrag. Arming the class then still made the
  // pointerup land on the pane, so a slightly unsteady click on a node lost
  // its click. Wait for the same distance a pane press needs before panning.
  const nodeDragPressRef = useRef<{ x: number; y: number } | null>(null)
  const armNodeDragInteractionOnceMoved = useCallback((event: React.MouseEvent) => {
    if (nodeDragInteractionActiveRef.current) return
    const press = nodeDragPressRef.current
    let distanceSq: number | null = null
    if (press) {
      const dx = event.clientX - press.x
      const dy = event.clientY - press.y
      distanceSq = (dx * dx) + (dy * dy)
      if (distanceSq < VIEWPORT_PAN_ACTIVATION_DISTANCE_PX * VIEWPORT_PAN_ACTIVATION_DISTANCE_PX) return
    }
    canvasPointerReleaseLog({
      event: 'node-drag-armed',
      feature: 'canvas:pointer-release',
      distancePx: distanceSq === null ? null : Math.round(Math.sqrt(distanceSq)),
      nodePointerEvents: 'none',
    })
    beginNodeDragInteraction()
  }, [beginNodeDragInteraction])

  const endNodeDragInteraction = useCallback(() => {
    nodeDragInteractionActiveRef.current = false
    nodeDragPressRef.current = null
    setReactFlowNodeDraggingClass(false)
    setIsDraggingForSnap(false)
  }, [setReactFlowNodeDraggingClass])

  const onNodeDragStart = useCallback(
    (event: React.MouseEvent, node: WorkspaceNode) => {
      nodeDragPressRef.current = { x: event.clientX, y: event.clientY }
      nodeDragStartPositionsRef.current.set(node.id, { ...node.position })
      recordForUndo()
    },
    [recordForUndo]
  )

  const onSelectionDragStart = useCallback(
    (event: React.MouseEvent) => {
      nodeDragPressRef.current = { x: event.clientX, y: event.clientY }
      for (const node of getSelectedNodes()) {
        acknowledgeCanvasRegenCue(node.id, 'selection-drag')
      }
      recordForUndo()
    },
    [acknowledgeCanvasRegenCue, getSelectedNodes, recordForUndo]
  )

  const onSelectionDrag = useCallback((event: React.MouseEvent) => {
    armNodeDragInteractionOnceMoved(event)
  }, [armNodeDragInteractionOnceMoved])

  const onNodeDragStop = useCallback(
    (_event: React.MouseEvent, node: WorkspaceNode, draggedNodes: WorkspaceNode[]) => {
      const toWrite = draggedNodes.length > 0 ? draggedNodes : [node]
      for (const n of toWrite) {
        const startPosition = nodeDragStartPositionsRef.current.get(n.id)
        const wasMoved = !startPosition ||
          Math.abs(n.position.x - startPosition.x) > 0.5 ||
          Math.abs(n.position.y - startPosition.y) > 0.5
        if (wasMoved) {
          acknowledgeCanvasRegenCue(n.id, 'drag')
        }
        if (n.parentId) continue
        dragLog('writePosition node=%s pos=(%d,%d) tainted=true',
          n.id, n.position.x, n.position.y)
        // NAD feature `canvas-snap-to-grid`: self-describing write — carries the
        // effective snap state so the extractor can assert grid alignment.
        snapLog({
          event: 'snap-drag-write',
          node: n.id,
          x: n.position.x,
          y: n.position.y,
          snap: canvasPerformanceSettings.snapToGrid,
          gridSize: CANVAS_GRID_SIZE,
        })
        writeNodePosition(n.id, n.position.x, n.position.y, true)
      }
    },
    [acknowledgeCanvasRegenCue, canvasPerformanceSettings.snapToGrid, writeNodePosition]
  )

  const onSelectionDragStop = useCallback(
    (_event: React.MouseEvent, nodes: WorkspaceNode[]) => {
      try {
        for (const n of nodes) {
          if (n.parentId) continue
          writeNodePosition(n.id, n.position.x, n.position.y, true)
        }
      } finally {
        nodeDragStartPositionsRef.current.clear()
        endNodeDragInteraction()
      }
    },
    [endNodeDragInteraction, writeNodePosition]
  )

  // Canvas-to-group drag handlers — pass canvas methods explicitly because
  // CanvasContext.Provider is rendered by THIS component, so useContext
  // inside the hook would read from an ancestor (null), not from ours.
  const canvasDragMethods = useMemo(() => ({
    updateNodeData: <T extends FeNodeType>(nodeRef: { id: string; type: T }, updates: NodeDataRecord<T>) => {
      writeNodeData(nodeRef, updates)
    },
    updateNodeParent: writeNodeParent,
    updateNodePosition: writeNodePosition,
    transact: (fn: () => void) => {
      ydoc.transact(fn, 'frontend')
    },
  }), [writeNodeData, writeNodeParent, writeNodePosition, ydoc])

  const { onNodeDrag: onNodeDragOverGroup, onNodeDragStop: onNodeDragStopWithGroupBase } =
    useCanvasDragToGroup(onNodeDragStop, canvasDragMethods)

  const onNodeDrag = useCallback(
    (event: React.MouseEvent, node: WorkspaceNode, draggedNodes: WorkspaceNode[]) => {
      armNodeDragInteractionOnceMoved(event)
      onNodeDragOverGroup(event, node, draggedNodes)
    },
    [armNodeDragInteractionOnceMoved, onNodeDragOverGroup],
  )

  const onNodeDragStopWithGroup = useCallback(
    (event: React.MouseEvent, node: WorkspaceNode, draggedNodes: WorkspaceNode[]) => {
      try {
        onNodeDragStopWithGroupBase(event, node, draggedNodes)
      } finally {
        nodeDragStartPositionsRef.current.clear()
        endNodeDragInteraction()
      }
    },
    [endNodeDragInteraction, onNodeDragStopWithGroupBase],
  )

  // ─── Focus ReactFlow on node click ────────────────────────────────────────

  const onNodeClick = useCallback((_event: React.MouseEvent, node: WorkspaceNode) => {
    pulseCanvasPerformanceMode()
    emitNuxEvent({
      type: 'nodeSelected',
      nodeId: node.id,
      nodeType: node.type,
    })
    acknowledgeCanvasRegenCue(node.id, 'click')
    const wrapper = document.querySelector('.react-flow')
    if (!(wrapper instanceof HTMLElement)) return
    if (wrapper && document.activeElement !== wrapper && !wrapper.contains(document.activeElement)) {
      wrapper.focus({ preventScroll: true })
    }
  }, [acknowledgeCanvasRegenCue, emitNuxEvent, pulseCanvasPerformanceMode])

  // ─── Keyboard Shortcuts ───────────────────────────────────────────────────

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target
      if (!(target instanceof HTMLElement)) return
      if (
        target.closest('[data-canvas-local-keys]') !== null ||
        target.tagName === 'SELECT' ||
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return
      }

      const isMod = e.metaKey || e.ctrlKey

      // A blocking overlay (modal / lightbox / audio editor portaled over the
      // canvas) is open — suspend ALL canvas shortcuts. The underlying node can
      // still be selected under the overlay, and this window listener fires
      // regardless of the portal, so without this the overlay's Delete/Backspace
      // would delete the covered node. Bailing here (before any preventDefault)
      // leaves native browser behavior — incl. text copy — intact for the
      // overlay. See canvasShortcutGuard.
      if (isBlockingOverlayOpen()) {
        const isCanvasShortcut =
          e.key === 'Delete' ||
          e.key === 'Backspace' ||
          e.key === 'Tab' ||
          (isMod && ['c', 'v', 'd', 'z'].includes(e.key.toLowerCase()))
        if (isCanvasShortcut) {
          overlayGuardLog({
            event: 'canvas-shortcut:suspended-blocking-overlay',
            feature: 'canvas-shortcut-overlay-guard',
            trace_id: 'canvas-shortcut-overlay-guard:workspace',
            key: e.key,
            handler: 'workspace-window',
            overlayOpen: true,
          })
        }
        return
      }

      const key = e.key.toLowerCase()

      // Canvas keyboard shortcuts. Each SHORTCUT_ID marker maps to a binding in
      // keyboardShortcuts.ts (the Keyboard Shortcuts modal). Add/change one → update both;
      // keyboardShortcutsCoverage.test.ts enforces this.
      if (!isMod && (e.key === 'Delete' || e.key === 'Backspace')) { // SHORTCUT_ID: canvas.delete
        const selectedIds = Array.from(selectedNodeIdsRef.current)
        const candidateIds = selectedIds.filter((nodeId) => {
          const node = nodesRef.current.find((candidate) => candidate.id === nodeId)
          return node ? !isGroupCanvasNode(node) : true
        })
        if (candidateIds.length > 0) {
          e.preventDefault()
          requestNodesDelete(candidateIds, 'keyboard')
        }
        return
      }

      if (isMod && key === 'c') { // SHORTCUT_ID: canvas.copy
        const selection = window.getSelection()
        if (shouldPreserveBrowserCopy(selection)) {
          keyboardShortcutLog({
            event: 'workspace-shortcut:browser-copy-selection',
            feature: 'prompt-sidebar-copy',
            trace_id: 'prompt-sidebar-copy:browser-copy-selection',
            selectedCount: getSelectedNodes().length,
            targetTagName: target.tagName.toLowerCase(),
            prompt_length: selection?.toString().length ?? 0,
          })
          return
        }
        e.preventDefault()
        keyboardShortcutLog({
          event: 'workspace-shortcut:canvas-copy',
          feature: 'prompt-sidebar-copy',
          trace_id: 'prompt-sidebar-copy:canvas-copy',
          selectedCount: getSelectedNodes().length,
          targetTagName: target.tagName.toLowerCase(),
        })
        handleCopy()
      } else if (isMod && key === 'v') { // SHORTCUT_ID: canvas.paste
        e.preventDefault()
        handlePaste()
      } else if (isMod && key === 'd') { // SHORTCUT_ID: canvas.duplicate
        e.preventDefault()
        handleDuplicate()
      } else if (isMod && key === 'z' && !e.shiftKey) { // SHORTCUT_ID: canvas.undo
        e.preventDefault()
        handleUndo()
      } else if (isMod && key === 'z' && e.shiftKey) { // SHORTCUT_ID: canvas.redo
        e.preventDefault()
        handleRedo()
      } else if (viewMode === 'canvas' && e.key === 'Tab' && !isMod) { // SHORTCUT_ID: canvas.cycleNext SHORTCUT_ID: canvas.cyclePrev
        e.preventDefault()
        const reverse = e.shiftKey
        const allNodes = nodesRef.current
        if (allNodes.length === 0) return

        const selectedIds = selectedNodeIdsRef.current
        const currentId = selectedIds.size > 0
          ? Array.from(selectedIds)[selectedIds.size - 1]
          : null
        const currentNode = currentId
          ? allNodes.find(n => n.id === currentId)
          : null

        const parentId = currentNode?.parentId ?? null
        const siblings = allNodes.filter(n => (n.parentId ?? null) === parentId)
        if (siblings.length === 0) return

        siblings.sort((a, b) => {
          const dy = a.position.y - b.position.y
          if (Math.abs(dy) > 10) return dy
          return a.position.x - b.position.x
        })

        let nextIndex: number
        if (!currentNode) {
          nextIndex = 0
        } else {
          const curIdx = siblings.findIndex(n => n.id === currentNode.id)
          if (curIdx === -1) {
            nextIndex = 0
          } else {
            nextIndex = reverse
              ? (curIdx - 1 + siblings.length) % siblings.length
              : (curIdx + 1) % siblings.length
          }
        }

        const nextNode = siblings[nextIndex]
        rememberNode(nextNode.id)
        const newSelection = new Set([nextNode.id])
        selectedNodeIdsRef.current = newSelection
        setSelectedNodeIds(newSelection)

        tabNavigationLog({
          event: 'tab-navigation:cycle',
          feature: 'tab-navigation',
          direction: reverse ? 'prev' : 'next',
          from_node: currentId,
          to_node: nextNode.id,
          sibling_count: siblings.length,
          parent_id: parentId,
        })
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [getSelectedNodes, handleCopy, handlePaste, handleDuplicate, handleUndo, handleRedo, requestNodesDelete, rememberNode, setSelectedNodeIds, viewMode])

  // ─── Trackpad Pinch-to-Zoom ───────────────────────────────────────────────

  useEffect(() => {
    let canvasEl: Element | null = null
    let pendingZoom:
      | {
          clientX: number
          clientY: number
          zoomFactor: number
        }
      | null = null
    let zoomRaf = 0
    let zoomGestureActive = false
    let zoomGestureTimeout: number | null = null
    const ZOOM_SENSITIVITY = 0.01
    const MIN_ZOOM = 0.1
    const MAX_ZOOM = 4

    const keepZoomGestureActive = () => {
      zoomGestureActive = true
      if (zoomGestureTimeout != null) {
        window.clearTimeout(zoomGestureTimeout)
      }
      zoomGestureTimeout = window.setTimeout(() => {
        zoomGestureTimeout = null
        zoomGestureActive = false
      }, 260)
    }

    // SHORTCUT_ID: canvas.zoomWheel — Cmd/Ctrl + wheel zoom-to-cursor; Sound Expert prompt editors keep wheel input local (keyboardShortcuts.ts).
    const flushZoom = () => {
      zoomRaf = 0
      const pending = pendingZoom
      pendingZoom = null
      if (!pending || !canvasEl) return

      const rf = reactFlowInstance.current
      if (!rf) return

      const rect = canvasEl.getBoundingClientRect()
      const mouseX = pending.clientX - rect.left
      const mouseY = pending.clientY - rect.top

      const vp = rf.getViewport()
      const newZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, vp.zoom * pending.zoomFactor))
      const ratio = newZoom / vp.zoom
      const nextViewport = {
        x: mouseX - (mouseX - vp.x) * ratio,
        y: mouseY - (mouseY - vp.y) * ratio,
        zoom: newZoom,
      }

      rf.setViewport(nextViewport, { duration: 0 })
      updateViewportPreviewTransform(nextViewport)
    }

    const handler = (e: WheelEvent) => {
      // Zoom triggers: physical Ctrl (also synthetic trackpad pinch) or physical
      // Cmd, tracked from keyboard `getModifierState` — NOT the wheel event's own
      // `metaKey`, which is a cached flag that goes stale on macOS after Cmd+V.
      const ctrlZoom = e.ctrlKey
      const metaZoom = metaKeyDownRef.current
      // The bug condition: the wheel reports Cmd held while it physically is not.
      const staleMeta = e.metaKey && !metaZoom

      if (!canvasEl) canvasEl = document.querySelector('.react-flow')
      const decision = getCanvasWheelZoomDecision({
        canvasElement: canvasEl,
        ctrlZoom,
        metaZoom,
        staleMeta,
        target: e.target,
        zoomGestureActive,
      })
      if (decision.kind === 'ignore') return
      if (decision.kind === 'skip-local-target') {
        const localWheelTarget = e.target instanceof Element
          ? e.target.closest<HTMLElement>(`[${CANVAS_LOCAL_WHEEL_TARGET_ATTR}="true"]`)
          : null
        zoomNadLog({
          event: 'canvas-zoom-skipped-local-scroll-target',
          trace_id: localWheelTarget?.dataset.canvasLocalWheelNodeId
            ? `sound-expert-wheel:${localWheelTarget.dataset.canvasLocalWheelNodeId}`
            : undefined,
          skip_reason: 'local_scroll_target',
          node_id: localWheelTarget?.dataset.canvasLocalWheelNodeId,
          source_feature: localWheelTarget?.dataset.canvasLocalWheelSourceFeature,
          source_surface: localWheelTarget?.dataset.canvasLocalWheelSourceSurface,
          target_role: localWheelTarget?.dataset.canvasLocalWheelTargetRole,
          target_can_scroll:
            Boolean(localWheelTarget && localWheelTarget.scrollHeight > localWheelTarget.clientHeight)
            || Boolean(localWheelTarget?.querySelector('.overflow-y-auto')),
          delta_y: e.deltaY,
          ctrl_key: e.ctrlKey,
          meta_key: e.metaKey,
          canvas_zoom_modifier_detected: ctrlZoom || metaZoom,
        })
        return
      }

      if (decision.kind === 'suppress-stale-meta') {
        // staleMeta only: pre-fix this would have zoomed; now it falls through to
        // ReactFlow's panOnScroll. Log it so the suppression is verifiable in traces.
        zoomNadLog({
          event: 'wheel-zoom-suppressed-stale-meta',
          feature: 'canvas:zoom-gesture',
          eventMetaKey: e.metaKey,
          trackedMeta: metaZoom,
        })
        return
      }

      e.preventDefault()
      keepZoomGestureActive()
      zoomNadLog({
        event: 'wheel-zoom',
        feature: 'canvas:zoom-gesture',
        source: ctrlZoom ? 'ctrl' : 'meta',
        eventMetaKey: e.metaKey,
        trackedMeta: metaZoom,
        overNowheel: decision.overNowheel,
        zoomGestureActive,
      })
      beginViewportInteraction(240)
      const eventZoomFactor = Math.max(0.25, Math.min(4, 1 - e.deltaY * ZOOM_SENSITIVITY))
      pendingZoom = {
        clientX: e.clientX,
        clientY: e.clientY,
        zoomFactor: (pendingZoom?.zoomFactor ?? 1) * eventZoomFactor,
      }
      if (zoomRaf === 0) {
        zoomRaf = window.requestAnimationFrame(flushZoom)
      }
    }

    // Defense in depth: drop the sticky zoom latch when the window loses focus or
    // the tab is hidden, so a gesture can never survive a focus change.
    const clearZoomGesture = () => {
      // Drop any queued zoom work too — a deferred rAF can still fire after the
      // page is hidden and would otherwise apply a stale zoom on refocus.
      pendingZoom = null
      if (zoomRaf !== 0) {
        window.cancelAnimationFrame(zoomRaf)
        zoomRaf = 0
      }
      if (zoomGestureTimeout != null) {
        window.clearTimeout(zoomGestureTimeout)
        zoomGestureTimeout = null
      }
      zoomGestureActive = false
    }
    const onVisibilityChange = () => {
      if (document.hidden) clearZoomGesture()
    }

    document.addEventListener('wheel', handler, { passive: false, capture: true })
    window.addEventListener('blur', clearZoomGesture)
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      if (zoomRaf !== 0) window.cancelAnimationFrame(zoomRaf)
      if (zoomGestureTimeout != null) window.clearTimeout(zoomGestureTimeout)
      document.removeEventListener('wheel', handler, { capture: true })
      window.removeEventListener('blur', clearZoomGesture)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [beginViewportInteraction, updateViewportPreviewTransform])

  // ─── Shift + Meta Key Tracking ────────────────────────────────────────────

  useEffect(() => {
    // macOS can consume Cmd+Shift+5 before Chromium receives Shift keyup. Reconcile from every key
    // and pointer event so the canvas cannot remain latched in selection mode after screen capture.
    return installShiftMetaTracking(window, {
      onShiftHeldChange: (held, { reason, event }) => {
        shiftHeldRef.current = held
        setIsShiftSelecting(held)

        const activeElement = document.activeElement
        selectionNadLog({
          event: 'shift-selection-key',
          feature: 'canvas:selection',
          held,
          reason,
          key: event && 'key' in event ? event.key : undefined,
          code: event && 'code' in event ? event.code : undefined,
          activeTag: activeElement?.tagName,
          activeClass:
            activeElement instanceof HTMLElement ? activeElement.className : undefined,
        })
      },
      onMetaHeldChange: (held, { reason }) => {
        metaKeyDownRef.current = held
        zoomNadLog({ event: 'meta-key-state', feature: 'canvas:zoom-gesture', held, reason })
      },
    })
  }, [])

  // ─── handleAddNode (from toolbox) ─────────────────────────────────────────

  // The toolbox lives in the header, outside the (pointer-gated) canvas
  // surface, so it stays clickable while a cold per-canvas config loads.
  // Refuse rather than stamp schema defaults in place of the project's
  // style / resolution / aspect ratio (see `canvasConfigReady`).
  const refuseAddWhileConfigLoading = useCallback(
    (nodeType: string | undefined) => {
      if (canvasConfigReady) return false
      canvasTabNavLog({
        event: 'add-node-blocked-config-loading',
        trace_id: canvasTabNavTraceId(activeCanvasId),
        projectId,
        canvasId: activeCanvasId,
        nodeType,
      })
      toast.info(t('workspace.canvasConfigLoading'), { id: 'canvas-config-loading' })
      return true
    },
    [activeCanvasId, canvasConfigReady, projectId, t],
  )

  const videoCreationRoomRef = useRef({ key: canvasRoomKey, epoch: roomEpoch })
  videoCreationRoomRef.current = { key: canvasRoomKey, epoch: roomEpoch }

  const handleAddNode = useCallback(
    async (node: ReturnType<typeof createNode>, explicitData?: RawNodeDataRecord) => {
      if (refuseAddWhileConfigLoading(node.type)) return
      const preference = projectConfig?.effective_default_video_model
      if (node.type === 'videoGenerator') {
        const explicitModel = typeof explicitData?.in_model === 'string'
          ? explicitData.in_model.trim()
          : ''
        const mode = String(explicitData?.in_mode ?? node.data.in_mode ?? 'REFERENCE')
        const traceId = `video-default:${node.id}`
        defaultVideoModelCreationLog({
          event: 'video-default:resolve-start',
          trace_id: traceId,
          project_id: projectId,
          canvas_id: activeCanvasId,
          node_id: node.id,
          requested_model: preference ?? null,
          explicit_model: explicitModel || null,
          mode,
        })

        if (!preference || explicitModel || mode === 'MOTION_IMAGE') {
          defaultVideoModelCreationLog({
            event: 'video-default:resolve-skipped',
            trace_id: traceId,
            project_id: projectId,
            canvas_id: activeCanvasId,
            node_id: node.id,
            reason: explicitModel
              ? 'explicit_model'
              : mode === 'MOTION_IMAGE'
                ? 'motion_image'
                : 'no_project_default',
            selected_model: explicitModel || node.data.in_model || null,
          })
        } else {
          const family = videoFamilyFor(preference)
          let fields: { in_model: string; in_mode?: string }
          let source: 'family_resolution' | 'project_model'
          if (family && preference === family.id) {
            const count = (port: string) => {
              const value = explicitData?.[port] ?? node.data[port]
              return Array.isArray(value) ? value.length : value ? 1 : 0
            }
            try {
              const resolved = await resolveVideoFamilyForNode({
                family_id: family.id,
                mode,
                image_count: count('in_images'),
                video_count: count('in_reference_videos'),
                audio_count: count('in_reference_audios'),
              })
              if (videoCreationRoomRef.current.key !== canvasRoomKey || videoCreationRoomRef.current.epoch !== roomEpoch) return
              fields = { in_model: resolved.model_id, in_mode: resolved.mode }
              source = 'family_resolution'
            } catch (error) {
              const message = error instanceof Error ? error.message : String(error)
              defaultVideoModelCreationLog({
                event: 'video-default:resolve-error',
                trace_id: traceId,
                project_id: projectId,
                canvas_id: activeCanvasId,
                node_id: node.id,
                family_id: family.id,
                error: message,
              })
              toast.error(message)
              return
            }
          } else {
            fields = { in_model: preference }
            source = 'project_model'
          }
          node = { ...node, data: { ...node.data, ...fields } }
          explicitData = { ...(explicitData ?? {}), ...fields }
          defaultVideoModelCreationLog({
            event: 'video-default:resolved',
            trace_id: traceId,
            project_id: projectId,
            canvas_id: activeCanvasId,
            node_id: node.id,
            source,
            selected_model: fields.in_model,
            selected_mode: fields.in_mode ?? mode,
          })
        }
      }
      // Toolbox/dock creation path — see the `addNode` record for why this is
      // needed and why it is safe for the node's generated assets.
      if (!isUndoRedo.current) {
        undoHistory.record(canvasRoomKey, roomEpoch, { nodes: ydocSafeSnapshotNodes(nodesRef.current), edges: [...edgesRef.current] })
      }
      const isGroupNode = node.type === 'nodeGroup'
      const imageDefaultData = applyDefaultImageModel(
        node.type,
        node.data,
        defaultImageModel,
        explicitData ?? {},
      )
      if (IMAGE_MODEL_DEFAULT_NODE_TYPES.has(node.type)) {
        imagePreferenceLog({
          event: 'new_node_image_model_selected',
          trace_id: `image-model:${node.id}`,
          project_id: projectId,
          node_id: node.id,
          node_type: node.type,
          requested: preferredImageModel,
          selected_model: imageDefaultData?.in_model ?? null,
          mode_source: typeof explicitData?.in_model === 'string' && explicitData.in_model.trim()
            ? 'explicit_node_model'
            : preferredImageModel === 'auto' ? 'existing_dreamwrite_logic' : 'project_preference',
        })
      }
      const styledData = applyWorkspaceStyleDefault(
        node.type,
        imageDefaultData,
        workspaceStyleId,
        explicitData,
      )
      const imageDefaultExplicitData = applyDefaultImageModel(
        node.type,
        explicitData ?? node.data,
        defaultImageModel,
        explicitData ?? {},
      )
      const resolvedData = applyProjectResolutionDefault(
        node.type,
        styledData,
        projectVideoResolution,
        imageDefaultExplicitData,
      )
      const visualDefaultedData = applyProjectAspectRatioDefault(
        node.type,
        resolvedData,
        projectAspectRatio,
        imageDefaultExplicitData,
      )
      const initialData = withVideoGeneratorCreationAudioDefaults(
        node.type,
        visualDefaultedData,
        {
          explicitData: explicitData ?? {},
        },
      )
      if (supportsVideoResolution(node.type)) {
        resolutionStampLog('toolbox-stamp', {
          event: 'project-video-resolution:toolbox-stamp',
          node_id: node.id,
          node_type: node.type,
          project_default: projectVideoResolution ?? null,
          resolution_value: (initialData as { in_resolution?: string }).in_resolution ?? null,
        })
      }
      if (supportsAspectRatio(node.type)) {
        aspectStampLog({
          event: 'director-aspect-default:toolbox-stamp',
          node_id: node.id,
          node_type: node.type,
          project_default: projectAspectRatio ?? null,
          // `RawNodeDataRecord` is Record<string, unknown> and an explicit
          // per-node value is preserved verbatim, so narrow before logging
          // rather than asserting a type the record does not guarantee.
          aspect_ratio_value:
            typeof initialData.in_aspect_ratio === 'string'
              ? initialData.in_aspect_ratio
              : null,
        })
      }
      if (supportsWorkspaceStyle(node.type)) {
        // Structured (record-first) form so the clean NAD record — with
        // project_default / style_value as top-level fields — lands in
        // `nad trace --feature project-default-style`.
        styleStampLog({
          event: 'project-default-style:toolbox-stamp',
          node_id: node.id,
          node_type: node.type,
          project_default: workspaceStyleId ?? null,
          style_value: initialData.in_style ?? null,
        })
      }
      assertNoDirectAssetInputPortWrites(
        node.type,
        node.id,
        initialData,
        'addNode',
      )
      const nodeData = { ...initialData, projectId }

      const enrichedNode: WorkspaceNode = {
        ...node,
        data: nodeData,
        // Group nodes need transparent background and no border so the
        // ReactFlow wrapper doesn't show through as a gray rectangle.
        ...(isGroupNode ? {
          style: {
            width: 355,
            padding: 0,
            backgroundColor: 'transparent',
            border: 'none',
          },
        } : {}),
      }

      // Keep placement reads current for back-to-back node creation in the
      // same tick, such as multiple reference uploads from one picker.
      nodesRef.current = appendOrReplaceNodeById(nodesRef.current, enrichedNode)
      setNodes((nds) => {
        const updated = appendOrReplaceNodeById(nds, enrichedNode)
        nodesRef.current = updated
        return updated
      })

      if (node.position && shouldSyncToBackend(node.type ?? '')) {
        addNodeToYDoc(
          node.id,
          node.type ?? '',
          firstStringDataField(node.data, ['label', 'name']) ?? node.id,
          node.position.x,
          node.position.y,
          initialData,
          true
        )
        if (node.type === 'videoGenerator') {
          defaultVideoModelCreationLog({
            event: 'video-default:persisted',
            trace_id: `video-default:${node.id}`,
            project_id: projectId,
            canvas_id: activeCanvasId,
            node_id: node.id,
            selected_model: typeof initialData.in_model === 'string'
              ? initialData.in_model
              : null,
          })
        }
      }

      // Group nodes are frontend-only but need a Y doc entry so writeNodeData
      // can persist child_node_ids and other data updates.
      if (node.type === 'group' && node.position) {
        addNodeToYDoc(
          node.id,
          'group',
          stringDataField(initialData, 'label') ?? 'Group',
          node.position.x,
          node.position.y,
          initialData,
        )
      }


    },
    [setNodes, projectId, activeCanvasId, projectConfig?.effective_default_video_model, addNodeToYDoc, workspaceStyleId, projectVideoResolution, projectAspectRatio, defaultImageModel, preferredImageModel, canvasRoomKey, roomEpoch, undoHistory, refuseAddWhileConfigLoading]
  )

  const waitForInitialUploadNodeReadback = useCallback(async (nodeIds: string[]) => {
    if (!projectId) {
      throw new Error('Cannot verify upload references without a project ID')
    }

    const deadline = Date.now() + 10_000
    let missingNodeIds = nodeIds
    let lastErrorMessage: string | null = null

    while (Date.now() < deadline) {
      try {
        const presence = await getYDocNodePresence(projectId, activeCanvasId, nodeIds)
        missingNodeIds = presence.missing_node_ids
        if (missingNodeIds.length === 0) {
          landingUploadLog({
            event: 'seeded-upload-nodes-readback',
            trace_id: landingUploadSeedTraceId(projectId, activeCanvasId, nodeIds),
            project_id: projectId,
            canvas_id: activeCanvasId,
            node_ids: nodeIds,
          })
          for (const nodeId of nodeIds) {
            landingUploadLog({
              event: 'landing-reference-node:readback', node_id: nodeId,
              trace_id: `landing-reference:${nodeId}`,
              project_id: projectId, canvas_id: activeCanvasId,
            })
          }
          return
        }
      } catch (error) {
        lastErrorMessage = error instanceof Error ? error.message : String(error)
      }

      await new Promise((resolve) => window.setTimeout(resolve, 200))
    }

    const detail = lastErrorMessage ? ` Last error: ${lastErrorMessage}` : ''
    throw new Error(
      `Upload reference nodes did not sync to backend YDoc: ${missingNodeIds.join(', ')}.${detail}`,
    )
  }, [activeCanvasId, projectId])

  useEffect(() => {
    // Wait for the canvas config too: handleAddNode refuses while it loads, and
    // the latch below would otherwise drop the seed nodes with no retry.
    if (!canvasReady || !canvasConfigReady || !projectId || initialUploadsSeededRef.current) return

    initialUploadsSeededRef.current = true
    if (initialUploadReferences.length === 0) {
      setInitialUploadsSeeded(true)
      return
    }

    let cancelled = false
    const nodeIds = initialUploadReferences.map((upload) => upload.id)

    const seedInitialUploads = async () => {
      initialUploadReferences.forEach((upload, index) => {
        const node = createNode(upload.type, { x: 80 + index * 340, y: 80 }, {
          ...createLandingUploadSeedNodeData(upload),
        })
        node.id = upload.id
        handleAddNode(node)
      })

      landingUploadLog({
        event: 'seeded-upload-nodes',
        trace_id: landingUploadSeedTraceId(projectId, activeCanvasId, nodeIds),
        project_id: projectId,
        canvas_id: activeCanvasId,
        count: initialUploadReferences.length,
        node_ids: nodeIds,
      })

      await waitForInitialUploadNodeReadback(nodeIds)
      if (!cancelled) setInitialUploadsSeeded(true)
    }

    void seedInitialUploads().catch((error) => {
      const message = error instanceof Error ? error.message : 'Upload reference nodes did not sync'
      landingUploadLog({
        event: 'seeded-upload-nodes-readback-failed',
        trace_id: landingUploadSeedTraceId(projectId, activeCanvasId, nodeIds),
        project_id: projectId,
        canvas_id: activeCanvasId,
        error: message,
      })
      if (!cancelled) setInitialUploadSeedError(message)
    })

    return () => {
      cancelled = true
    }
  }, [
    activeCanvasId,
    canvasConfigReady,
    canvasReady,
    handleAddNode,
    initialUploadReferences,
    projectId,
    waitForInitialUploadNodeReadback,
  ])

  // Add-node modal and asset library: add, then pan to the new node.
  const handleUserAddNode = useCallback(
    (node: ReturnType<typeof createNode>, explicitData?: RawNodeDataRecord) => {
      handleAddNode(node, explicitData)
      focusNewNode(node.id)
    },
    [handleAddNode, focusNewNode],
  )

  // ─── Director Upload → Node Creation ──────────────────────────────────────

  /** Create one upload node at the viewport centre, pan to it, and return its id. */
  const createUploadNodeFromDirector = useCallback(
    (file: File, type: 'image' | 'video' | 'audio', source: UploadSource): string | null => {
      const rf = reactFlowInstance.current
      if (!rf) return null
      tagUploadSource(file, source)

      // Map type to node type (identity — type names are the same)
      const nodeType = type

      // Calculate viewport center position
      const canvasEl = document.querySelector('.react-flow')
      const width = canvasEl?.clientWidth ?? window.innerWidth
      const height = canvasEl?.clientHeight ?? window.innerHeight
      const centerScreen = { x: width / 2, y: height / 2 }
      const flowPos = rf.screenToFlowPosition(centerScreen)

      const position = findNonOverlappingCanvasPosition(nodeType, flowPos, nodesRef.current)

      // Create blob URL eagerly so the first render shows the preview + spinner
      // instead of the empty "Upload Image" placeholder.
      const previewUrl = URL.createObjectURL(file)

      // Create the node with initialFile, preview URL, and running status
      const newNode = createNode(nodeType, position, {
        initialFile: file,
        _initialPreviewUrl: previewUrl,
        status: 'running',
        in_file_name: file.name,
        projectId,
      })

      noteNodeActions([newNode.id], 'add', 'file_upload')
      handleAddNode(newNode)
      focusNewNode(newNode.id)
      return newNode.id
    },
    [projectId, handleAddNode, focusNewNode]
  )

  // Add-node modal file chooser.
  const handleUploadFromDirector = useCallback(
    (file: File, type: 'image' | 'video' | 'audio') => {
      createUploadNodeFromDirector(file, type, 'picker')
    },
    [createUploadNodeFromDirector]
  )

  // Multi-file Director uploads run ONE AT A TIME: create a node, wait for its
  // own initialFile upload to settle (status leaves 'running'), reference it in
  // the chat composer if it completed, then create the next. A second selection
  // made while a batch is in flight is appended to the same queue.
  const directorUploadQueueRef = useRef<ClassifiedMediaFile[]>([])
  // Counters for the current run of the queue (reset when it starts from idle;
  // a selection appended mid-run extends the same run).
  const directorUploadRunRef = useRef({ total: 0, started: 0, succeeded: 0, failed: 0 })
  const [directorUploadInFlight, setDirectorUploadInFlight] = useState<{
    nodeId: string
    fileName: string
    mediaType: ClassifiedMediaFile['type']
    /** 1-based position in the run, e.g. "2/3". */
    position: string
    /** The node has been observed in canvas state at least once. */
    seen: boolean
  } | null>(null)

  const startNextDirectorUpload = useCallback(() => {
    const run = directorUploadRunRef.current
    while (directorUploadQueueRef.current.length > 0) {
      const next = directorUploadQueueRef.current.shift()
      if (!next) break
      // Files attached in the Director chat composer.
      const nodeId = createUploadNodeFromDirector(next.file, next.type, 'prompt_attachment')
      if (!nodeId) {
        // No ReactFlow instance — nothing can be placed. Say so instead of
        // silently swallowing the rest of the batch.
        multiFileUploadLog({
          event: 'director-node-create-failed',
          file_name: next.file.name,
          dropped_count: directorUploadQueueRef.current.length + 1,
        })
        directorUploadQueueRef.current = []
        setDirectorUploadInFlight(null)
        return
      }
      run.started += 1
      const position = `${run.started}/${run.total}`
      multiFileUploadLog({
        event: 'director-node-created',
        node_id: nodeId,
        file_name: next.file.name,
        media_type: next.type,
        batch_position: position,
      })
      setDirectorUploadInFlight({ nodeId, fileName: next.file.name, mediaType: next.type, position, seen: false })
      return
    }
    multiFileUploadLog({
      event: 'director-batch-done',
      success_count: run.succeeded,
      failed_count: run.failed,
    })
    setDirectorUploadInFlight(null)
  }, [createUploadNodeFromDirector])

  useEffect(() => {
    if (!directorUploadInFlight) return
    const { nodeId, fileName, mediaType, position, seen } = directorUploadInFlight
    const logFields = { node_id: nodeId, file_name: fileName, media_type: mediaType, batch_position: position }
    const node = nodes.find((n) => n.id === nodeId)
    if (!node) {
      // Not yet in canvas state right after creation; once seen, a missing node
      // means the user deleted it mid-upload — move on.
      if (seen) {
        directorUploadRunRef.current.failed += 1
        multiFileUploadLog({ event: 'director-node-settled', ...logFields, upload_status: 'removed' })
        startNextDirectorUpload()
      }
      return
    }
    if (!seen) {
      setDirectorUploadInFlight({ ...directorUploadInFlight, seen: true })
      return
    }
    const status = stringDataField(node.data, 'status')
    if (status === 'running') return
    multiFileUploadLog({ event: 'director-node-settled', ...logFields, upload_status: status ?? 'none' })
    if (status === 'completed') {
      directorUploadRunRef.current.succeeded += 1
      dispatchAppEvent('add-node-to-chat', { nodeId, source: 'upload' })
      multiFileUploadLog({ event: 'director-mention-added', ...logFields })
    } else {
      directorUploadRunRef.current.failed += 1
    }
    startNextDirectorUpload()
  }, [directorUploadInFlight, nodes, startNextDirectorUpload])

  // A created node normally appears in canvas state within a frame or two. If
  // it never does (e.g. the YDoc add failed, or it was deleted before React
  // committed it), stop waiting so the rest of the batch is not stuck forever.
  useEffect(() => {
    if (!directorUploadInFlight || directorUploadInFlight.seen) return
    const { nodeId, fileName, mediaType, position } = directorUploadInFlight
    const timer = window.setTimeout(() => {
      directorUploadRunRef.current.failed += 1
      multiFileUploadLog({
        event: 'director-node-settled',
        node_id: nodeId,
        file_name: fileName,
        media_type: mediaType,
        batch_position: position,
        upload_status: 'never-appeared',
      })
      startNextDirectorUpload()
    }, DIRECTOR_UPLOAD_NODE_APPEAR_TIMEOUT_MS)
    return () => window.clearTimeout(timer)
  }, [directorUploadInFlight, startNextDirectorUpload])

  const handleUploadMediaFromDirector = useCallback(
    (accepted: ClassifiedMediaFile[], rejected: File[]) => {
      if (rejected.length > 0) {
        toast.error(
          t('shared:uploadFlyout.unsupportedIgnored', {
            names: rejected.map((file) => file.name).join(', '),
          }),
          { id: 'director-upload-unsupported' },
        )
        multiFileUploadLog({
          event: 'unsupported-ignored',
          surface: 'director-chat',
          rejected_files: rejected.map((file) => file.name).join(','),
          notified: true,
        })
      }
      if (accepted.length === 0) return
      if (!directorUploadInFlight) {
        directorUploadRunRef.current = { total: 0, started: 0, succeeded: 0, failed: 0 }
      }
      directorUploadRunRef.current.total += accepted.length
      directorUploadQueueRef.current.push(...accepted)
      multiFileUploadLog({
        event: 'director-batch-enqueued',
        accepted_count: accepted.length,
        accepted_files: describeMediaFiles(accepted),
        queue_length: directorUploadQueueRef.current.length,
      })
      if (!directorUploadInFlight) startNextDirectorUpload()
    },
    [directorUploadInFlight, startNextDirectorUpload, t]
  )

  // ─── Reference Selector Upload → Node Creation (no pan) ───────────────────

  const handleUploadFromRefSelector = useCallback(
    (file: File, type: 'image' | 'video' | 'audio'): string => {
      const rf = reactFlowInstance.current
      const nodeType = type
      // The reference selector's upload button opens a file chooser.
      tagUploadSource(file, 'picker')

      // Calculate viewport center position
      const canvasEl = document.querySelector('.react-flow')
      const width = canvasEl?.clientWidth ?? window.innerWidth
      const height = canvasEl?.clientHeight ?? window.innerHeight
      const centerScreen = { x: width / 2, y: height / 2 }
      const flowPos = rf?.screenToFlowPosition(centerScreen) ?? { x: 400, y: 300 }

      const position = findNonOverlappingCanvasPosition(nodeType, flowPos, nodesRef.current)

      // Create blob URL eagerly so the first render shows the preview + spinner
      const previewUrl = URL.createObjectURL(file)

      const newNode = createNode(nodeType, position, {
        initialFile: file,
        _initialPreviewUrl: previewUrl,
        status: 'running',
        in_file_name: file.name,
        projectId,
      })

      noteNodeActions([newNode.id], 'add', 'file_upload')
      handleAddNode(newNode)

      // NO canvas pan — node is created silently in the background
      return newNode.id
    },
    [projectId, handleAddNode]
  )

  // ─── Canvas-Level Drag-and-Drop ───────────────────────────────────────────

  const handleCanvasDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'copy'
  }, [])

  // One upload node per supported media file, fanned out from `anchor` (flow
  // coordinates) in file order. Shared by canvas drag-and-drop and the canvas
  // right-click menu's Upload item.
  const placeMediaFilesOnCanvas = useCallback(
    (
      files: File[],
      anchor: { x: number; y: number },
      surface: 'canvas-drop' | 'context-menu-upload',
      source: NodeMutationSource,
    ) => {
      const { accepted, rejected } = classifyMediaFiles(files)
      multiFileUploadLog({
        event: surface,
        dropped_count: files.length,
        accepted_count: accepted.length,
        accepted_files: describeMediaFiles(accepted),
        rejected_count: rejected.length,
      })

      if (rejected.length > 0) {
        toast.error(
          t('shared:uploadFlyout.unsupportedIgnored', {
            names: rejected.map((file) => file.name).join(', '),
          }),
          { id: `${surface}-unsupported` },
        )
        multiFileUploadLog({
          event: 'unsupported-ignored',
          surface,
          rejected_files: rejected.map((file) => file.name).join(','),
          notified: true,
        })
      }

      // Each node is placed against the existing canvas PLUS the nodes placed
      // earlier in this batch, so several files fan out instead of stacking.
      const placed: WorkspaceNode[] = []
      for (const [index, { file, type }] of accepted.entries()) {
        const position = findNonOverlappingCanvasPosition(
          type,
          anchor,
          [...nodesRef.current, ...placed],
        )
        // Create upload node with initialFile and eager preview
        tagUploadSource(file, 'drag_drop')
        const previewUrl = URL.createObjectURL(file)
        const newNode = createNode(type, position, {
          initialFile: file,
          _initialPreviewUrl: previewUrl,
          status: 'running',
          in_file_name: file.name,
          projectId,
        })
        placed.push(newNode)
        noteNodeActions([newNode.id], 'add', source)
        handleAddNode(newNode)
        multiFileUploadLog({
          event: `${surface}-node-created`,
          node_id: newNode.id,
          file_name: file.name,
          media_type: type,
          batch_position: `${index + 1}/${accepted.length}`,
        })
      }
    },
    [projectId, handleAddNode, t]
  )

  const handleCanvasDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      const rf = reactFlowInstance.current
      if (!rf) return

      const files = Array.from(e.dataTransfer.files)
      if (files.length === 0) return

      // Convert drop position to flow coordinates
      const dropPosition = rf.screenToFlowPosition({ x: e.clientX, y: e.clientY })
      placeMediaFilesOnCanvas(files, dropPosition, 'canvas-drop', 'drop')
    },
    [placeMediaFilesOnCanvas]
  )

  // ─── Canvas Right-Click Menus (Figma "Canvas UI 2.0", node 566:1022) ──────

  const [contextMenu, setContextMenu] = useState<CanvasContextMenuState | null>(null)
  const contextMenuUploadInputRef = useRef<HTMLInputElement>(null)
  const contextMenuUploadAnchorRef = useRef<{ x: number; y: number } | null>(null)
  // Re-render an open menu when a node (un)registers Download / Save to Library.
  const nodeContextActionsVersion = useNodeContextActionsVersion()

  const closeContextMenu = useCallback((reason: string) => {
    setContextMenu((current) => {
      if (current) contextMenuLog({ event: 'closed', feature: 'canvas-context-menu', kind: current.kind, reason })
      return null
    })
  }, [])

  // Right-clicking a node outside the current selection selects just that node
  // (like a left click), so the menu and the selection always agree.
  const selectNodesForContextMenu = useCallback((ids: string[]) => {
    const selection = new Set(ids)
    selectedNodeIdsRef.current = selection
    setSelectedNodeIds(selection)
    setNodes((current) => current.map((node) => {
      const selected = selection.has(node.id)
      return Boolean(node.selected) === selected ? node : { ...node, selected }
    }))
  }, [setNodes, setSelectedNodeIds])

  const openNodeContextMenu = useCallback((event: React.MouseEvent, nodeId: string) => {
    event.preventDefault()
    event.stopPropagation()
    const selected = selectedNodeIdsRef.current
    const targetNodeIds = selected.has(nodeId) ? Array.from(selected) : [nodeId]
    if (!selected.has(nodeId)) selectNodesForContextMenu(targetNodeIds)
    contextMenuLog({
      event: 'opened',
      feature: 'canvas-context-menu',
      kind: 'node',
      view_mode: viewMode,
      anchor_node_id: nodeId,
      target_count: targetNodeIds.length,
      kept_selection: selected.has(nodeId),
    })
    setContextMenu({ kind: 'node', x: event.clientX, y: event.clientY, anchorNodeId: nodeId, targetNodeIds })
  }, [selectNodesForContextMenu, viewMode])

  const openCanvasContextMenu = useCallback((event: React.MouseEvent | MouseEvent, target: 'pane' | 'edge') => {
    event.preventDefault()
    const rf = reactFlowInstance.current
    const flowPosition = rf?.screenToFlowPosition({ x: event.clientX, y: event.clientY })
    contextMenuLog({ event: 'opened', feature: 'canvas-context-menu', kind: 'canvas', target, view_mode: viewMode })
    setContextMenu({ kind: 'canvas', x: event.clientX, y: event.clientY, targetNodeIds: [], flowPosition })
  }, [viewMode])

  const onPaneContextMenu = useCallback(
    (event: React.MouseEvent | MouseEvent) => openCanvasContextMenu(event, 'pane'),
    [openCanvasContextMenu],
  )
  const onEdgeContextMenu = useCallback(
    (event: React.MouseEvent) => openCanvasContextMenu(event, 'edge'),
    [openCanvasContextMenu],
  )
  const onNodeContextMenu = useCallback(
    (event: React.MouseEvent, node: WorkspaceNode) => openNodeContextMenu(event, node.id),
    [openNodeContextMenu],
  )
  // Right-click on the box around a multi-selection: the node menu for the whole
  // selection, anchored on the selected node under the pointer (the box sits on
  // top of the nodes, so ReactFlow reports the selection, not the node).
  // The box also covers any unselected node lying inside its bounds: a
  // right-click on one of those targets that node, as it would without the box.
  const openSelectionContextMenu = useCallback((event: React.MouseEvent, selection: WorkspaceNode[]) => {
    const underPointer = document
      .elementsFromPoint(event.clientX, event.clientY)
      .map((el) => el.closest<HTMLElement>('.react-flow__node')?.dataset.id)
      .find((id): id is string => !!id)
    const anchorId = underPointer ?? selection[0]?.id
    if (!anchorId) return openCanvasContextMenu(event, 'pane')
    openNodeContextMenu(event, anchorId)
  }, [openCanvasContextMenu, openNodeContextMenu])
  // ReactFlow's own selection rect (onSelectionContextMenu) and Dreamwrite's
  // hairline box (<CanvasSelectionBounds>) both cover a multi-selection.
  const onSelectionContextMenu = openSelectionContextMenu
  const onSelectionBoundsContextMenu = useCallback(
    (event: React.MouseEvent) => openSelectionContextMenu(event, getSelectedNodes()),
    [getSelectedNodes, openSelectionContextMenu],
  )

  // Never show the browser's own menu anywhere on the canvas / list surface,
  // including node panels, text fields and the empty list-view area.
  const handleCanvasSurfaceContextMenuCapture = useCallback((event: React.MouseEvent) => {
    event.preventDefault()
  }, [])

  const contextMenuSections = useMemo(() => {
    if (!contextMenu) return []
    void nodeContextActionsVersion
    const capability = (cap: 'download' | 'saveToLibrary') =>
      combineCapability(contextMenu.targetNodeIds.map((id) => getNodeContextAction(id, cap)))
    const canPaste = clipboardRef.current.nodes.length > 0
    return buildMenu(
      contextMenu.kind,
      {
        canUndo: !!undoHistory.peekUndo(canvasRoomKey, roomEpoch),
        canRedo: !!undoHistory.peekRedo(canvasRoomKey, roomEpoch),
        canPaste,
      },
      { canPaste, saveToLibrary: capability('saveToLibrary'), download: capability('download') },
    )
  }, [contextMenu, nodeContextActionsVersion, undoHistory, canvasRoomKey, roomEpoch])

  const runNodeContextCapability = useCallback((nodeIds: string[], cap: 'download' | 'saveToLibrary') => {
    let ran = 0
    for (const id of nodeIds) {
      const action = getNodeContextAction(id, cap)
      if (action?.enabled) {
        action.run()
        ran += 1
      }
    }
    return ran
  }, [])

  const handleContextMenuSelect = useCallback((id: ContextMenuItemId) => {
    const menu = contextMenu
    if (!menu) return
    setContextMenu(null)
    const targets = menu.targetNodeIds
    const anchorNode = menu.anchorNodeId
      ? nodesRef.current.find((node) => node.id === menu.anchorNodeId)
      : undefined
    if (projectId) {
      trackInteraction('canvas_context_menu_action', {
        project_id: projectId,
        menu: menu.kind,
        action: id,
        view_mode: viewMode,
        item_count: menu.kind === 'node' ? targets.length : 0,
        ...(anchorNode ? { node_id: anchorNode.id, node_type: analyticsNodeType(anchorNode.type ?? '') } : {}),
      })
    }
    const logFields = {
      feature: 'canvas-context-menu',
      kind: menu.kind,
      item: id,
      view_mode: viewMode,
      target_count: targets.length,
      target_ids: targets.join(','),
    }

    switch (id) {
      case 'upload': {
        contextMenuUploadAnchorRef.current = menu.flowPosition ?? null
        contextMenuUploadInputRef.current?.click()
        break
      }
      case 'asset_manager':
        setAssetManagerOpen(true)
        break
      case 'add_nodes':
        setPaletteAddNodeOpen(true)
        break
      case 'undo':
        handleUndo()
        break
      case 'redo':
        handleRedo()
        break
      case 'paste':
        handlePaste('context_menu')
        break
      case 'add_to_chat':
        for (const nodeId of targets) dispatchAppEvent('add-node-to-chat', { nodeId })
        break
      case 'save_to_library': {
        const ran = runNodeContextCapability(targets, 'saveToLibrary')
        contextMenuLog({ event: 'item-selected', ...logFields, ran_count: ran })
        return
      }
      case 'download': {
        const ran = runNodeContextCapability(targets, 'download')
        contextMenuLog({ event: 'item-selected', ...logFields, ran_count: ran })
        return
      }
      case 'copy':
        handleCopy()
        break
      case 'duplicate':
        handleDuplicate('context_menu')
        break
      case 'project_settings': {
        const handled = openProjectSettings('canvas_context_menu')
        if (!handled) throw new Error('canvas context menu: no Project Settings opener is mounted')
        break
      }
      case 'delete':
        requestNodesDelete(targets, 'context_menu')
        break
      case 'send_feedback':
        dispatchAppEvent('open-feedback', {
          nodes: targets.map((nodeId) => ({
            id: nodeId,
            type: analyticsNodeType(nodesRef.current.find((node) => node.id === nodeId)?.type ?? ''),
          })),
        })
        break
    }
    contextMenuLog({ event: 'item-selected', ...logFields })
  }, [
    contextMenu,
    projectId,
    viewMode,
    handleUndo,
    handleRedo,
    handlePaste,
    handleCopy,
    handleDuplicate,
    requestNodesDelete,
    runNodeContextCapability,
  ])

  const handleContextMenuUploadChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? [])
    event.target.value = ''
    if (files.length === 0) return
    const rf = reactFlowInstance.current
    const anchor = contextMenuUploadAnchorRef.current
      ?? rf?.screenToFlowPosition({ x: window.innerWidth / 2, y: window.innerHeight / 2 })
    if (!anchor) throw new Error('canvas context menu upload: no canvas position to place files at')
    placeMediaFilesOnCanvas(files, anchor, 'context-menu-upload', 'context_menu')
  }, [placeMediaFilesOnCanvas])

  const observedSelectedNodeId = selectedNodeIds.size === 1
    ? Array.from(selectedNodeIds)[0]
    : undefined
  const observedSelectedNode = useMemo(
    () => nodes.find((node) => node.id === observedSelectedNodeId),
    [nodes, observedSelectedNodeId],
  )

  const activeVisualEdgeNodeIds = useMemo(() => {
    if (!renderActiveEdgesOnly) return EMPTY_ACTIVE_VISUAL_EDGE_NODE_IDS
    const ids = new Set<string>(selectedNodeIds)
    if (hoveredNodeId) ids.add(hoveredNodeId)
    for (const node of nodes) {
      const status = stringDataField(node.data, 'status')
      if (isEdgeActiveNodeStatus(status)) {
        ids.add(node.id)
      }
    }
    return ids
  }, [hoveredNodeId, nodes, renderActiveEdgesOnly, selectedNodeIds])

  const visibleEdges = useMemo(() => {
    if (canvasPerformanceSettings.alwaysHideEdges) return []
    if (!renderActiveEdgesOnly) return edges
    // No `size === 0` early return: an explicitly selected edge whose endpoints
    // are not active must still render (deletion/inspection feedback). The
    // filter already keeps selected edges, and with an empty active set it
    // returns just those — its endpoints then get handles via the
    // visibleEdges-endpoint union in computeHandleVisibility.
    return edges.filter((edge) =>
      edge.selected === true ||
      activeVisualEdgeNodeIds.has(edge.source) ||
      activeVisualEdgeNodeIds.has(edge.target),
    )
  }, [
    activeVisualEdgeNodeIds,
    canvasPerformanceSettings.alwaysHideEdges,
    edges,
    renderActiveEdgesOnly,
  ])

  // ─── Per-node handle visibility (Change B) ────────────────────────────────
  // Pair active-only EDGE rendering with active-only HANDLE rendering so idle
  // nodes also drop their ReactFlow handle-bounds measurement cost. The visible
  // set must include both endpoints of every visible edge (anchoring), which is
  // exactly what computeHandleVisibility does over visibleEdges.
  const handleVisibility = useMemo(
    () =>
      computeHandleVisibility(activeVisualEdgeNodeIds, visibleEdges, {
        alwaysHideEdges: canvasPerformanceSettings.alwaysHideEdges,
        renderActiveEdgesOnly,
      }),
    [
      activeVisualEdgeNodeIds,
      visibleEdges,
      canvasPerformanceSettings.alwaysHideEdges,
      renderActiveEdgesOnly,
    ],
  )

  const setHandleVisibility = useSetHandleVisibility()
  const updateNodeInternals = useUpdateNodeInternals()
  const lastHandleVisibilityRef = useRef<HandleVisibility | null>(null)
  useEffect(() => {
    // `handleVisibility` is a freshly-allocated object whenever activeVisualEdge-
    // NodeIds/visibleEdges recompute — which `updateNodeInternals` itself causes
    // (it emits a dimension change → onNodesChange → nodes → those memos). Gate
    // the whole effect on an ACTUAL membership change so we don't re-measure on
    // every render and spin a continuous rAF / onNodesChange loop while a node
    // is active.
    if (
      lastHandleVisibilityRef.current &&
      handleVisibilityEqual(lastHandleVisibilityRef.current, handleVisibility)
    ) {
      return
    }
    lastHandleVisibilityRef.current = handleVisibility
    setHandleVisibility(handleVisibility)
    handleGateNadLog({
      event: 'handle-visible-set-changed',
      feature: 'canvas:handle-gate',
      hideAll: handleVisibility.hideAll,
      // -1 = "all nodes visible" sentinel (renderActiveEdgesOnly off).
      visibleCount: handleVisibility.visibleNodeIds?.size ?? -1,
      totalNodes: nodes.length,
    })
    // Handles for the visible set mount in the store-driven render this publish
    // triggers — AFTER ReactFlow has already tried to render the now-visible
    // edges. Without a re-measure, an edge whose endpoint handle was unmounted
    // until this moment can't anchor and won't paint until some later nudge.
    // Force ReactFlow to re-read handle bounds for the (few) visible nodes so
    // visible edges anchor within the same interaction. Bounded because the
    // effect body only runs on a real visibility-set change (ref gate above).
    const ids = handleVisibility.visibleNodeIds
    if (!ids || ids.size === 0) return
    const frame = requestAnimationFrame(() => {
      ids.forEach((id) => updateNodeInternals(id))
    })
    return () => cancelAnimationFrame(frame)
  }, [handleVisibility, setHandleVisibility, updateNodeInternals, nodes.length])

  useEffect(() => {
    activeEdgesNadLog({
      event: 'visible-edges-recomputed',
      feature: 'canvas:active-edges',
      renderActiveEdgesOnly,
      alwaysHideEdges: canvasPerformanceSettings.alwaysHideEdges,
      totalEdges: edges.length,
      visibleEdges: visibleEdges.length,
      activeNodes: activeVisualEdgeNodeIds.size,
    })
  }, [
    visibleEdges.length,
    edges.length,
    activeVisualEdgeNodeIds.size,
    renderActiveEdgesOnly,
    canvasPerformanceSettings.alwaysHideEdges,
  ])

  // ─── Loading / Error States ───────────────────────────────────────────────

  if (isLoading && !projectError) {
    return <LoadingState />
  }

  // A canvas whose project defaults could not be read must not accept new
  // nodes: every toolbox add stamps style / resolution / aspect ratio from
  // `projectConfig`, and each stamp no-ops on an unknown value — so the node
  // would silently take schema defaults (16:9 on a 9:16 project). React Query
  // has already retried by the time this is true, so this is a persistent
  // failure, and the same backend outage almost always fails the project fetch
  // below too. Reuse that surface rather than shipping a half-usable canvas
  // (found by CodeRabbit; the loading gate alone did not cover it).
  if (project && projectConfigError && !projectConfig) {
    return (
      <ErrorState
        title={t('workspace.projectError.backendUnavailableTitle')}
        message={t('workspace.projectError.backendUnavailableMessage', { appName: APP_NAME })}
      />
    )
  }

  if (!project) {
    const errorState = getWorkspaceProjectErrorState(projectErrorDetails, projectId)
    if (errorState.kind === 'backendUnavailable') {
      return (
        <ErrorState
          title={t('workspace.projectError.backendUnavailableTitle')}
          message={t('workspace.projectError.backendUnavailableMessage', { appName: APP_NAME })}
        />
      )
    }
    return (
      <ErrorState
        title={t('workspace.projectError.notFoundTitle')}
        message={
          errorState.kind === 'notFound'
            ? t('workspace.projectError.notFoundMessage', { projectId: errorState.projectId })
            : t('workspace.projectError.notFoundMessageUnknown')
        }
      />
    )
  }

  if (initialUploadSeedError) {
    return (
      <ErrorState
        title={t('workspace.uploadSyncErrorTitle')}
        message={initialUploadSeedError}
      />
    )
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  const isCanvasPerformanceActive = isDraggingForSnap || isPerformancePulseActive
  const previewPreservingInteractionLod = canvasPerformanceSettings.previewPreservingInteractionLod
  const lightweightImageNodeShellEnabled = canvasPerformanceSettings.lightweightImageNodeShell
  const lightweightNodeChromeEnabled = canvasPerformanceSettings.lightweightNodeChrome
  const viewportPreviewLayerEnabled = canvasPerformanceSettings.viewportPreviewLayer
  const shouldSimplifyPreviewsDuringInteraction =
    viewportPreviewLayerEnabled ||
    (previewPreservingInteractionLod && canvasPerformanceSettings.renderOnlyVisibleElements)
  // The default path keeps virtualization off while floating bottom panels are
  // open because panels live inside node subtrees. The opt-in Preview LOD path
  // keeps virtualization stable with panels open to avoid first-drag remounts.
  const renderOnlyVisibleElements =
    canvasPerformanceSettings.renderOnlyVisibleElements &&
    (!anyBottomPanelVisible || previewPreservingInteractionLod)
  const reactFlowRenderOnlyVisibleElements = renderOnlyVisibleElements
  const canvasClassName = [
    'bg-background canvas-gradient-bg',
    // Repoints the pane cursor from ReactFlow's hard-coded grab hand to the
    // arrow. See the `.canvas-tool-select` rule in globals.css.
    canvasTool === 'select' ? 'canvas-tool-select' : '',
    // Suspends node hover/pointer UI for the duration of a marquee drag.
    isMarqueeSelecting ? 'canvas-marquee-selecting' : '',
    isCanvasPerformanceActive ? 'canvas-performance-active' : '',
    isDraggingForSnap ? 'canvas-node-dragging' : '',
    canvasPerformanceSettings.renderActiveEdgesOnly ? 'canvas-render-active-edges-only' : '',
    canvasPerformanceSettings.alwaysHideEdges ? 'canvas-always-hide-edges' : '',
    canvasPerformanceSettings.hideEdgesDuringInteraction ? 'canvas-hide-edges-during-interaction' : '',
    canvasPerformanceSettings.imagePreviewOnlyDuringInteraction ? 'canvas-image-preview-only-during-interaction' : '',
    viewportPreviewLayerEnabled ? 'canvas-media-only-while-moving-enabled' : '',
    shouldSimplifyPreviewsDuringInteraction ? 'canvas-simplify-previews-during-pan' : '',
    shouldSimplifyPreviewsDuringInteraction ? 'canvas-simplify-previews-during-node-drag' : '',
  ].filter(Boolean).join(' ')
  const chatPaneInactive = isMobileViewport && mobilePane !== 'chat'
  const canvasPaneInactive = isMobileViewport && mobilePane !== 'canvas'

  // Canvas switcher — lives in <AppHeader>'s center slot (Workplace post
  // 1566820201741944 mockup). Per Workplace post 1640954430995187 the canvases
  // are a dropdown list (active canvas + chevron) rather than a tab strip, with
  // "+" beside it. Closures over canvasTabs / rename / delete state stay in
  // this component; only the JSX lives here.
  const canvasDisplayTitle = (canvas: CanvasTab): string =>
    canvas.id === DEFAULT_CANVAS_ID && canvas.title === 'Agent Canvas'
      ? t('workspace.defaultCanvasTitle')
      : canvas.title
  const activeCanvasTab = canvasTabs.find((canvas) => canvas.id === activeCanvasId)
  const activeCanvasDisplayTitle = activeCanvasTab ? canvasDisplayTitle(activeCanvasTab) : ''
  const appHeaderCenterContent = multiCanvasEnabled ? (
    <div
      className="flex min-w-0 items-center gap-1"
      data-testid="canvas-tabs"
      data-nux-anchor="canvas-switcher"
      onClickCapture={(event) => {
        const target = event.target
        if (!(target instanceof HTMLElement)) return
        if (target.closest('[data-testid="canvas-switcher-trigger"]')) {
          emitNuxEvent({ type: 'controlActivated', controlId: 'canvasSwitcherOpened', anchorId: 'canvas-switcher' })
        } else if (target.closest('[data-testid="create-canvas-tab"]')) {
          emitNuxEvent({ type: 'controlActivated', controlId: 'canvasCreated', anchorId: 'canvas-switcher' })
        } else if (target.closest('[data-testid="import-canvas-tab"]')) {
          emitNuxEvent({ type: 'controlActivated', controlId: 'canvasImported', anchorId: 'canvas-switcher' })
        } else if (target.closest('[role="option"][data-testid^="canvas-tab-"]')) {
          emitNuxEvent({ type: 'controlActivated', controlId: 'canvasChanged', anchorId: 'canvas-switcher' })
        }
      }}
    >
      <div ref={canvasSwitcherRef} className="relative min-w-0">
        <button
          type="button"
          onClick={() => setCanvasSwitcherOpenLogged(!canvasSwitcherOpen, 'trigger')}
          aria-haspopup="listbox"
          aria-expanded={canvasSwitcherOpen}
          aria-label={t('workspace.canvasSwitcherLabel')}
          title={activeCanvasDisplayTitle}
          data-testid="canvas-switcher-trigger"
          className="flex h-7 w-40 max-w-56 items-center gap-1.5 rounded-full border border-input-border bg-surface-1 pl-3 pr-2 text-xs font-medium text-foreground transition-colors hover:border-input-border-hover hover:bg-surface-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
        >
          <span className="min-w-0 flex-1 truncate text-left" data-testid="canvas-switcher-active-title">
            {activeCanvasDisplayTitle}
          </span>
          <ChevronDown
            aria-hidden="true"
            className={`h-3.5 w-3.5 flex-shrink-0 text-muted-foreground transition-transform duration-200 ${canvasSwitcherOpen ? 'rotate-180' : ''}`}
          />
        </button>
        <div
          className={`absolute left-0 top-full z-50 mt-1 w-64 rounded-xl border border-input-border bg-surface-1 p-1 shadow-[0_8px_30px_rgba(0,0,0,0.6)] ${canvasSwitcherOpen ? '' : 'hidden'}`}
          data-testid="canvas-switcher-menu"
        >
          <div
            role="listbox"
            aria-label={t('workspace.canvasTabsAriaLabel')}
            className="max-h-80 space-y-0.5 overflow-y-auto"
          >
            {canvasTabs.map((canvas) => {
              const active = canvas.id === activeCanvasId
              const canDeleteCanvas = canvas.id !== DEFAULT_CANVAS_ID
              const isEditingCanvas = editingCanvasId === canvas.id
              const isRenamingCanvas = renamingCanvasId === canvas.id
              const displayTitle = canvasDisplayTitle(canvas)
              return (
                <div
                  key={canvas.id}
                  className={[
                    'group flex h-8 items-center rounded-lg text-xs font-medium transition-colors',
                    active
                      ? 'bg-primary/10 text-foreground'
                      : 'text-muted-foreground hover:bg-surface-3 hover:text-foreground',
                  ].join(' ')}
                  title={displayTitle}
                >
                  {isEditingCanvas ? (
                    <input
                      autoFocus
                      type="text"
                      value={editingCanvasTitle}
                      maxLength={CANVAS_TITLE_MAX_LENGTH}
                      disabled={isRenamingCanvas}
                      onMouseDown={(event) => event.stopPropagation()}
                      onClick={(event) => event.stopPropagation()}
                      onChange={(event) => setEditingCanvasTitle(event.target.value)}
                      onBlur={() => {
                        if (renameCancelledRef.current) {
                          renameCancelledRef.current = false
                          return
                        }
                        void handleCommitRenameCanvas(canvas.id)
                      }}
                      onKeyDown={(event) => handleRenameKeyDown(event, canvas)}
                      className="mx-1 min-w-24 flex-1 rounded-md border border-input-border bg-input-bg px-2 py-1 text-xs font-medium text-foreground outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:cursor-wait disabled:opacity-60"
                      aria-label={t('workspace.newCanvas.renameInput', { title: canvas.title })}
                      data-testid={`canvas-tab-rename-input-${canvas.id}`}
                    />
                  ) : (
                    <button
                      type="button"
                      role="option"
                      aria-selected={active}
                      data-testid={`canvas-tab-${canvas.id}`}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => {
                        setCanvasSwitcherOpenLogged(false, 'pick', canvas.id)
                        handleCanvasTabNavigate(canvas.id)
                      }}
                      className="flex h-full min-w-0 flex-1 appearance-none items-center gap-2 bg-transparent pl-2 pr-2 text-left outline-0 focus:outline-0 focus-visible:outline-0 focus:ring-0 focus-visible:ring-0 active:bg-transparent"
                    >
                      <Check
                        aria-hidden="true"
                        className={`h-3.5 w-3.5 flex-shrink-0 ${active ? 'text-foreground' : 'invisible'}`}
                      />
                      <span className="min-w-0 flex-1 truncate">{displayTitle}</span>
                    </button>
                  )}
                  {!isEditingCanvas && (
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation()
                        handleStartRenameCanvas(canvas)
                      }}
                      disabled={Boolean(renamingCanvasId) || Boolean(editingCanvasId)}
                      className="mr-1 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md text-text-muted opacity-0 transition-all duration-200 hover:bg-surface-3 hover:text-foreground focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-40 group-hover:opacity-70"
                      title={t('workspace.newCanvas.rename', { title: canvas.title })}
                      aria-label={t('workspace.newCanvas.rename', { title: canvas.title })}
                      data-testid={`canvas-tab-rename-${canvas.id}`}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {canDeleteCanvas && (
                    <CanvasTabDeleteButton
                      canvasId={canvas.id}
                      canvasTitle={canvas.title}
                      deleting={deletingCanvasId === canvas.id}
                      disabled={Boolean(deletingCanvasId || pendingCanvasDelete || editingCanvasId || renamingCanvasId)}
                      onRequestDelete={(canvasId) => {
                        setCanvasSwitcherOpenLogged(false, 'delete', canvasId)
                        handleRequestDeleteCanvasTab(canvasId)
                      }}
                    />
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={handleOpenNewCanvasModal}
        disabled={creatingCanvas}
        className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border border-input-border bg-surface-1 text-muted-foreground transition-colors hover:border-input-border-hover hover:bg-surface-3 hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-input-border disabled:hover:bg-surface-1 disabled:hover:text-muted-foreground"
        title={t('workspace.newCanvas.buttonLabel')}
        aria-label={t('workspace.newCanvas.buttonLabel')}
        data-testid="create-canvas-tab"
      >
        <Plus className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={handleOpenImportCanvasModal}
        disabled={canvasTabs.length <= 1}
        className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border border-input-border bg-surface-1 text-muted-foreground transition-colors hover:border-input-border-hover hover:bg-surface-3 hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-input-border disabled:hover:bg-surface-1 disabled:hover:text-muted-foreground"
        title={t('workspace.importCanvas.buttonLabel')}
        aria-label={t('workspace.importCanvas.buttonLabel')}
        data-testid="import-canvas-tab"
      >
        <CopyPlus className="h-4 w-4" />
      </button>
      {canvasTabsLoading && (
        <span className="h-1.5 w-1.5 flex-shrink-0 animate-pulse rounded-full bg-text-muted" aria-hidden="true" />
      )}
      {canvasTabsError && (
        <span className="max-w-56 truncate px-2 text-xxs text-destructive" title={canvasTabsError}>
          {canvasTabsError}
        </span>
      )}
    </div>
  ) : null

  return (
    <ProjectIdProvider value={projectId}>
    <ActiveCanvasIdProvider value={activeCanvasId}>
    <CanvasHydrationProvider hydrated={canvasHydrated}>
    <UploadContext.Provider value={handleUploadFromRefSelector}>
    <CanvasContext.Provider value={canvasContextValue}>
    <VoiceCatalogueModalProvider>
        <div
          data-testid="workspace-v2"
          className={`fixed inset-x-0 bottom-0 top-[var(--banner-height,0px)] bg-background ${
            isMobileViewport ? 'block overflow-hidden' : 'flex'
          }`}
        >
          {/* New-frontend-release refresh nudge (bottom-center of the canvas). */}
          <NewVersionPrompt />
          {/* Chat Sidebar (Left) - Collapsible & Resizable */}
          {projectId && initialUploadsSeeded && (
            <div
              data-testid="mobile-chat-pane"
              className={isMobileViewport
                ? `absolute inset-0 z-[60] transition-opacity ${
                    chatPaneInactive ? 'pointer-events-none opacity-0' : 'opacity-100'
                  }`
                : 'contents'}
              {...inertWhen(chatPaneInactive)}
            >
              <ChatSidebar
                projectId={projectId}
                canvasId={activeCanvasId}
                initialPrompt={initialPrompt}
                initialSelectedSkill={locationState.initialSelectedSkill}
                initialReferencedNodeIds={initialReferencedNodeIds}
                pendingActions={locationState.pendingActions}
                initialReplayScenario={initialReplayScenario}
                onStop={handleStop}
                autoConfirmCostSettings={autoConfirmCostSettings}
                canvasNodes={canvasNodesStable}
                replayCanvasNodes={nodes}
                canvasEdges={edges}
                onReplayCreateCanvas={multiCanvasEnabled ? handleReplayCreateCanvas : undefined}
                onReplayDeleteCanvas={multiCanvasEnabled ? handleReplayDeleteCanvas : undefined}
                onCanvasCreated={multiCanvasEnabled ? handleDirectorCanvasCreated : undefined}
                // NOT gated on multiCanvasEnabled: the WS-connect refresh is
                // the fix for the race where the initial `listCanvases` reply
                // sets multiCanvasEnabled=true AFTER the socket first opens.
                // If we gated it, the first-connect refresh would be dropped
                // and we'd only recover on a subsequent reconnect. Single-
                // canvas users see a cheap no-op refetch on connect.
                onChatSocketConnectedChange={handleChatSocketConnectedChange}
                ydocRunningCount={ydocRunningCount}
                ydocQueuedCount={ydocQueuedCount}
                ydocDirectorRunning={ydocDirectorRunning}
                ydocStatusLive={ydocStatusLive}
                onMentionClick={handleMentionClick}
                onUploadMedia={handleUploadMediaFromDirector}
                executionPlanBatches={executionPlanBatches}
                onConfirmBatch={handleConfirmBatch}
                onCancelBatch={cancelBatch}
                mobileMode={isMobileViewport}
                onShowCanvas={() => setMobilePane('canvas')}
              />
            </div>
          )}

          {/* Canvas */}
          <div
            data-testid="mobile-canvas-pane"
            className={isMobileViewport
              ? `absolute inset-0 min-w-0 ${canvasPaneInactive ? 'pointer-events-none opacity-0' : ''}`
              : 'relative flex-1'}
            {...inertWhen(canvasPaneInactive)}
          >
            <div className="absolute inset-0 flex min-w-0 flex-col">
              {/* CanvasEdgesProvider hoisted up so <AppHeader> (which uses
                  useCanvasEdges via ClipDownloadModal) sits inside its
                  context alongside the ReactFlow surface below. */}
              <CanvasEdgesProvider
                edges={edges}
                renderActiveEdgesOnly={renderActiveEdgesOnly}
                hideConnectionHandles={canvasPerformanceSettings.alwaysHideEdges}
              >
              {/* RFC-1577: attributes toolbar clicks and the follow-up actions
                  inside the surfaces they open (see paletteAnalytics.tsx). */}
              <PaletteAnalyticsProvider
                projectId={projectId ?? null}
                viewMode={viewMode}
                openSurfaces={{
                  add_node: paletteAddNodeOpen,
                  asset_library: assetManagerOpen,
                  download: paletteClipDownloadOpen,
                  shortcuts: paletteShortcutsOpen,
                }}
              >
              {projectId && initialUploadsSeeded && (
                <AppHeader
                  projectId={projectId}
                  projectName={project?.name}
                  autoConfirmCostSettings={autoConfirmCostSettings}
                  onAutoConfirmImageCostChange={handleAutoConfirmImageCostChange}
                  onAutoConfirmVideoCostChange={handleAutoConfirmVideoCostChange}
                  taskCompletionChimeEnabled={taskCompletionChimeEnabled}
                  onTaskCompletionChimeEnabledChange={handleTaskCompletionChimeEnabledChange}
                  canvasPerformanceSettings={canvasPerformanceSettings}
                  onCanvasPerformanceSettingChange={setCanvasPerformanceSetting}
                  onOpenUsageCost={() => setShowUsageCost(true)}
                  centerContent={appHeaderCenterContent}
                />
              )}
              <div
                ref={canvasSurfaceRef}
                data-nux-anchor="canvas"
                className="relative min-h-0 flex-1"
                aria-hidden={!canvasInteractive}
                style={{ pointerEvents: canvasInteractive ? 'auto' : 'none' }}
                onPointerDownCapture={handleCanvasPointerDownCapture}
                onFocusCapture={captureNodeInteraction}
                onPointerMoveCapture={handleCanvasPointerMoveCapture}
                onPointerUpCapture={handleCanvasPointerEndCapture}
                onPointerCancelCapture={handleCanvasPointerEndCapture}
                onWheelCapture={handleCanvasWheelCapture}
                onContextMenuCapture={handleCanvasSurfaceContextMenuCapture}
              >
                <ReactFlowCapture instanceRef={reactFlowInstance} onReady={onRfInstanceReady} />
                <CanvasNodeChromeModeProvider
                  lightweightImageNodeShell={lightweightImageNodeShellEnabled}
                  lightweightNodeChrome={lightweightNodeChromeEnabled}
                >
                <SingleNodeDetailsProvider
                  selectedNodeCount={viewMode === 'canvas' ? selectedNodeIds.size : 0}
                  suppressed={isMarqueeSelecting}
                >
                <NodePresentationContext.Provider value={viewMode === 'canvas' ? 'canvas' : 'background'}>
                <div className="absolute inset-0" style={{ visibility: viewMode === 'canvas' ? 'visible' : 'hidden' }} {...inertWhen(viewMode !== 'canvas')}>
                <CanvasReferencePicking>
                <ReactFlow
                  style={{ opacity: canvasVisible ? 1 : 0 }}
                  nodes={nodes}
                  edges={visibleEdges}
                  onNodesChange={onNodesChange}
                  onEdgesChange={onEdgesChange}
                  onConnect={onConnect}
                  onMove={handleViewportMove}
                  onMoveStart={onMoveStart}
                  onMoveEnd={onMoveEndSave}
                  onNodeClick={onNodeClick}
                  onNodeMouseEnter={handleNodeMouseEnter}
                  onNodeMouseMove={handleNodeMouseEnter}
                  onNodeMouseLeave={handleNodeMouseLeave}
                  onNodeDragStart={onNodeDragStart}
                  onNodeDrag={onNodeDrag}
                  onNodeDragStop={onNodeDragStopWithGroup}
                  onSelectionDragStart={onSelectionDragStart}
                  onSelectionDrag={onSelectionDrag}
                  onSelectionDragStop={onSelectionDragStop}
                  onSelectionChange={onSelectionChange}
                  onDragOver={handleCanvasDragOver}
                  onDrop={handleCanvasDrop}
                  onPaneContextMenu={onPaneContextMenu} // SHORTCUT_ID: canvas.contextMenu
                  onNodeContextMenu={onNodeContextMenu}
                  onEdgeContextMenu={onEdgeContextMenu}
                  onSelectionContextMenu={onSelectionContextMenu}
                  isValidConnection={isValidConnection}
                  nodeTypes={nodeTypes}
                  edgeTypes={edgeTypes}
                  edgesFocusable={false}
                  deleteKeyCode={CANVAS_DELETE_KEY_CODE}
                  defaultViewport={{ x: 0, y: 0, zoom: 1 }}
                  minZoom={0.1}
                  maxZoom={4}
                  // Pointer-tool wiring. The Select tool makes marquee-drag
                  // persistent; Shift keeps doing it temporarily from either
                  // tool, so canvas.boxSelect is unchanged. The two are OR'd
                  // rather than switched so Shift cannot un-select-mode the
                  // canvas mid-drag. SelectionMode.Partial then hands us every
                  // node the box touches and onNodesChange drops the ones it
                  // covers less than halfway (see marqueeSelection.ts); Full
                  // would pre-filter to fully-enclosed nodes, which we could
                  // never add back.
                  //
                  // KEEP THESE FOUR PROPS CONTIGUOUS. keyboardShortcutsCoverage
                  // requires a key/gesture token within 4 lines of every
                  // SHORTCUT_ID marker and strips comments before looking, so
                  // prose inserted between them still consumes the budget and
                  // un-binds the marker. That is what reds the build, not a
                  // missing marker.
                  panOnScroll // SHORTCUT_ID: canvas.pan
                  panOnScrollMode={PanOnScrollMode.Free}
                  panOnDrag={canvasTool === 'hand' && !isShiftSelecting}
                  selectionOnDrag={canvasTool === 'select' || isShiftSelecting} // SHORTCUT_ID: canvas.boxSelect
                  selectionMode={SelectionMode.Partial}
                  onlyRenderVisibleElements={reactFlowRenderOnlyVisibleElements}
                  // Dreamwrite owns Shift state so it can reconcile a keyup consumed by macOS.
                  // ReactFlow's default Shift tracker is a second, inaccessible sticky latch.
                  selectionKeyCode={null}
                  multiSelectionKeyCode={CANVAS_MULTI_SELECTION_KEY_CODE} // SHORTCUT_ID: canvas.multiSelect
                  zoomOnScroll={false}
                  zoomOnPinch // SHORTCUT_ID: canvas.zoomPinch
                  zoomOnDoubleClick // SHORTCUT_ID: canvas.zoomInDoubleClick SHORTCUT_ID: canvas.zoomOutDoubleClick
                  snapToGrid={canvasPerformanceSettings.snapToGrid || !isDraggingForSnap}
                  snapGrid={[CANVAS_GRID_SIZE, CANVAS_GRID_SIZE]}
                  className={canvasClassName}
                  proOptions={{ hideAttribution: true }}
                >
                  {/* Hairline box around a multi-selection. Derived from
                      node.selected, so it cannot drift out of step with the
                      highlights the way ReactFlow's own rect did. */}
                  <CanvasSelectionBounds onContextMenu={onSelectionBoundsContextMenu} />

                  <Background
                    variant={BackgroundVariant.Dots}
                    gap={CANVAS_GRID_SIZE}
                    size={1}
                    color="rgba(255,255,255,0.08)"
                  />


                  {/* Canvas Controls (bottom-left zoom controls) */}
                  <CanvasControls onArrange={arrange} arranging={arranging} arrangeDisabled={project?.is_demo === true} arrangeLoading={arrangeLoading} awaitingDecision={awaitingDecision} onKeepArrangement={keepArrangement} onRestoreArrangement={restoreArrangement} />

                  {projectId && observedSelectedNodeId && (
                    <Panel position="top-right" className="!m-4">
                      <CanvasObservedByBadge
                        projectId={projectId}
                        canvasId={activeCanvasId}
                        nodeId={observedSelectedNodeId}
                        nodeVersion={observedSelectedNode?.data}
                        nodePosition={observedSelectedNode?.position}
                        ydoc={ydoc}
                      />
                    </Panel>
                  )}

                  {/* Floating tool palette (bottom centre) — Figma
                      "Canvas UI 2.0" Navbar, node 355:6242. The 44px offset is
                      the frame's own: the Navbar's bottom edge sits 88px above
                      a 2160px-tall 2x frame. */}
                  <Panel position="bottom-center" className="!mb-11">
                    {renderBottomPalette(true)}
                  </Panel>

                  {/* Interactive MiniMap */}
                  <Panel position="bottom-right" className="!m-4 flex flex-col items-end gap-2">
                    <InteractiveMiniMap />
                  </Panel>

                  {/* CanvasToolbox + Publish + Sparks lifted into
                      <AppHeader> per Workplace post 1566820201741944 mockup —
                      no more floating panels over the canvas. */}
                </ReactFlow>
                </CanvasReferencePicking>
                </div>
                </NodePresentationContext.Provider>
              </SingleNodeDetailsProvider>
              </CanvasNodeChromeModeProvider>
              {viewMode === 'simplified' && (
                <SimplifiedWorkspace
                  key={canvasRoomKey}
                  nodes={nodes}
                  selectedNodeIds={selectedNodeIds}
                  onSelectNode={selectSimplifiedNode}
                  onNodeContextMenu={openNodeContextMenu}
                  onOpenLibrary={() => setAssetManagerOpen(true)}
                  palette={renderBottomPalette(false)}
                  overview={
                    <ProjectConfigGear
                      inline
                      autoConfirmCostSettings={autoConfirmCostSettings}
                      onAutoConfirmImageCostChange={handleAutoConfirmImageCostChange}
                      onAutoConfirmVideoCostChange={handleAutoConfirmVideoCostChange}
                      taskCompletionChimeEnabled={taskCompletionChimeEnabled}
                      onTaskCompletionChimeEnabledChange={handleTaskCompletionChimeEnabledChange}
                      canvasPerformanceSettings={canvasPerformanceSettings}
                      onCanvasPerformanceSettingChange={setCanvasPerformanceSetting}
                    />
                  }
                />
              )}
              <CanvasLoadOverlay
                visible={canvasLoadOverlayVisible}
                connected={yjsConnected}
                synced={yjsSynced}
                ready={ydocMatchesActiveCanvas && canvasReady}
                nodeCount={activeCanvasRendered ? nodes.length : 0}
                resetKey={canvasRoomKey}
                error={yjsError}
                quiet={warmSwitchReady && !activeCanvasRendered && yjsError == null}
              />
              </div>

              {/* Drawers owned by <CanvasBottomPalette>. Mounted here rather
                  than inside the palette so ClipDownloadModal stays within
                  <CanvasEdgesProvider>, whose useCanvasEdges it needs. */}
              <ClipDownloadModal
                isOpen={paletteClipDownloadOpen}
                onClose={() => setPaletteClipDownloadOpen(false)}
                canvasNodes={nodes}
                canvasEdges={edges}
              />
              <KeyboardShortcutsModal
                isOpen={paletteShortcutsOpen}
                onClose={() => setPaletteShortcutsOpen(false)}
              />
              <AddNodeModal
                isOpen={paletteAddNodeOpen}
                onClose={() => setPaletteAddNodeOpen(false)}
                onAddNode={handleUserAddNode}
                onUploadFile={handleUploadFromDirector}
              />
              {contextMenu && (
                <CanvasContextMenu
                  kind={contextMenu.kind}
                  x={contextMenu.x}
                  y={contextMenu.y}
                  sections={contextMenuSections}
                  onSelect={handleContextMenuSelect}
                  onClose={closeContextMenu}
                />
              )}
              <input
                ref={contextMenuUploadInputRef}
                type="file"
                multiple
                accept={CONTEXT_MENU_UPLOAD_ACCEPT}
                className="hidden"
                onChange={handleContextMenuUploadChange}
                data-testid="canvas-context-menu-upload-input"
              />
              {projectId && (
                <AssetManagerDrawer
                  projectId={projectId}
                  isOpen={assetManagerOpen}
                  onClose={() => setAssetManagerOpen(false)}
                  onAddNode={handleUserAddNode}
                />
              )}
              </PaletteAnalyticsProvider>
              </CanvasEdgesProvider>
              {isMobileViewport && mobilePane === 'canvas' && (
                <button
                  type="button"
                  data-testid="mobile-show-chat"
                  onClick={() => setMobilePane('chat')}
                  className="absolute left-3 top-3 z-50 flex h-9 items-center gap-2 rounded-lg border border-input-border bg-surface-1 px-3 text-sm font-medium text-foreground shadow-lg transition-colors hover:border-input-border-hover hover:bg-surface-3"
                >
                  <MessageSquare className="h-4 w-4" />
                  {t('workspace.chat')}
                </button>
              )}
            </div>
          </div>
        </div>

        <UsageCostModal
          isOpen={showUsageCost}
          onClose={() => {
            setShowUsageCost(false)
          }}
          projectId={projectId}
        />
        {multiCanvasEnabled && (
          <>
            <NewCanvasInitModal
              isOpen={newCanvasModalOpen}
              title={newCanvasTitle}
              prompt={newCanvasPrompt}
              sourceNodes={newCanvasSourceNodes}
              selectedSourceNodeIds={newCanvasSourceNodeIds}
              creating={creatingCanvas}
              error={canvasTabsError}
              onTitleChange={setNewCanvasTitle}
              onPromptChange={setNewCanvasPrompt}
              onAskDirector={handleAskDirectorToCreateCanvas}
              onOpenSourceSelector={() => setNewCanvasSourceSelectorOpen(true)}
              onClose={() => setNewCanvasModalOpen(false)}
              onSubmit={handleSubmitNewCanvas}
            />
            <CanvasTabDeleteConfirmationDialog
              canvas={pendingDeleteCanvas}
              deleting={Boolean(deletingCanvasId)}
              error={canvasTabsError}
              onCancel={handleCancelDeleteCanvasTab}
              onConfirm={handleConfirmDeleteCanvasTab}
            />
            <ReferenceSelector
              isOpen={newCanvasSourceSelectorOpen}
              onClose={() => setNewCanvasSourceSelectorOpen(false)}
              onSave={() => {}}
              excludeNodeId=""
              canvasNodes={newCanvasSelectableNodes}
              currentNodeSelections={newCanvasSelectedSourceReferences}
              maxReferences={Math.max(newCanvasSelectableNodes.length, 1)}
              mode="node-selector"
              nodeSelectorTitle={t('workspace.newCanvas.sourceLabel')}
              nodeSelectorEmptyLabel={t('workspace.newCanvas.noSources')}
              nodeSelectorNoSelectionLabel={t('workspace.newCanvas.noSourcesSelected')}
              onSaveNodeSelections={handleSaveNewCanvasSourceNodes}
            />
            <ImportCanvasModal
              isOpen={importCanvasModalOpen}
              sourceCanvases={importSourceCanvases}
              sourceCanvasId={importSourceCanvasId}
              description={importDescription}
              error={canvasTabsError}
              onSourceCanvasChange={setImportSourceCanvasId}
              onDescriptionChange={setImportDescription}
              onAskDirector={handleAskDirectorToImport}
              onClose={() => setImportCanvasModalOpen(false)}
            />
          </>
        )}
        <NodeDeleteConfirmationDialog
          isOpen={pendingNodeDelete !== null}
          nodes={pendingNodeDelete?.nodes ?? []}
          onCancel={handleCancelNodeDelete}
          onConfirm={handleConfirmNodeDelete}
        />
        <InsufficientSparksMount />
    </VoiceCatalogueModalProvider>
    </CanvasContext.Provider>
    </UploadContext.Provider>
    </CanvasHydrationProvider>
    </ActiveCanvasIdProvider>
    </ProjectIdProvider>
  )
}

/**
 * Renders the workspace-level <InsufficientSparksModal>, wired to
 * the InsufficientSparksContext that node-level Generate buttons
 * trigger from their executeNode catch blocks. Informational only —
 * there is no self-serve top-up (operators add credits via /admin/topup).
 */
function InsufficientSparksMount() {
  const { request, dismiss } = useInsufficientSparks()
  return (
    <InsufficientSparksModal
      isOpen={request !== null}
      onClose={dismiss}
      nodeType={request?.nodeType}
    />
  )
}

// ─── Export ───────────────────────────────────────────────────────────────────

function WorkspacePageV2Route() {
  const { projectId } = useParams<{ projectId?: string }>()
  return <WorkspaceV2Inner key={projectId ?? ''} />
}

export default function WorkspacePageV2() {
  return (
    <ReactFlowProvider>
      <CanvasBottomPanelVisibilityProvider>
        <GroupDropTargetProvider>
          <InsufficientSparksProvider>
            <WorkspacePageV2Route />
          </InsufficientSparksProvider>
        </GroupDropTargetProvider>
      </CanvasBottomPanelVisibilityProvider>
    </ReactFlowProvider>
  )
}
